import { Alert, Autocomplete, Box, Card, Grid, Tooltip, Typography, useTheme, Button, Avatar, Chip, Paper, Modal, CircularProgress, alpha, Popover, LinearProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Stack, Divider, Switch, FormControlLabel, Badge } from "@mui/material"
import { styled } from "@mui/material/styles"
import { departmentsApi, shiftApi, departmentsEmployeesApi, calendarEntriesApi, calendarEntriesShiftTemplateApi, shiftTemplateApi, agreementApi, tenantConfigApi, complianceApi, shiftNotesApi, employeeDayNotesApi } from "api"
import { DepartmentEntity, EmployeeEntity, ShiftAvailabilityRequestEntity, ShiftEntity, ShiftTemplateEntity, ShiftDetailEntity, CalendarEntryEntity, CalendarEntryEntityTypeEnum, AgreementWithEmployeeDto, SubordinateOrderEntity, ConfigureSubordinateOrderDto, UpdateShiftDetailWithChoiceDtoChoiceEnum, ShiftNoteEntity, EmployeeDayNoteEntity } from "api/generated"
import ShiftNotesModal from "page-sections/notes/ShiftNotesModal"
import EmployeeDayNotesSection from "page-sections/notes/EmployeeDayNotesSection"
import AppTextField from "components/input-fields/AppTextField"
import { useSeason } from "contexts/SeasonContext"
import React, { FC, useEffect, useState, useCallback, useMemo, useRef } from "react"
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd"
import { useTranslation } from "react-i18next"
import { fetchShiftAssignmentData, calendarEntryColor, ignoreUTC } from "utils/utils"
import { weekLabel, groupByWeek } from "utils/weekLabel"
import errorImage from "assets/images/error.png"
import defaultProfileImage from "assets/images/default-profile.png"
import PublishShiftsDatePicker from "page-sections/shifts/publishShiftDatePicker"
import { useNavigate } from "react-router-dom"
import { addDays, isSameDay, startOfWeek, endOfWeek, format, eachDayOfInterval, startOfMonth, endOfMonth, addWeeks, addMonths, isBefore, startOfDay, differenceInDays, subDays } from "date-fns"
import { it, de, fr, enUS } from "date-fns/locale"
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import { checkCanViewShifts, checkTokenRole, checkSeasonalRole } from "utils/checkToken"
import toast, { Toaster } from "react-hot-toast"
import RequestAviability from "page-sections/shifts/requestAviability"
import { Send, FactCheck, Download, ContentCopy, Add, Person, Work, AccessTime, DeleteForever, Edit, Warning, Close as CloseIcon, EmojiEvents, TrendingUp, CheckCircle, ViewModule, ViewList, DragIndicator, Save, Cancel, WorkOutline, FreeBreakfast, Timer, Event, Description, PersonOff, GppMaybe, EditNote, Visibility, VisibilityOff } from "@mui/icons-material"
import AddShiftModal from "page-sections/data-table/dataTableV2/AddShiftModal"
import AddEventModal, { AddWorkFormData } from "page-sections/employees/calendar/AddEventModal"
import IconButtonMui from "@mui/material/IconButton"
import ShiftReportModal from "./ShiftReportModal"
import ComplianceOverviewModal from "./ComplianceOverviewModal"
import CopyShiftPlanModal from "page-sections/shifts/CopyShiftPlanModal"


export interface ShiftData {
    id: number
    index: number
    name: string
    width: number
    startHour: string
    endHour: string
    date?: Date
    notes?: string
}

export interface Position {
    id: number
    workPositionId: number
    firstName?: string
    lastName?: string
    employeeId?: number
    assignmentId?: number
    start?: Date
    end?: Date
    breakStartTime?: Date
    breakEndTime?: Date
    additionalBreaks?: Array<{ startTime: Date; endTime: Date }>
    breakPaddingStart?: number
    breakPaddingEnd?: number
    date: Date
    leftPadding: number
    rightPadding: number
    deleted?: boolean,
    published: boolean | undefined
}

export interface AssignableShiftDetail extends ShiftDetailEntity { }

const BetaChip = styled(Chip)(({ theme }) => ({
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    fontWeight: "bold",
    fontSize: "0.6rem",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    boxShadow: "0 2px 8px rgba(102, 126, 234, 0.4)",
    height: "20px",
    "& .MuiChip-label": {
        padding: "0px 8px",
        fontSize: "0.6rem",
    },
}))


const ShiftAssignmentV2: FC = () => {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()

    // Helper function to get the correct image URL from filePath
    const getEmployeeImageUrl = (filePath: string | null | undefined): string => {
        if (!filePath) return defaultProfileImage
        // If filePath is already a full URL (presigned URL), use it directly
        if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
            return filePath
        }
        // Otherwise, it's a relative path and needs the /api/files/ prefix
        return `/api/files/${filePath}`
    }
    const { seasonId } = useSeason()
    const theme = useTheme()
    const screenWidth = window.innerWidth

    const getLocale = () => {
        switch (i18n.language) {
            case 'it': return it
            case 'de': return de
            case 'fr': return fr
            default: return enUS
        }
    }
    const [shifts, setShifts] = useState<ShiftData[]>([])
    // Load saved dates from localStorage or use defaults
    // Start date restores the user's last selected period (at midnight to avoid
    // timezone issues); falls back to today only if nothing valid is saved.
    const getInitialStartDate = (): Date => {
        const saved = localStorage.getItem('shift-assignment-selected-start-date')

        if (saved) {
            const savedDate = startOfDay(new Date(saved))
            if (!isNaN(savedDate.getTime())) {
                return savedDate
            }
        }

        return startOfDay(new Date())
    }
    // End date uses saved period's end date, but if it's less than 2 days from today, use 7 days from today
    const getInitialEndDate = (): Date => {
        const today = startOfDay(new Date())
        const saved = localStorage.getItem('shift-assignment-selected-end-date')

        if (saved) {
            const savedDate = startOfDay(new Date(saved))
            if (!isNaN(savedDate.getTime())) {
                // Calculate days difference between saved end date and today
                const daysDifference = differenceInDays(savedDate, today)

                // If saved end date is less than 2 days from today, use 7 days from today instead
                if (daysDifference < 2) {
                    return addDays(today, 7)
                }

                // Otherwise, use the saved end date
                return savedDate
            }
        }

        // If no saved end date, default to 7 days from today
        return addDays(today, 7)
    }
    const [selectedDay, setSelectedDay] = useState(getInitialStartDate())
    const [selectedEndDay, setSelectedEndDay] = useState(getInitialEndDate())
    const [resources, setResources] = useState<Record<number, Position[]>>([])
    const [workPositions, setWorkPositions] = useState<Array<{ id: number; name: string }>>([])
    const [calendarEntries, setCalendarEntries] = useState<Record<string, CalendarEntryEntity[]>>({}) // key: date string, value: entries for that date
    const [selectedDepartment, setSelectedDepartment] = useState<{ label: string; id: number } | null>(null)
    const [departments, setDepartments] = useState<DepartmentEntity[]>([])
    const [employees, setEmployees] = useState<EmployeeEntity[]>([])
    const [workedHours, setWorkedHours] = useState<Record<number, number>>([])
    const [weeklyWorkHours, setWeeklyWorkHours] = useState<Record<number, number>>({}) // key: employeeId, value: weekly work hours
    const [defaultWeeklyHours, setDefaultWeeklyHours] = useState<number>(40) // Default from tenant config
    const [currentAssignmentScore, setCurrentAssignmentScore] = useState<number | null>(null) // Current score for display
    const [openSendShift, setOpenSendShift] = useState(false)
    const [openAskForAviability, setOpenAskForAviability] = useState(false)
    const [requestsHasBeenSent, setRequestsHasBeenSent] = useState(false)
    const [overlappingRequests] = useState<ShiftAvailabilityRequestEntity[]>([])
    const [countAviability, setCountAviability] = useState(true)
    const [openReportModal, setOpenReportModal] = useState(false)
    const [openComplianceModal, setOpenComplianceModal] = useState(false)
    const [complianceIssueCount, setComplianceIssueCount] = useState(0)
    const [complianceMode, setComplianceMode] = useState<string>('off')
    const [openCopyModal, setOpenCopyModal] = useState(false)
    const [isEditShiftModalOpen, setIsEditShiftModalOpen] = useState(false)
    const [shiftTemplateData, setShiftTemplateData] = useState<ShiftTemplateEntity>()
    const [shiftModalData, setShiftModalData] = useState<ShiftEntity>()
    const [selectedShift, setSelectedShift] = useState<ShiftData>()
    const [openDayEditModal, setOpenDayEditModal] = useState(false)
    const [selectedDayForEdit, setSelectedDayForEdit] = useState<Date | null>(null)
    const [dayTemplateData, setDayTemplateData] = useState<ShiftTemplateEntity | null>(null)
    const [dayShiftsData, setDayShiftsData] = useState<ShiftEntity[]>([])
    const [dayTemplatesCache, setDayTemplatesCache] = useState<Record<string, { template: ShiftTemplateEntity; shifts: ShiftEntity[] }>>({}) // key: date string (YYYY-MM-DD)
    const [isLoadingDayTemplate, setIsLoadingDayTemplate] = useState(false)
    const [openDeleteShiftDialog, setOpenDeleteShiftDialog] = useState(false)
    const [shiftToDelete, setShiftToDelete] = useState<ShiftEntity | null>(null)
    const [dateRangePickerAnchor, setDateRangePickerAnchor] = useState<HTMLButtonElement | null>(null)
    const [tempStartDate, setTempStartDate] = useState<Date>(selectedDay)
    const [editOrder, setEditOrder] = useState<boolean>(false)
    const [employeesInEditMode, setEmployeesInEditMode] = useState<EmployeeEntity[]>([])
    const [employeeWorkPositions, setEmployeeWorkPositions] = useState<Record<number, number>>({}) // employeeId -> workPositionId
    const [isSavingOrder, setIsSavingOrder] = useState<boolean>(false)
    // Employees the manager has chosen to hide from the shifts view (persisted per
    // boss/department/season alongside the custom order). These stay in `employees`
    // for all data loading and are only filtered out at render time.
    const [hiddenEmployeeIds, setHiddenEmployeeIds] = useState<Set<number>>(new Set())
    // Working copy of the hidden set while in edit-order mode.
    const [hiddenInEditMode, setHiddenInEditMode] = useState<Set<number>>(new Set())
    const employeesInEditModeRef = useRef<EmployeeEntity[]>([])
    // Always points at the employee list the UI currently shows. Hours are fetched
    // asynchronously, so we use this to discard a stale response (e.g. the previous
    // department's) that would otherwise overwrite the current one and show 0h.
    const employeesRef = useRef<EmployeeEntity[]>([])
    const [tempEndDate, setTempEndDate] = useState<Date>(selectedEndDay)
    const [currentTime, setCurrentTime] = useState<Date>(new Date())
    const [daysWithoutTemplate, setDaysWithoutTemplate] = useState<Set<string>>(new Set()) // Set of date strings (YYYY-MM-DD) that don't have templates
    const [openAssignTemplateModal, setOpenAssignTemplateModal] = useState(false)
    const [selectedDayForTemplate, setSelectedDayForTemplate] = useState<Date | null>(null)
    const [availableTemplates, setAvailableTemplates] = useState<ShiftTemplateEntity[]>([])
    const [openOverlapModal, setOpenOverlapModal] = useState(false)
    const [overlapData, setOverlapData] = useState<{
        newShift: { startTime: Date; endTime: Date; name: string; breakStart?: Date; breakEnd?: Date }
        existingItem: { startTime: Date; endTime: Date; name: string; type: 'shift' | 'calendar'; breakStart?: Date; breakEnd?: Date }
        shiftDetailId?: number
        calendarEventData?: { startedOn: Date; endedOn: Date; allDay: boolean; startTime?: string; endTime?: string; type: CalendarEntryEntityTypeEnum | string; customTypeId?: number; rest: any }
    } | null>(null)
    const [openCalendarEntryModal, setOpenCalendarEntryModal] = useState(false)
    const skipOverlapCheckRef = React.useRef<boolean>(false)
    const [selectedCalendarEntry, setSelectedCalendarEntry] = useState<{
        entry: CalendarEntryEntity
        employee: EmployeeEntity
        date: Date
        startTime: Date
        endTime: Date
        color: string
        type: CalendarEntryEntityTypeEnum
    } | null>(null)
    const [openAssignmentScoreModal, setOpenAssignmentScoreModal] = useState(false)
    const [assignmentScore, setAssignmentScore] = useState<{
        score: number
        totalRequiredHours: number
        totalAssignedHours: number
        totalOverAllocationHours: number
        employeeScores: Array<{
            employee: EmployeeEntity
            requiredHours: number
            assignedHours: number
            score: number
            percentage: number
            overAllocationHours: number
        }>
    } | null>(null)
    const [viewMode, setViewMode] = useState<'detailed' | 'compact'>('detailed')
    const [excludeNoContract, setExcludeNoContract] = useState<boolean>(true)
    const [isDepchief, setIsDepchief] = useState<boolean>(false)
    // Manager-only shift notes (separate from the public per-shift `notes` field).
    const [shiftNotes, setShiftNotes] = useState<ShiftNoteEntity[]>([])
    const [openShiftNotesModal, setOpenShiftNotesModal] = useState<boolean>(false)
    const [selectedShiftNotesDate, setSelectedShiftNotesDate] = useState<Date | undefined>(undefined)
    // Manager-only per-employee-per-day notes (loaded for the whole visible range).
    const [employeeDayNotes, setEmployeeDayNotes] = useState<EmployeeDayNoteEntity[]>([])
    const [hideEmployeesRequiredRow] = useState<boolean>(true) // Hide the "Required Employees" row by default

    // --- DRAG-TO-RESIZE STATE ---
    const [resizingShift, setResizingShift] = useState<{
        shift: any           // The cached shift object being resized
        edge: 'left' | 'right'
        day: Date
        employee: EmployeeEntity
        initialMouseX: number
        initialLeft: number   // Original left px position of the shift
        initialRight: number  // Original right px position of the shift (left + width)
        currentDeltaPx: number
        currentMouseX: number // Current mouse X for fixed tooltip
        currentMouseY: number // Current mouse Y for fixed tooltip
    } | null>(null)
    const resizingShiftRef = useRef<typeof resizingShift>(null)

    const [movingShift, setMovingShift] = useState<{
        shift: any
        day: Date
        employee: EmployeeEntity
        initialMouseX: number
        initialLeft: number   // Original left px of the shift body
        initialRight: number  // Original right px (left + width)
        currentDeltaPx: number
        currentMouseX: number
        currentMouseY: number
        hasMoved: boolean     // true once delta exceeds the click threshold (4px)
    } | null>(null)
    const movingShiftRef = useRef<typeof movingShift>(null)

    // --- DRAG-TO-EDIT BREAKS STATE ---
    // Allows the user to drag a break (main or additional) along the shift: move the whole
    // break left/right, or pull/push either edge to shorten/prolong it.
    const [draggingBreak, setDraggingBreak] = useState<{
        shift: any
        day: Date
        employee: EmployeeEntity
        breakKind: 'main' | 'additional'
        breakIndex: number   // -1 for the main break, otherwise the index into additionalBreaks
        mode: 'move' | 'resize-left' | 'resize-right'
        origStartTime: Date
        origEndTime: Date
        initialMouseX: number
        initialStartLeft: number
        initialEndLeft: number
        currentDeltaPx: number
        currentMouseX: number
        currentMouseY: number
        hasMoved: boolean
    } | null>(null)
    const draggingBreakRef = useRef<typeof draggingBreak>(null)

    const weekDays = eachDayOfInterval({ start: selectedDay, end: selectedEndDay })

    // Contiguous runs of days sharing an ISO week, for the Kalenderwoche band above the day headers
    const weekBands = useMemo(() => groupByWeek(weekDays), [selectedDay, selectedEndDay])

    // 1. OPTIMIZATION: Pre-calculate Shifts into a Map
    // Key: `${employeeId}-${yyyy-MM-dd}`, Value: Array of calculated shift positions
    const shiftsCache = useMemo(() => {
        const cache = new Map<string, any[]>()

        // Iterate over shifts (Sparse data) instead of Employees * Days
        shifts.forEach(shiftData => {
            if (!shiftData.date) return

            const shiftResources = resources[shiftData.index] || []
            const dateKey = format(shiftData.date, "yyyy-MM-dd")

            shiftResources.forEach(position => {
                if (position.deleted || !position.employeeId) return

                // --- LOGIC MOVED FROM getShiftsForHourlyColumns ---
                // We calculate the geometry ONCE here
                let startTime: Date
                let endTime: Date

                // Determine start time: use shift detail startTime if available, otherwise use shift template startHour
                if (position.start) {
                    startTime = new Date(position.start)
                } else if (shiftData.startHour) {
                    const startParts = shiftData.startHour.split(':')
                    const startH = parseInt(startParts[0], 10)
                    const startM = parseInt(startParts[1] || '0', 10)
                    startTime = new Date(position.date)
                    startTime.setHours(startH, startM, 0, 0)
                } else {
                    return
                }

                // Determine end time: use shift detail endTime if available, otherwise use shift template endHour
                if (position.end) {
                    endTime = new Date(position.end)
                } else if (shiftData.endHour) {
                    const endParts = shiftData.endHour.split(':')
                    const endH = parseInt(endParts[0], 10)
                    const endM = parseInt(endParts[1] || '0', 10)
                    endTime = new Date(position.date)
                    endTime.setHours(endH, endM, 0, 0)
                } else {
                    return
                }

                // Ensure times are on the position's date
                const positionDate = new Date(position.date)
                positionDate.setHours(0, 0, 0, 0)

                // If start time is not on the position date, adjust it
                const startDate = new Date(startTime)
                startDate.setHours(0, 0, 0, 0)
                if (!isSameDay(startDate, positionDate)) {
                    startTime.setFullYear(positionDate.getFullYear(), positionDate.getMonth(), positionDate.getDate())
                }

                const endDate = new Date(endTime)
                endDate.setHours(0, 0, 0, 0)
                if (!isSameDay(endDate, positionDate)) {
                    endTime.setFullYear(positionDate.getFullYear(), positionDate.getMonth(), positionDate.getDate())
                    // If end time is before start time after adjustment, it's next day
                    if (endTime < startTime) {
                        endTime = addDays(endTime, 1)
                    }
                }

                // Handle end time that might be next day (e.g., night shift ending at 02:00)
                if (endTime < startTime) {
                    endTime = addDays(endTime, 1)
                }

                // Check if this is a night shift that spans midnight
                const startDay = new Date(startTime)
                startDay.setHours(0, 0, 0, 0)
                const endDay = new Date(endTime)
                endDay.setHours(0, 0, 0, 0)
                const isNightShift = !isSameDay(startDay, endDay)

                const startHour = startTime.getHours()
                // For night shifts, endHour on the start day is 23 (midnight)
                // The actual end hour will be rendered on the next day
                const endHourOnStartDay = isNightShift ? 23 : endTime.getHours()
                const endHourOnEndDay = isNightShift ? endTime.getHours() : null

                const leftOffset = (startTime.getMinutes() / 60) * 100
                const rightOffset = isNightShift ? 100 : (endTime.getMinutes() / 60) * 100

                // Calculate break times for total hours (main break + additional breaks)
                let breakHours = 0
                if (position.breakStartTime && position.breakEndTime) {
                    const breakMs = position.breakEndTime.getTime() - position.breakStartTime.getTime()
                    breakHours += breakMs / (1000 * 60 * 60)
                }
                // Add time from additional breaks
                if (position.additionalBreaks && position.additionalBreaks.length > 0) {
                    position.additionalBreaks.forEach(ab => {
                        const breakMs = ab.endTime.getTime() - ab.startTime.getTime()
                        breakHours += breakMs / (1000 * 60 * 60)
                    })
                }
                const totalHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60) - breakHours

                const calculatedShift = {
                    ...position,
                    shiftData,
                    startHour,
                    endHour: endHourOnStartDay,
                    endHourNextDay: endHourOnEndDay,
                    isNightShift,
                    spanColumns: endHourOnStartDay - startHour + 1,
                    leftOffset,
                    rightOffset,
                    startTime,
                    endTime,
                    workPositionName: (() => {
                        const workPosition = workPositions.find(wp => wp.id === position.workPositionId)
                        return workPosition?.name || t("shift.noPosition")
                    })(),
                    totalHours: Math.round(totalHours * 10) / 10
                }

                const key = `${position.employeeId}-${dateKey}`
                if (!cache.has(key)) cache.set(key, [])
                cache.get(key)?.push(calculatedShift)
            })
        })

        return cache
    }, [shifts, resources, workPositions, t])

    // 2. OPTIMIZATION: Pre-calculate Calendar Entries into a Map
    // Key: `${employeeId}-${yyyy-MM-dd}`
    const calendarCache = useMemo(() => {
        const cache = new Map<string, any[]>()

        // Iterate dates in the map we already fetched
        Object.keys(calendarEntries).forEach(dateKey => {
            const entries = calendarEntries[dateKey]
            const dateObj = new Date(dateKey) // This is midnight

            entries.forEach(entry => {
                if (!entry.employeeId) return

                // --- LOGIC MOVED FROM getCalendarEntriesForEmployee ---
                // Parse entry dates
                const entryStartDate = new Date(entry.startedOn)
                const entryEndDate = new Date(entry.endedOn)

                // Determine if this is the first day, last day, or a middle day of a multi-day event
                const entryStartDateOnly = startOfDay(entryStartDate)
                const entryEndDateOnly = startOfDay(entryEndDate)
                const isFirstDay = isSameDay(dateObj, entryStartDateOnly)
                const isLastDay = isSameDay(dateObj, entryEndDateOnly)
                const isMultiDay = !isSameDay(entryStartDateOnly, entryEndDateOnly)

                let startTime = new Date(dateObj)
                let endTime = new Date(dateObj)

                // Check if this is an all-day event (startTime and endTime are null)
                if (entry.startTime === null && entry.endTime === null) {
                    // All-day event: span the entire day
                    startTime.setHours(0, 0, 0, 0)
                    endTime.setHours(23, 59, 59, 999)
                } else if (entry.startTime && entry.endTime) {
                    // Has specific times: parse them (could be string "HH:mm" or Date)
                    let startHours = 0
                    let startMinutes = 0
                    let endHours = 23
                    let endMinutes = 59

                    if (typeof entry.startTime === 'string') {
                        // Parse time string like "09:00"
                        const startParts = entry.startTime.split(':')
                        startHours = parseInt(startParts[0] || '0', 10)
                        startMinutes = parseInt(startParts[1] || '0', 10)
                    } else {
                        // It's a Date object
                        const entryStart = new Date(entry.startTime)
                        startHours = entryStart.getHours()
                        startMinutes = entryStart.getMinutes()
                    }

                    if (typeof entry.endTime === 'string') {
                        // Parse time string like "12:00"
                        const endParts = entry.endTime.split(':')
                        endHours = parseInt(endParts[0] || '0', 10)
                        endMinutes = parseInt(endParts[1] || '0', 10)
                    } else {
                        // It's a Date object
                        const entryEnd = new Date(entry.endTime)
                        endHours = entryEnd.getHours()
                        endMinutes = entryEnd.getMinutes()
                    }

                    // Handle multi-day events with specific times
                    if (isMultiDay) {
                        if (isFirstDay) {
                            // First day: starts at entry's startTime, ends at end of day
                            startTime = new Date(dateObj)
                            startTime.setHours(startHours, startMinutes, 0, 0)
                            endTime = new Date(dateObj)
                            endTime.setHours(23, 59, 59, 999)
                        } else if (isLastDay) {
                            // Last day: starts at start of day, ends at entry's endTime
                            startTime = new Date(dateObj)
                            startTime.setHours(0, 0, 0, 0)
                            endTime = new Date(dateObj)
                            endTime.setHours(endHours, endMinutes, 59, 999)
                        } else {
                            // Middle days: span full day
                            startTime = new Date(dateObj)
                            startTime.setHours(0, 0, 0, 0)
                            endTime = new Date(dateObj)
                            endTime.setHours(23, 59, 59, 999)
                        }
                    } else {
                        // Single day event: use the parsed times
                        startTime = new Date(dateObj)
                        startTime.setHours(startHours, startMinutes, 0, 0)
                        endTime = new Date(dateObj)
                        endTime.setHours(endHours, endMinutes, 59, 999)
                    }
                } else {
                    // Fallback: use startedOn/endedOn if available, otherwise full day
                    if (entry.startedOn && entry.endedOn) {
                        const entryStart = new Date(entry.startedOn)
                        const entryEnd = new Date(entry.endedOn)

                        // Check if entry has time components (not just date)
                        const hasTimeComponents = entryStart.getHours() !== 0 || entryStart.getMinutes() !== 0 ||
                            entryEnd.getHours() !== 23 || entryEnd.getMinutes() !== 59

                        if (hasTimeComponents && isMultiDay) {
                            // Multi-day event with time components
                            if (isFirstDay) {
                                // First day: starts at entry's start time, ends at end of day
                                startTime = new Date(dateObj)
                                startTime.setHours(entryStart.getHours(), entryStart.getMinutes(), 0, 0)
                                endTime = new Date(dateObj)
                                endTime.setHours(23, 59, 59, 999)
                            } else if (isLastDay) {
                                // Last day: starts at start of day, ends at entry's end time
                                startTime = new Date(dateObj)
                                startTime.setHours(0, 0, 0, 0)
                                endTime = new Date(dateObj)
                                endTime.setHours(entryEnd.getHours(), entryEnd.getMinutes(), 59, 999)
                            } else {
                                // Middle days: span full day
                                startTime = new Date(dateObj)
                                startTime.setHours(0, 0, 0, 0)
                                endTime = new Date(dateObj)
                                endTime.setHours(23, 59, 59, 999)
                            }
                        } else if (hasTimeComponents) {
                            // Single day event with time components
                            startTime = new Date(dateObj)
                            startTime.setHours(entryStart.getHours(), entryStart.getMinutes(), 0, 0)
                            endTime = new Date(dateObj)
                            endTime.setHours(entryEnd.getHours(), entryEnd.getMinutes(), 59, 999)
                        } else {
                            // All-day based on startedOn/endedOn (handles both single and multi-day)
                            startTime.setHours(0, 0, 0, 0)
                            endTime.setHours(23, 59, 59, 999)
                        }
                    } else {
                        // Default: full day
                        startTime.setHours(0, 0, 0, 0)
                        endTime.setHours(23, 59, 59, 999)
                    }
                }

                const startHour = startTime.getHours()
                const endHour = endTime.getHours()
                const leftOffset = (startTime.getMinutes() / 60) * 100
                const rightOffset = (endTime.getMinutes() / 60) * 100

                // Get color for calendar entry - handle causali + custom types like in calendar.tsx
                let color: string
                if ((entry as any).absenceType?.colour) {
                    // Prefer the causale's own colour so it's visually distinct from generic vacation.
                    color = (entry as any).absenceType.colour
                } else if (entry.type === CalendarEntryEntityTypeEnum.Custom && entry.customType?.colour) {
                    // Use custom type color if available
                    color = entry.customType.colour
                } else {
                    // Use standard colors for default types
                    const colorValue = calendarEntryColor(entry.type.toUpperCase() as any, theme)
                    // Ensure hex colors have # prefix, theme colors are already valid
                    color = colorValue.startsWith('#') || colorValue.includes('rgb') || colorValue.includes('rgba')
                        ? colorValue
                        : `#${colorValue}`
                }

                const processedEntry = {
                    entry,
                    startTime,
                    endTime,
                    startHour,
                    endHour,
                    leftOffset,
                    rightOffset,
                    color,
                    type: entry.type
                }

                const key = `${entry.employeeId}-${dateKey}`
                if (!cache.has(key)) cache.set(key, [])
                cache.get(key)?.push(processedEntry)
            })
        })

        return cache
    }, [calendarEntries, theme])

    // Helper to read from cache (O(1) access)
    const getShiftsFromCache = (employeeId: number, day: Date) => {
        const key = `${employeeId}-${format(day, "yyyy-MM-dd")}`
        return shiftsCache.get(key) || []
    }

    const getCalendarFromCache = (employeeId: number, day: Date) => {
        const key = `${employeeId}-${format(day, "yyyy-MM-dd")}`
        return calendarCache.get(key) || []
    }

    // Localized label for a calendar entry: prefer the absence causale (AbsenceType),
    // then a custom type name, then the legacy type translation. This shows causali as
    // what they really are (e.g. "Malattia") instead of their legacy "Vacation" mapping.
    const calendarEntryLabel = (rawEntry: any): string => {
        const at = rawEntry?.absenceType
        if (at) {
            const lang = (i18n.language || 'en').slice(0, 2)
            return lang === 'it' ? at.labelIt : lang === 'de' ? at.labelDe : lang === 'fr' ? at.labelFr : at.labelEn
        }
        if ((rawEntry?.type === 'CUSTOM' || rawEntry?.type === CalendarEntryEntityTypeEnum.Custom) && rawEntry?.customType) {
            return rawEntry.customType.name
        }
        return t(`employees.calendar.types.${String(rawEntry?.type ?? '').toLowerCase()}`) || rawEntry?.type
    }

    // Check if user is DEPCHIEF for the current season
    useEffect(() => {
        const checkDepchiefRole = async () => {
            const userRole = await checkSeasonalRole(seasonId)
            setIsDepchief(userRole === 'DEPCHIEF')
        }
        checkDepchiefRole()
    }, [seasonId])

    // Read-only viewer mode: USER-role employees with the canViewShifts permission
    // get full visibility but every cell is read-only across all dates.
    const isViewerOnly = checkTokenRole() === 'USER' && checkCanViewShifts()

    // Helper function to check if a date is read-only (past days for DEPCHIEF users)
    const isDateReadOnly = (date: Date): boolean => {
        if (isViewerOnly) return true
        if (!isDepchief) return false
        const today = startOfDay(new Date())
        const dateToCheck = startOfDay(date)
        // Past days (more than 2 days ago) are read-only, recent past (up to 2 days), today and future days are editable
        return isBefore(dateToCheck, subDays(today, 2))
    }

    // --- MANAGER-ONLY SHIFT NOTES ---
    // These are private notes only visible to managers (OWNER/MANAGER/CONTROLLER/DEPCHIEF),
    // separate from the public per-shift `notes` field that is exposed in the app/PDF.
    // Read-only viewers (USER + canViewShifts) must never see them.
    const canManageShiftNotes = !isViewerOnly && ['OWNER', 'MANAGER', 'CONTROLLER', 'DEPCHIEF'].includes(checkTokenRole())

    const fetchShiftNotes = useCallback(() => {
        if (!canManageShiftNotes) return
        shiftNotesApi
            .findAll({ params: { departmentId: selectedDepartment?.id, seasonId } })
            .then(({ data }) => setShiftNotes(data))
            .catch((error) => console.error('Error fetching shift notes:', error))
    }, [canManageShiftNotes, selectedDepartment?.id, seasonId])

    useEffect(() => {
        fetchShiftNotes()
    }, [fetchShiftNotes])

    // Notes for a given day (matched by calendar date, ignoring time/zone).
    const getShiftNotesForDay = (day: Date): ShiftNoteEntity[] =>
        shiftNotes.filter((note) => isSameDay(new Date(note.date), day))

    // A date is "flagged" when it has at least one unresolved manager note.
    const hasUnresolvedShiftNotes = (day: Date): boolean =>
        getShiftNotesForDay(day).some((note) => !note.resolved)

    const handleOpenShiftNotes = (day: Date) => {
        setSelectedShiftNotesDate(ignoreUTC(day))
        setOpenShiftNotesModal(true)
    }

    // --- MANAGER-ONLY PER-EMPLOYEE-PER-DAY NOTES ---
    // Loaded once for the visible date range so we can both render the thread in
    // the Add-Shift modal and flag employee×day cells that have unresolved notes.
    const fetchEmployeeDayNotes = useCallback(() => {
        if (!canManageShiftNotes) return
        employeeDayNotesApi
            .findAll({
                params: {
                    departmentId: selectedDepartment?.id,
                    seasonId,
                    from: `${format(selectedDay, "yyyy-MM-dd")}T00:00:00.000Z`,
                    to: `${format(selectedEndDay, "yyyy-MM-dd")}T23:59:59.999Z`,
                }
            })
            .then(({ data }) => setEmployeeDayNotes(data))
            .catch((error) => console.error('Error fetching employee day notes:', error))
    }, [canManageShiftNotes, selectedDepartment?.id, seasonId, selectedDay, selectedEndDay])

    useEffect(() => {
        fetchEmployeeDayNotes()
    }, [fetchEmployeeDayNotes])

    // Notes about a specific employee on a specific calendar day.
    const getEmployeeDayNotes = (employeeId: number, day: Date): EmployeeDayNoteEntity[] =>
        employeeDayNotes.filter((note) => note.employeeId === employeeId && isSameDay(new Date(note.date), day))

    // Cell is "flagged" when the employee has at least one unresolved note that day.
    const hasEmployeeDayNotes = (employeeId: number, day: Date): boolean =>
        getEmployeeDayNotes(employeeId, day).some((note) => !note.resolved)

    // Unresolved notes are also rendered on the tableau itself as an all-day strip
    // (a note has no shift and no time, so it spans the whole day like an all-day
    // calendar event). Resolved notes stay hidden to keep the tableau clean.
    const getUnresolvedEmployeeDayNotes = (employeeId: number, day: Date): EmployeeDayNoteEntity[] =>
        getEmployeeDayNotes(employeeId, day).filter((note) => !note.resolved)

    // Image-only notes are stored with blank text.
    const employeeDayNoteText = (note: EmployeeDayNoteEntity): string => note.text?.trim() || "📷"

    // Mode-aware "sticky note" palette for the tableau note strips. Solid colors
    // (no alpha) because the strips overlay shift cards and grid lines.
    const noteStrip = theme.palette.mode === 'dark'
        ? { bg: '#3E3122', bgHover: '#4C3D2A', text: '#FFD9A0', accent: '#C99A4D' }
        : { bg: '#FFF4E0', bgHover: '#FFEBC7', text: '#6B4A10', accent: '#E3A63F' }

    const employeeDayNotesTooltip = (notes: EmployeeDayNoteEntity[]) => (
        <Box>
            <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                {t("employeeDayNotes.title")}
            </Typography>
            {notes.map((note) => (
                <Typography key={note.id} variant="caption" display="block" sx={{ whiteSpace: 'pre-wrap' }}>
                    {note.createdBy ? `${note.createdBy.firstName} ${note.createdBy.lastName}: ` : ''}{employeeDayNoteText(note)}
                </Typography>
            ))}
        </Box>
    )

    // Calculate fixed hour column width - show 24 hours on screen
    // Account for employee column (300px) and some padding
    const EMPLOYEE_COLUMN_WIDTH = 300
    const availableWidth = screenWidth - EMPLOYEE_COLUMN_WIDTH - 100 // 100px for padding/margins
    const HOUR_COLUMN_WIDTH = Math.max(20, Math.round((availableWidth / 24) * 0.7 * 0.7)) // Each hour column width, reduced by another 30% (total 51% from original), minimum 20px
    const EMPLOYEE_ROW_HEIGHT = 80 // Height of each employee row (reduced from 120px for more compact layout)
    // Minimum card/segment width (px) needed to show the hours counter inline. Narrower cards
    // render the hours as a floating badge just below the card instead so it stays visible.
    const HOURS_INLINE_MIN_WIDTH = 50

    const resetData = () => {
        setShifts([])
        setResources({})
    }

    const fetchHours = async (emps: EmployeeEntity[], weeklyHoursMap?: Record<number, number>) => {
        const w: Record<number, number> = {}

        // Use hoursForPeriodDaily to get hours for the actual date range
        try {
            // API signature: hoursForPeriodDaily(startDate: string, endDate: string, seasonId: number, departmentId?: number, options?: AxiosRequestConfig)
            const dailyHours = (await shiftApi.hoursForPeriodDaily(
                selectedDay.toISOString(),
                selectedEndDay.toISOString(),
                seasonId,
                selectedDepartment?.id
            )).data

            // Sum up hours for each employee from shifts only (calendar events are not counted)
            for (const e of emps) {
                const employeeDailyHours = dailyHours.filter((d: any) => d.employee?.id === e.id)
                const totalMinutes = employeeDailyHours.reduce((sum: number, d: any) => sum + (d.totalMinutes || 0), 0)
                const totalHours = totalMinutes / 60

                w[e.id] = Math.round(totalHours * 10) / 10 // Round to 1 decimal
            }
        } catch (error) {
            console.error("Error fetching hours for period:", error)
            // Fallback to weekly hours bulk endpoint if the API call fails
            try {
                const employeeIds = emps.map(emp => emp.id)
                // Note: Using type assertion until API is regenerated with new DTO fields
                const bulkResponse = await shiftApi.weeklyEmployeeHoursBulk({
                    employeeIds,
                    date: selectedDay.toISOString(),
                    seasonId,
                    departmentId: selectedDepartment?.id
                } as any)

                // Map the response to the hours record
                const bulkHours = bulkResponse.data || []
                bulkHours.forEach((item: { employeeId: number; hours: number }) => {
                    w[item.employeeId] = Math.round(item.hours)
                })

                // Ensure all employees have an entry (0 if not in response)
                emps.forEach(emp => {
                    if (w[emp.id] === undefined) {
                        w[emp.id] = 0
                    }
                })
            } catch (bulkError) {
                console.error("Error fetching weekly hours in bulk:", bulkError)
                // If bulk also fails, set all to 0
                emps.forEach(emp => {
                    w[emp.id] = 0
                })
            }
        }

        return w
    }

    // Fetch all agreements for all employees in bulk
    const fetchAgreementsForEmployees = async (emps: EmployeeEntity[]): Promise<Record<number, AgreementWithEmployeeDto[]>> => {
        if (emps.length === 0) return {}

        try {
            const employeeIds = emps.map(emp => emp.id)

            // Single bulk request for all employees
            const response = await agreementApi.findByEmployees({
                employeeIds,
                seasonId,
                departmentId: selectedDepartment?.id
            })

            const allAgreements = response.data || []

            // Group agreements by employeeId
            const agreementsMap: Record<number, AgreementWithEmployeeDto[]> = {}
            allAgreements.forEach(agreement => {
                if (!agreementsMap[agreement.employeeId]) {
                    agreementsMap[agreement.employeeId] = []
                }
                agreementsMap[agreement.employeeId].push(agreement)
            })

            return agreementsMap
        } catch (error) {
            console.error('Error fetching agreements for employees:', error)
            return {}
        }
    }

    // Get weekly work hours for an employee from their cached agreements or fallback to default
    const getWeeklyWorkHoursForEmployee = (employeeId: number, agreements: AgreementWithEmployeeDto[]): number => {
        try {
            // Find the agreement that's valid today
            const today = new Date()
            today.setHours(0, 0, 0, 0)

            const validAgreement = agreements.find((agreement: AgreementWithEmployeeDto) => {
                const startDate = new Date(agreement.startedOn)
                startDate.setHours(0, 0, 0, 0)

                // Check if agreement has started
                if (startDate > today) return false

                // Check if agreement has ended (if endedOn is set)
                if (agreement.endedOn) {
                    const endDate = new Date(agreement.endedOn)
                    endDate.setHours(23, 59, 59, 999)
                    if (endDate < today) return false
                }

                return true
            })

            // If we found a valid agreement with weeklyWorkHours, use it
            if (validAgreement?.weeklyWorkHours !== null && validAgreement?.weeklyWorkHours !== undefined) {
                return validAgreement.weeklyWorkHours
            }

            // Otherwise, fallback to default from tenant config
            return defaultWeeklyHours
        } catch (error) {
            console.error(`Error getting weekly work hours for employee ${employeeId}:`, error)
            // Fallback to default on error
            return defaultWeeklyHours
        }
    }

    // Fetch weekly work hours for all employees using cached agreements
    const fetchWeeklyWorkHours = async (emps: EmployeeEntity[]) => {
        // First fetch all agreements in bulk
        const agreementsMap = await fetchAgreementsForEmployees(emps)

        // Then calculate weekly work hours from cached agreements
        const hours: Record<number, number> = {}
        for (const emp of emps) {
            const employeeAgreements = agreementsMap[emp.id] || []
            hours[emp.id] = getWeeklyWorkHoursForEmployee(emp.id, employeeAgreements)
        }
        return hours
    }

    // Fetch subordinate order from backend
    const fetchSubordinateOrder = async () => {
        if (!selectedDepartment) return {}

        try {
            const response = await shiftApi.getSubordinateOrder(String(selectedDepartment.id), String(seasonId))
            const orderData: Record<number, number> = {}
            const hidden = new Set<number>()
            response.data.forEach((item: SubordinateOrderEntity) => {
                orderData[item.subordinateId] = item.order
                if (item.isHidden) hidden.add(item.subordinateId)
            })
            setHiddenEmployeeIds(hidden)
            return orderData
        } catch (error) {
            console.error("Error fetching subordinate order:", error)
            setHiddenEmployeeIds(new Set())
            return {}
        }
    }

    // Save subordinate order to backend
    const saveSubordinateOrder = async (orderItems: Array<{ subordinateId: number; departmentId: number; seasonId: number; order: number }>) => {
        if (!selectedDepartment) return

        setIsSavingOrder(true)
        try {
            const dto: ConfigureSubordinateOrderDto = {
                items: orderItems
            }
            await shiftApi.configureSubordinateOrder(dto)
            toast.success(t("shift.orderSaved") || "Employee order saved successfully")
            // Refresh the order data
            await fetchSubordinateOrder()
        } catch (error) {
            console.error("Error saving subordinate order:", error)
            toast.error(t("shift.orderSaveError") || "Failed to save employee order")
            throw error
        } finally {
            setIsSavingOrder(false)
        }
    }

    const fetchEmployees = async () => {
        if (!selectedDepartment) return

        // 1. Fetch subordinate order first
        const customOrder = await fetchSubordinateOrder()

        // 2. Fetch the raw relationship data
        // Pass 'false' so the backend does NOT filter by Date.now() — that filter would
        // hide every employee whose contract starts after today (e.g. new summer-season
        // hires before the season begins). Period-based filtering happens client-side
        // below, scoped to the user's selected viewing range.
        const response = await departmentsEmployeesApi.findAll(String(selectedDepartment.id), 'false', seasonId)
        const emps = response.data || []

        // 3. SORTING LOGIC: Apply custom order first, then work position
        emps.sort((a, b) => {
            const employeeA = a.employee
            const employeeB = b.employee

            if (!employeeA || !employeeB) return 0

            // Check if both employees have custom order
            const orderA = customOrder[employeeA.id]
            const orderB = customOrder[employeeB.id]
            const hasOrderA = orderA !== undefined
            const hasOrderB = orderB !== undefined

            // If both have custom order, sort by order
            if (hasOrderA && hasOrderB) {
                return orderA - orderB
            }

            // If only A has order, A comes first
            if (hasOrderA && !hasOrderB) {
                return -1
            }

            // If only B has order, B comes first
            if (!hasOrderA && hasOrderB) {
                return 1
            }

            // If neither has custom order, sort by work position hierarchy
            // Primary Sort: Work Position Hierarchy
            let rankA = 999999
            let rankB = 999999

            // Check if workPosition is included in the API response
            const workPosA = (a as any).workPosition
            const workPosB = (b as any).workPosition

            if (workPosA) {
                rankA = workPosA.hierarchyNumber ??
                    workPosA.order ??
                    workPosA.sortOrder ??
                    workPosA.level ??
                    workPosA.id ??
                    999999
            } else {
                // Fallback: look up from workPositions state or use workPositionId
                const wpA = workPositions.find(wp => wp.id === a.workPositionId)
                rankA = (wpA as any)?.hierarchyNumber ??
                    (wpA as any)?.order ??
                    (wpA as any)?.sortOrder ??
                    (wpA as any)?.level ??
                    a.workPositionId ??
                    999999
            }

            if (workPosB) {
                rankB = workPosB.hierarchyNumber ??
                    workPosB.order ??
                    workPosB.sortOrder ??
                    workPosB.level ??
                    workPosB.id ??
                    999999
            } else {
                // Fallback: look up from workPositions state or use workPositionId
                const wpB = workPositions.find(wp => wp.id === b.workPositionId)
                rankB = (wpB as any)?.hierarchyNumber ??
                    (wpB as any)?.order ??
                    (wpB as any)?.sortOrder ??
                    (wpB as any)?.level ??
                    b.workPositionId ??
                    999999
            }

            if (rankA !== rankB) {
                return rankA - rankB
            }

            // Secondary Sort: Tie-breaker by Last Name (Alphabetical)
            const nameA = employeeA.lastName ?? ""
            const nameB = employeeB.lastName ?? ""
            return nameA.localeCompare(nameB)
        })

        // 4. Map the sorted relationships to Employee entities and store work positions
        let employees = emps.map(emp => emp.employee).filter((emp): emp is EmployeeEntity => emp !== undefined)

        // 5. Filter out employees without a valid contract covering the selected period.
        // This replaces the old backend filter which was anchored to Date.now() and
        // therefore wrongly hid every employee whose contract had not started yet today.
        if (excludeNoContract) {
            const periodStart = selectedDay
            const periodEnd = selectedEndDay
            employees = employees.filter(emp => {
                const contract = emp.employeesToSeasons?.[0]?.contract
                // No contract, draft, or no start date → not a valid contract
                if (!contract?.startedOn || contract.isDraft) return false
                // Normalize to local midnight to avoid UTC-vs-local off-by-one. Stored
                // dates are UTC midnight; parsed in CEST that becomes 02:00 local, which
                // would otherwise compare greater than the local-midnight period bounds.
                const contractStart = startOfDay(new Date(contract.startedOn))
                const contractEnd = contract.endedOn ? startOfDay(new Date(contract.endedOn)) : null
                // Exclude if contract ends before the period starts
                if (contractEnd && contractEnd < periodStart) return false
                // Exclude if contract starts after the period ends
                if (contractStart > periodEnd) return false
                return true // Contract overlaps with the period
            })
        }

        const workPosMap: Record<number, number> = {}
        emps.forEach(emp => {
            if (emp.employee) {
                workPosMap[emp.employee.id] = emp.workPositionId
            }
        })
        setEmployeeWorkPositions(workPosMap)

        employeesRef.current = employees
        setEmployees(employees)

        // Fetch weekly work hours first, then fetch worked hours (which needs weeklyWorkHours for calendar events)
        const weeklyHours = await fetchWeeklyWorkHours(employees)
        setWeeklyWorkHours(weeklyHours)

        // Now fetch worked hours with access to weeklyWorkHours and calendarEntries.
        // Guard against a department/period switch that happened while we were
        // awaiting: only apply if these are still the employees on screen.
        const hours = await fetchHours(employees, weeklyHours)
        if (employeesRef.current === employees) {
            setWorkedHours(hours)
        }

        // Don't call fetchCalendarEntries here - let the useEffect handle it
        // This prevents race conditions and duplicate calls
    }

    // Handle entering edit order mode
    const handleEnterEditOrder = () => {
        // Create a fresh copy to ensure proper references
        const employeesCopy = employees.map(emp => ({ ...emp }))
        setEmployeesInEditMode(employeesCopy)
        employeesInEditModeRef.current = employeesCopy
        // Seed the edit-mode hidden set from the currently applied one.
        setHiddenInEditMode(new Set(hiddenEmployeeIds))
        setEditOrder(true)
    }

    // Handle canceling edit order mode
    const handleCancelEditOrder = () => {
        setEditOrder(false)
        setEmployeesInEditMode([])
        employeesInEditModeRef.current = []
        setHiddenInEditMode(new Set())
    }

    // Toggle an employee's hidden state while in edit-order mode
    const handleToggleHiddenInEditMode = (employeeId: number) => {
        if (isViewerOnly) return
        setHiddenInEditMode(prev => {
            const next = new Set(prev)
            if (next.has(employeeId)) {
                next.delete(employeeId)
            } else {
                next.add(employeeId)
            }
            return next
        })
    }

    // Handle drag end in edit order mode
    const handleDragEnd = (result: DropResult) => {
        if (isViewerOnly) return
        if (!result.destination) return

        // Don't do anything if dropped in the same position
        if (result.destination.index === result.source.index) return

        // Use ref to get current state to avoid stale closure issues
        const currentItems = [...employeesInEditModeRef.current]
        const [reorderedItem] = currentItems.splice(result.source.index, 1)
        currentItems.splice(result.destination.index, 0, reorderedItem)

        setEmployeesInEditMode(currentItems)
        employeesInEditModeRef.current = currentItems
    }

    // Handle saving the new order
    const handleSaveOrder = async () => {
        if (!selectedDepartment) return

        try {
            const orderItems = employeesInEditMode.map((emp, index) => ({
                subordinateId: emp.id,
                departmentId: selectedDepartment.id,
                seasonId,
                order: index + 1,
                isHidden: hiddenInEditMode.has(emp.id)
            }))

            await saveSubordinateOrder(orderItems)
            setEditOrder(false)
            // Refresh employees to apply new order
            await fetchEmployees()
        } catch (error) {
            // Error already handled in saveSubordinateOrder
        }
    }

    // Refresh worked hours and update assignment score
    const refreshHoursAndScore = async () => {
        if (!selectedDepartment || employees.length === 0) return

        try {
            // Capture the list we compute for; if the department/period switches
            // while fetchHours is in-flight, employeesRef will have moved on and we
            // must NOT overwrite the new department's hours with this stale result.
            const computedFor = employees
            // Refresh worked hours for current employees (use current weeklyWorkHours state)
            const updatedHours = await fetchHours(employees, weeklyWorkHours)
            if (employeesRef.current !== computedFor) return
            setWorkedHours(updatedHours)

            // Update score after hours are refreshed
            const score = await calculateAssignmentScore()
            if (score) {
                setCurrentAssignmentScore(score.score)
            }
        } catch (error) {
            console.error("Error refreshing hours and score:", error)
        }
    }

    // Calculate assignment score based on assigned hours vs required hours
    const calculateAssignmentScore = useCallback(async () => {
        if (!selectedDepartment || employees.length === 0) return null

        // Use eachDayOfInterval to get the exact number of days (inclusive of both start and end)
        const daysInRange = eachDayOfInterval({ start: selectedDay, end: selectedEndDay }).length
        const weeksInRange = daysInRange / 7

        const employeeScores: Array<{
            employee: EmployeeEntity
            requiredHours: number
            assignedHours: number
            score: number
            percentage: number
            overAllocationHours: number
        }> = []

        let totalRequiredHours = 0
        let totalAssignedHours = 0
        let totalOverAllocationHours = 0

        for (const employee of employees) {
            // Calculate required hours: weeklyWorkHours * weeksInRange * workPercentage
            const employeeWeeklyHours = weeklyWorkHours[employee.id] ?? defaultWeeklyHours
            const workPercentage = (employee.employeesToSeasons?.[0]?.contract?.workPercentage ?? 100) / 100
            const requiredHours = employeeWeeklyHours * weeksInRange * workPercentage

            // Get assigned hours from workedHours (this is already calculated for the period)
            const assignedHours = workedHours[employee.id] || 0

            // Calculate over-allocation (only if assigned > required)
            const overAllocationHours = Math.max(0, assignedHours - requiredHours)

            // Calculate score: assigned / required, capped at 100% (over-allocation doesn't improve score)
            const percentage = requiredHours > 0 ? Math.min(100, (assignedHours / requiredHours) * 100) : 0
            const score = Math.round(percentage)

            employeeScores.push({
                employee,
                requiredHours: Math.round(requiredHours * 10) / 10,
                assignedHours: Math.round(assignedHours * 10) / 10,
                score,
                percentage,
                overAllocationHours: Math.round(overAllocationHours * 10) / 10
            })

            totalRequiredHours += requiredHours
            // For overall score calculation, only count up to required hours (over-allocation doesn't help)
            totalAssignedHours += Math.min(assignedHours, requiredHours)
            totalOverAllocationHours += overAllocationHours
        }

        // Calculate overall score: only count assigned hours up to required (over-allocation doesn't improve score)
        const overallPercentage = totalRequiredHours > 0 ? Math.min(100, (totalAssignedHours / totalRequiredHours) * 100) : 0
        const overallScore = Math.round(overallPercentage)

        return {
            score: overallScore,
            totalRequiredHours: Math.round(totalRequiredHours * 10) / 10,
            totalAssignedHours: Math.round((totalAssignedHours + totalOverAllocationHours) * 10) / 10, // Total includes over-allocation for display
            totalOverAllocationHours: Math.round(totalOverAllocationHours * 10) / 10,
            employeeScores: employeeScores.sort((a, b) => {
                // Sort by: over-allocated first (negative), then by score descending
                if (a.overAllocationHours > 0 && b.overAllocationHours === 0) return -1
                if (a.overAllocationHours === 0 && b.overAllocationHours > 0) return 1
                return b.score - a.score
            })
        }
    }, [selectedDepartment, employees, weeklyWorkHours, defaultWeeklyHours, selectedDay, selectedEndDay, workedHours])

    const handleOpenAssignmentScoreModal = async () => {
        const score = await calculateAssignmentScore()
        if (score) {
            setAssignmentScore(score)
            setOpenAssignmentScoreModal(true)
        }
    }

    const fetchRequestsHasBeenSent = async () => {
        // Always use the currently selected seasonId from context
        // hasAvailabilityBeenSent requires seasonId, ensuring DEPCHIEF users only see data for their season
        const resp = await shiftApi.hasAvailabilityBeenSent(
            selectedDay.toISOString(),
            selectedEndDay.toISOString(),
            seasonId, // Always use the current seasonId from useSeason() context
            selectedDepartment?.id
        )
        if (!resp.data) setCountAviability(false)
        setRequestsHasBeenSent(resp.data)
    }

    const fetchCalendarEntries = useCallback(async (): Promise<Record<string, CalendarEntryEntity[]>> => {
        // Validate prerequisites
        if (!selectedDepartment) {
            console.warn("fetchCalendarEntries: No department selected")
            return {}
        }

        if (employees.length === 0) {
            console.warn("fetchCalendarEntries: No employees loaded yet")
            // Don't update state - preserve existing entries or return empty
            return {}
        }

        if (!selectedDay || !selectedEndDay) {
            console.warn("fetchCalendarEntries: Invalid date range")
            return {}
        }

        const entriesMap: Record<string, CalendarEntryEntity[]> = {}

        try {
            // Format dates as ISO strings at midnight UTC
            const startDateISO = `${format(selectedDay, "yyyy-MM-dd")}T00:00:00.000Z`
            const endDateISO = `${format(selectedEndDay, "yyyy-MM-dd")}T23:59:59.999Z`

            // Extract employee IDs from the employees array
            const employeeIds = employees.map(emp => emp.id)

            if (employeeIds.length === 0) {
                console.warn("fetchCalendarEntries: No employee IDs to fetch")
                // Don't update state - preserve existing entries
                return {}
            }

            // Single bulk request for all employees and all days in the range
            // Note: Using type assertion due to DTO naming conflict in generated API
            const response = await calendarEntriesApi.findByDateRange({
                employeeIds,
                startDate: startDateISO,
                endDate: endDateISO,
                seasonId,
                departmentId: selectedDepartment.id
            } as any)

            // Organize entries by ALL days they span (from startedOn to endedOn)
            // This ensures multi-day events appear on all relevant days
            const fetchedEntries = response.data || []
            fetchedEntries.forEach(entry => {
                if (entry.startedOn && entry.endedOn) {
                    // Parse the ISO strings to get date objects
                    const entryStartDate = new Date(entry.startedOn)
                    const entryEndDate = new Date(entry.endedOn)

                    // Generate all dates between start and end (inclusive)
                    let currentDate = new Date(entryStartDate)
                    currentDate.setHours(0, 0, 0, 0)
                    const endDate = new Date(entryEndDate)
                    endDate.setHours(23, 59, 59, 999)

                    // Iterate through all days the entry spans
                    while (currentDate <= endDate) {
                        const dateStr = format(currentDate, "yyyy-MM-dd")

                        // Only include dates within our selected range
                        if (currentDate >= selectedDay && currentDate <= selectedEndDay) {
                            if (!entriesMap[dateStr]) {
                                entriesMap[dateStr] = []
                            }
                            // Only add if not already present (avoid duplicates)
                            if (!entriesMap[dateStr].some(e => e.id === entry.id)) {
                                entriesMap[dateStr].push(entry)
                            }
                        }

                        // Move to next day
                        currentDate = addDays(currentDate, 1)
                    }
                }
            })


            // Only update state if we actually have entries or if this is a fresh fetch (empty is valid)
            // This prevents overwriting valid entries with empty data from race conditions
            setCalendarEntries(entriesMap)
        } catch (error) {
            console.error(`Error fetching calendar entries for date range ${format(selectedDay, "yyyy-MM-dd")} to ${format(selectedEndDay, "yyyy-MM-dd")}:`, error)
            // Don't clear existing entries on error, just log it
            // Return existing entries instead of empty object
            return {}
        }

        return entriesMap
    }, [selectedDepartment, employees, selectedDay, selectedEndDay, seasonId])

    const fetchData = async () => {
        if (!selectedDepartment) return

        const { shifts, resources, workPositions } = await fetchShiftAssignmentData(selectedDepartment, seasonId, selectedDay, selectedEndDay, true)

        if (shifts.length === 0) {
            resetData()
            return
        }
        setShifts(shifts)
        setResources(resources)
        setWorkPositions(workPositions || [])

        // Update templates cache to refresh the "Required Employees" header bar
        await fetchTemplatesForAllDays()

        // Check which days have templates to update the day header colors (orange when no template)
        await checkDaysForTemplates()

        // Refresh hours and score after data is updated
        if (employees.length > 0) {
            await refreshHoursAndScore()
        }
    }

    // Check which days don't have templates assigned
    const checkDaysForTemplates = async () => {
        if (!selectedDepartment) return

        const daysWithoutTemplateSet = new Set<string>()
        const days = eachDayOfInterval({ start: selectedDay, end: selectedEndDay })

        try {
            // Format dates as ISO strings at midnight UTC
            const startDateISO = `${format(selectedDay, "yyyy-MM-dd")}T00:00:00.000Z`
            const endDateISO = `${format(selectedEndDay, "yyyy-MM-dd")}T23:59:59.999Z`

            // Single bulk request for all days in the range
            const response = await calendarEntriesShiftTemplateApi.findByDateRange({
                startDate: startDateISO,
                endDate: endDateISO,
                departmentId: selectedDepartment.id,
                seasonId
            })

            const allEntries = response.data || []

            // Create a set of dates that have templates
            const datesWithTemplates = new Set<string>()
            allEntries.forEach(entry => {
                if (entry.startedOn && entry.shiftTemplate) {
                    const dateString = entry.startedOn.split('T')[0] // Extract "YYYY-MM-DD"
                    datesWithTemplates.add(dateString)
                }
            })

            // Find days without templates
            days.forEach(day => {
                const dateString = format(day, "yyyy-MM-dd")
                if (!datesWithTemplates.has(dateString)) {
                    daysWithoutTemplateSet.add(dateString)
                }
            })
        } catch (error) {
            // If error, assume all days have no template
            days.forEach(day => {
                const dateString = format(day, "yyyy-MM-dd")
                daysWithoutTemplateSet.add(dateString)
            })
        }

        setDaysWithoutTemplate(daysWithoutTemplateSet)
    }

    const handleAssignTemplateToDay = async (day: Date) => {
        setSelectedDayForTemplate(day)
        setOpenAssignTemplateModal(true)

        // Fetch available templates for the department
        try {
            const templates = await shiftTemplateApi.findAll(selectedDepartment?.id, isDepchief ? seasonId : undefined)
            // Filter out generated templates (only show original templates)
            const originalTemplates = templates.data.filter(t => t.generatedFromShiftTemplateId === -1)
            setAvailableTemplates(originalTemplates)
        } catch (error) {
            console.error("Error fetching templates:", error)
            toast.error(t("shift.errorFetchingShifts"))
        }
    }

    const handleTemplateAssignSubmit = async (templateId: number) => {
        if (!selectedDayForTemplate || !selectedDepartment) return

        try {
            const dateString = format(selectedDayForTemplate, "yyyy-MM-dd")
            // Convert JavaScript day (0=Sunday, 1=Monday, etc.) to API format (MONDAY, TUESDAY, etc.)
            const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']
            const dayOfWeek = dayNames[selectedDayForTemplate.getDay()]

            const payload = {
                templateId,
                fromDate: dateString,
                toDate: dateString,
                daysOfWeek: [dayOfWeek]
            }

            const role = checkTokenRole()
            const isAuthorized = role === 'OWNER' || role === 'MANAGER' || role === 'CONTROLLER' || isDepchief

            if (!isAuthorized || !seasonId || !selectedDepartment) {
                toast.error(t("shift.unauthorized") || "You are not authorized to perform this action")
                return
            }

            await calendarEntriesShiftTemplateApi.assignShiftTemplate(
                "false", // allowOverwrite = false
                selectedDepartment.id, // departmentId
                seasonId,
                payload
            )

            toast.success(t("shift.templateAssignedSuccessfully") || "Template assigned successfully")
            setOpenAssignTemplateModal(false)
            setSelectedDayForTemplate(null)

            // Refresh data
            fetchData()
            fetchCalendarEntries()
            checkDaysForTemplates()
        } catch (error: any) {
            console.error("Error assigning template:", error)
            toast.error(error.response?.data?.message || t("shift.errorAssigningTemplate") || "Error assigning template")
        }
    }

    const fetchDepartemnts = async () => {
        const role = checkTokenRole()
        // canViewShifts grants cross-department visibility regardless of role,
        // so it takes precedence over the DEPCHIEF boss-only narrowing.
        const d =
            checkCanViewShifts()
                ? (await departmentsApi.findAll()).data
                : role === "DEPCHIEF"
                    ? (await departmentsApi.findWhereIsBoss(seasonId)).data
                    : (await departmentsApi.findAll()).data
        setDepartments(d)

        // Load saved department from localStorage
        const savedDepartmentId = localStorage.getItem('shift-assignment-selected-department-id')
        const savedDepartment = savedDepartmentId
            ? d.find(dept => dept.id === parseInt(savedDepartmentId, 10))
            : null

        setSelectedDepartment(
            savedDepartment
                ? {
                    id: savedDepartment.id,
                    label: savedDepartment.name
                }
                : d[0]
                    ? {
                        id: d[0].id,
                        label: d[0].name
                    }
                    : null
        )
    }

    useEffect(() => {
        fetchData()
        checkDaysForTemplates()
        fetchTemplatesForAllDays()
    }, [selectedDepartment, selectedDay, selectedEndDay, seasonId])

    // Clear calendar entries and employees when department changes to prevent showing old department's data
    useEffect(() => {
        if (selectedDepartment?.id) {
            setCalendarEntries({})
            employeesRef.current = [] // Mark the old list as stale before the new one loads
            setEmployees([]) // Clear employees so useEffect doesn't run with stale data
        }
    }, [selectedDepartment?.id])

    // Dedicated useEffect to ensure calendar entries are always loaded when prerequisites are met
    // This will only run after employees are loaded for the new department
    useEffect(() => {
        // Only fetch if we have all required data: department, employees, and valid date range
        if (selectedDepartment && employees.length > 0 && selectedDay && selectedEndDay) {
            fetchCalendarEntries()
        }
    }, [selectedDepartment, employees.length, selectedDay, selectedEndDay, seasonId, fetchCalendarEntries])

    // Fetch templates for all days in the range
    const fetchTemplatesForAllDays = async () => {
        if (!selectedDepartment) return

        const templatesCache: Record<string, { template: ShiftTemplateEntity; shifts: ShiftEntity[] }> = {}

        try {
            // Format dates as ISO strings at midnight UTC
            const startDateISO = `${format(selectedDay, "yyyy-MM-dd")}T00:00:00.000Z`
            const endDateISO = `${format(selectedEndDay, "yyyy-MM-dd")}T23:59:59.999Z`

            // Single bulk request for all days in the range
            const response = await calendarEntriesShiftTemplateApi.findByDateRange({
                startDate: startDateISO,
                endDate: endDateISO,
                departmentId: selectedDepartment.id,
                seasonId
            })

            const allEntries = response.data || []

            // Organize entries by their startedOn date
            allEntries.forEach(entry => {
                if (entry.startedOn && entry.shiftTemplate) {
                    // Extract date string from startedOn (format: "2025-11-05T00:00:00.000Z")
                    const dateString = entry.startedOn.split('T')[0] // Extract "2025-11-05"

                    templatesCache[dateString] = {
                        template: entry.shiftTemplate,
                        shifts: entry.shiftTemplate.shifts || []
                    }
                }
            })
        } catch (error) {
            console.error(`Error fetching templates for date range ${format(selectedDay, "yyyy-MM-dd")} to ${format(selectedEndDay, "yyyy-MM-dd")}:`, error)
        }

        setDayTemplatesCache(templatesCache)
    }

    useEffect(() => {
        fetchEmployees()
    }, [selectedDepartment, seasonId, countAviability, excludeNoContract, selectedDay, selectedEndDay])

    // Update assignment score when employees, worked hours, or weekly work hours change
    useEffect(() => {
        const updateScore = async () => {
            if (employees.length > 0 && Object.keys(weeklyWorkHours).length > 0) {
                const score = await calculateAssignmentScore()
                if (score) {
                    setCurrentAssignmentScore(score.score)
                }
            } else {
                setCurrentAssignmentScore(null)
            }
        }
        updateScore()
    }, [calculateAssignmentScore, weeklyWorkHours])

    useEffect(() => {
        fetchRequestsHasBeenSent()
    }, [selectedDay, selectedEndDay, selectedDepartment])

    useEffect(() => {
        fetchDepartemnts()
    }, [seasonId])

    // Proactively scan the currently displayed period for working-time compliance
    // issues so the planner is warned without having to open the overview manually.
    useEffect(() => {
        let cancelled = false
        const scanCompliance = async () => {
            if (!seasonId || !selectedDepartment) {
                setComplianceIssueCount(0)
                return
            }
            try {
                const res = await complianceApi.getViolations({
                    from: selectedDay.toISOString(),
                    to: selectedEndDay.toISOString(),
                    seasonId,
                    departmentId: selectedDepartment.id,
                })
                if (cancelled) return
                setComplianceMode(res.data.mode)
                setComplianceIssueCount(res.data.mode === 'off' ? 0 : (res.data.violations?.length ?? 0))
            } catch {
                if (!cancelled) setComplianceIssueCount(0)
            }
        }
        scanCompliance()
        return () => { cancelled = true }
    }, [selectedDepartment, seasonId, selectedDay, selectedEndDay])

    // Fetch tenant config to get default weekly hours
    useEffect(() => {
        const fetchTenantConfig = async () => {
            try {
                const config = await tenantConfigApi.findConfigs()
                if (config.data?.defaultWeeklyHours) {
                    setDefaultWeeklyHours(config.data.defaultWeeklyHours)
                }
            } catch (error) {
                console.error('Error fetching tenant config:', error)
                // Keep default of 40 if fetch fails
            }
        }
        fetchTenantConfig()
    }, [])

    // Update temp dates when selected dates change
    useEffect(() => {
        setTempStartDate(selectedDay)
        setTempEndDate(selectedEndDay)
    }, [selectedDay, selectedEndDay])

    // Update current time every minute
    useEffect(() => {
        const updateCurrentTime = () => {
            setCurrentTime(new Date())
        }
        updateCurrentTime() // Set immediately
        const interval = setInterval(updateCurrentTime, 60000) // Update every minute
        return () => clearInterval(interval)
    }, [])

    const handleOpenDateRangePicker = (event: React.MouseEvent<HTMLButtonElement>) => {
        setTempStartDate(selectedDay)
        setTempEndDate(selectedEndDay)
        setDateRangePickerAnchor(event.currentTarget)
    }

    const handleCloseDateRangePicker = () => {
        setDateRangePickerAnchor(null)
    }

    const handleApplyDateRange = () => {
        if (tempStartDate && tempEndDate) {
            if (tempStartDate > tempEndDate) {
                toast.error(t("shift.invalidDateRange"))
                return
            }
            setSelectedDay(tempStartDate)
            setSelectedEndDay(tempEndDate)
            // Save to localStorage
            localStorage.setItem('shift-assignment-selected-start-date', tempStartDate.toISOString())
            localStorage.setItem('shift-assignment-selected-end-date', tempEndDate.toISOString())
            handleCloseDateRangePicker()
        }
    }

    const handleSetTodayAsStart = () => {
        const today = startOfDay(new Date())
        // Keep the current end date, but make sure it's not before the new start
        const end = selectedEndDay && selectedEndDay >= today ? selectedEndDay : addDays(today, 7)

        setTempStartDate(today)
        setTempEndDate(end)
        setSelectedDay(today)
        setSelectedEndDay(end)
        // Save to localStorage
        localStorage.setItem('shift-assignment-selected-start-date', today.toISOString())
        localStorage.setItem('shift-assignment-selected-end-date', end.toISOString())
        handleCloseDateRangePicker()
    }

    const handleQuickPeriodSelect = (period: 'thisWeek' | 'nextWeek' | 'thisMonth' | 'nextMonth') => {
        const now = new Date()
        let start: Date
        let end: Date

        switch (period) {
            case 'thisWeek':
                start = startOfWeek(now, { weekStartsOn: 1 })
                end = endOfWeek(now, { weekStartsOn: 1 })
                break
            case 'nextWeek': {
                const nextWeekStart = addWeeks(now, 1)
                start = startOfWeek(nextWeekStart, { weekStartsOn: 1 })
                end = endOfWeek(nextWeekStart, { weekStartsOn: 1 })
                break
            }
            case 'thisMonth':
                start = startOfMonth(now)
                end = endOfMonth(now)
                break
            case 'nextMonth': {
                const nextMonthStart = addMonths(now, 1)
                start = startOfMonth(nextMonthStart)
                end = endOfMonth(nextMonthStart)
                break
            }
        }

        setTempStartDate(start)
        setTempEndDate(end)
        setSelectedDay(start)
        setSelectedEndDay(end)
        // Save to localStorage
        localStorage.setItem('shift-assignment-selected-start-date', start.toISOString())
        localStorage.setItem('shift-assignment-selected-end-date', end.toISOString())
        handleCloseDateRangePicker()
    }

    const sendShifts = async (
        startDate: Date,
        endDate: Date,
        mode: string,
        sendOnWhatsapp: boolean,
        languageOverride?: string
    ) => {
        // Helper function to format date at local midnight to avoid timezone issues
        const formatDateForBackend = (date: Date, isEndDate: boolean = false): string => {
            const localDate = new Date(date);
            localDate.setHours(0, 0, 0, 0);
            const dateString = format(localDate, 'yyyy-MM-dd');
            // For end date, use end of day to include the entire day
            return isEndDate ? `${dateString}T23:59:59.999Z` : `${dateString}T00:00:00.000Z`;
        };

        await shiftApi.sendShifts({
            params: {
                startDate: formatDateForBackend(startDate, false),
                endDate: formatDateForBackend(endDate, true),
                seasonId,
                departmentId:
                    selectedDepartment && mode === "department"
                        ? +selectedDepartment.id
                        : undefined,
                sendOnWhatsapp,
                ...(languageOverride ? { languageOverride } : {}),
            },
        })
        fetchData()
    }

    const requestAviability = async (startDate: Date, endDate: Date, mode: string, expirationDate: Date) => {
        await shiftApi
            .createAviabilityRequests({
                startDate: startDate.toISOString(),
                expirationDate: expirationDate.toISOString(),
                endDate: endDate.toISOString(),
                seasonId,
                departmentId: selectedDepartment && mode === "department" ? +selectedDepartment.id : undefined
            })
            .then(() => {
                toast.success(t("shift.availabilityRequestSuccess"))
                fetchRequestsHasBeenSent()
            })
            .catch(() => {
                toast.error(t("employees.alerts.error"))
            })
    }

    const getEmployeeShiftsForDate = (employeeId: number, date: Date) => {
        const dayShifts = shifts.filter(shift =>
            shift.date && isSameDay(shift.date, date)
        )

        const positions = dayShifts.flatMap(shift => {
            const shiftResources = resources[shift.index] || []
            const filtered = shiftResources.filter(position =>
                position.employeeId === employeeId && !position.deleted
            )
            return filtered
        })

        return positions
    }

    const getShiftNameById = (shiftId: number) => {
        const shift = shifts.find(s => s.id === shiftId)
        return shift?.name || t("shift.unnamedShift")
    }




    // Helper function to get hour and minutes from a time string or Date
    const getTimeComponents = (time: Date | string) => {
        const date = typeof time === 'string' ? new Date(time) : time
        return {
            hour: date.getHours(),
            minutes: date.getMinutes()
        }
    }

    // Calculate which hour column a shift starts in (0-23)
    const getStartHourColumn = (startTime: Date | string) => {
        const { hour } = getTimeComponents(startTime)
        return hour
    }

    // Calculate which hour column a shift ends in (0-23)
    const getEndHourColumn = (endTime: Date | string) => {
        const { hour } = getTimeComponents(endTime)
        return hour
    }

    // Calculate left offset within the start hour column (percentage 0-100)
    const getLeftOffsetPercentage = (startTime: Date | string) => {
        const { minutes } = getTimeComponents(startTime)
        return (minutes / 60) * 100
    }

    // Calculate right offset within the end hour column (percentage 0-100)
    const getRightOffsetPercentage = (endTime: Date | string) => {
        const { minutes } = getTimeComponents(endTime)
        return (minutes / 60) * 100
    }


    const [openAddShiftModal, setOpenAddShiftModal] = useState(false)
    const [openAddEventModal, setOpenAddEventModal] = useState(false)
    const [selectedEmployee, setSelectedEmployee] = useState<EmployeeEntity | null>(null)
    const [selectedDate, setSelectedDate] = useState<Date | null>(null)
    const [availableShifts, setAvailableShifts] = useState<ShiftDetailEntity[]>([])
    const [isLoadingShifts, setIsLoadingShifts] = useState(false)
    const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null)
    const [selectedWorkPositionId, setSelectedWorkPositionId] = useState<number | null>(null)
    const [openShiftDetailsModal, setOpenShiftDetailsModal] = useState(false)
    const [selectedShiftDetails, setSelectedShiftDetails] = useState<{
        shift: Position
        employee: EmployeeEntity
        date: Date
        shiftData: ShiftData
    } | null>(null)
    const [assignedShiftDetailIds, setAssignedShiftDetailIds] = useState<Set<number>>(new Set())

    const handleAddShift = (employee: EmployeeEntity, date: Date) => {
        // 1. Checks
        if (isDateReadOnly(date)) {
            toast.error(t("shift.cannotModifyPastDays") || "Cannot modify past days")
            return
        }

        // 2. Set state immediately so UI reacts instantly
        setSelectedEmployee(employee)
        setSelectedDate(date)
        setSelectedShiftId(null)
        setSelectedWorkPositionId(null)
        setOpenAddShiftModal(true) // <--- Open Modal NOW
        setIsLoadingShifts(true)   // <--- Show spinner inside modal

            // 3. Fetch data in background
            ; (async () => {
                try {
                    // Run requests in parallel
                    const [assignableResponse, templateResult] = await Promise.all([
                        shiftApi.findAssignableShiftDetailsForEmployee(
                            String(employee.id),
                            String(seasonId),
                            format(date, "yyyy-MM-dd"),
                            String(countAviability)
                        ),
                        fetchDayTemplate(date)
                    ])

                    // Update Template Data
                    if (templateResult) {
                        setDayTemplateData(templateResult.template)
                        setDayShiftsData(templateResult.shifts)
                    }

                    // Get shift IDs from the template for the selected department
                    const templateShiftIds = new Set<number>()
                    if (templateResult) {
                        templateResult.shifts.forEach(shift => {
                            templateShiftIds.add(shift.id)
                        })
                    }

                    // Filter and deduplicate shift details:
                    // 1. Only include shifts from the selected department (match shiftId with template)
                    // 2. Deduplicate by ID (same shift detail can appear multiple times if employee can work in multiple positions)
                    const uniqueShiftDetails = new Map<number, ShiftDetailEntity>()
                    assignableResponse.data.forEach(detail => {
                        // Only include if this shift detail belongs to a shift in the selected department's template
                        if (templateShiftIds.has(detail.shiftId) && !uniqueShiftDetails.has(detail.id)) {
                            uniqueShiftDetails.set(detail.id, detail)
                        }
                    })
                    setAvailableShifts(Array.from(uniqueShiftDetails.values()))
                } catch (error) {
                    console.error("Error fetching assignable shifts:", error)
                    toast.error(t("shift.errorFetchingShifts"))
                } finally {
                    setIsLoadingShifts(false) // Stop spinner
                }
            })()
    }

    // Helper function to parse time from shift detail or fallback to shift template
    const parseShiftTime = (shiftDetail: ShiftDetailEntity, date: Date, isStart: boolean): Date => {
        const timeValue = isStart ? shiftDetail.startTime : shiftDetail.endTime

        // If shift detail has its own time, use it
        if (timeValue) {
            const timeDate = new Date(date)
            let timeStr: string

            if (typeof timeValue === 'string') {
                timeStr = timeValue
            } else {
                // It's a Date object - use ignoreUTC to properly convert from UTC to local time
                const convertedDate = ignoreUTC(new Date(timeValue), true)
                const hours = convertedDate.getHours()
                const minutes = convertedDate.getMinutes()
                timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
            }

            const [hours, minutes] = timeStr.split(':').map(Number)
            timeDate.setHours(hours || 0, minutes || 0, 0, 0)
            return timeDate
        }

        // Fallback to shift template time
        const shift = shifts.find(s => s.id === shiftDetail.shiftId)
        if (shift) {
            const timeStr = isStart ? shift.startHour : shift.endHour
            if (timeStr) {
                const timeDate = new Date(date)
                const [hours, minutes] = timeStr.split(':').map(Number)
                timeDate.setHours(hours || 0, minutes || 0, 0, 0)
                return timeDate
            }
        }

        // Default fallback
        const timeDate = new Date(date)
        timeDate.setHours(isStart ? 0 : 23, isStart ? 0 : 59, 0, 0)
        return timeDate
    }

    // Check for overlaps between a new shift and existing shifts/calendar entries
    const checkForOverlaps = (shiftDetail: ShiftDetailEntity, employeeId: number, date: Date): {
        hasOverlap: boolean
        overlappingItem?: { startTime: Date; endTime: Date; name: string; type: 'shift' | 'calendar'; breakStart?: Date; breakEnd?: Date }
    } => {
        // Calculate new shift time range (with fallback to template times)
        const newStartTime = parseShiftTime(shiftDetail, date, true)
        let newEndTime = parseShiftTime(shiftDetail, date, false)

        // Handle end time on next day (e.g., night shift)
        if (newEndTime <= newStartTime) {
            newEndTime = new Date(newEndTime)
            newEndTime.setDate(newEndTime.getDate() + 1)
        }

        // Get existing shifts for this employee on this date
        const existingShifts = getShiftsFromCache(employeeId, date)

        // Check overlaps with existing shifts
        for (const existingShift of existingShifts) {
            const existingStart = new Date(existingShift.startTime)
            const existingEnd = new Date(existingShift.endTime)

            // Check if there's an overlap (not just touching)
            if (newStartTime < existingEnd && newEndTime > existingStart) {
                // Found overlap with existing shift
                const shiftName = existingShift.shiftData?.name || t("shift.unnamedShift")
                return {
                    hasOverlap: true,
                    overlappingItem: {
                        startTime: existingStart,
                        endTime: existingEnd,
                        name: shiftName,
                        type: 'shift',
                        breakStart: existingShift.breakStartTime ? new Date(existingShift.breakStartTime) : undefined,
                        breakEnd: existingShift.breakEndTime ? new Date(existingShift.breakEndTime) : undefined
                    }
                }
            }
        }

        // Get calendar entries for this employee on this date
        const calendarEntries = getCalendarFromCache(employeeId, date)

        // Check overlaps with calendar entries
        for (const entry of calendarEntries) {
            const entryStart = new Date(entry.startTime)
            const entryEnd = new Date(entry.endTime)

            // Check if there's an overlap
            if (newStartTime < entryEnd && newEndTime > entryStart) {
                // Found overlap with calendar entry
                const entryType = calendarEntryLabel(entry.entry)
                return {
                    hasOverlap: true,
                    overlappingItem: {
                        startTime: entryStart,
                        endTime: entryEnd,
                        name: entryType,
                        type: 'calendar'
                    }
                }
            }
        }

        return { hasOverlap: false }
    }

    // Check for overlaps between a new calendar event and existing shifts/calendar events
    const checkCalendarEventOverlaps = (
        startedOn: Date,
        endedOn: Date,
        allDay: boolean,
        startTime: string | undefined,
        endTime: string | undefined,
        employeeId: number
    ): {
        hasOverlap: boolean
        overlappingItem?: { startTime: Date; endTime: Date; name: string; type: 'shift' | 'calendar'; breakStart?: Date; breakEnd?: Date }
    } => {
        // Calculate new calendar event time range
        let newStartTime: Date
        let newEndTime: Date

        if (allDay) {
            // All-day event spans the entire day(s)
            newStartTime = new Date(startedOn)
            newStartTime.setHours(0, 0, 0, 0)
            newEndTime = new Date(endedOn)
            newEndTime.setHours(23, 59, 59, 999)
        } else {
            // Time-specific event
            newStartTime = new Date(startedOn)
            newEndTime = new Date(endedOn)

            if (startTime && endTime) {
                // Parse start and end times
                const [startHours, startMinutes] = startTime.split(':').map(Number)
                const [endHours, endMinutes] = endTime.split(':').map(Number)

                newStartTime.setHours(startHours || 0, startMinutes || 0, 0, 0)
                newEndTime.setHours(endHours || 0, endMinutes || 0, 0, 0)

                // Handle case where end time is on next day
                if (newEndTime <= newStartTime) {
                    newEndTime = addDays(newEndTime, 1)
                }
            } else {
                // Fallback: use full day
                newStartTime.setHours(0, 0, 0, 0)
                newEndTime.setHours(23, 59, 59, 999)
            }
        }

        // Check overlaps for each day the event spans
        const days = eachDayOfInterval({ start: startedOn, end: endedOn })

        for (const day of days) {
            // Get existing shifts for this employee on this day
            const existingShifts = getShiftsFromCache(employeeId, day)

            // Check overlaps with existing shifts
            for (const existingShift of existingShifts) {
                const existingStart = new Date(existingShift.startTime)
                const existingEnd = new Date(existingShift.endTime)

                // For all-day events, they overlap with any shift on that day
                // For time-specific events, check if times overlap
                const dayStart = new Date(day)
                dayStart.setHours(0, 0, 0, 0)
                const dayEnd = new Date(day)
                dayEnd.setHours(23, 59, 59, 999)

                let eventStartOnDay: Date
                let eventEndOnDay: Date

                if (allDay) {
                    eventStartOnDay = dayStart
                    eventEndOnDay = dayEnd
                } else {
                    eventStartOnDay = new Date(day)
                    eventStartOnDay.setHours(newStartTime.getHours(), newStartTime.getMinutes(), 0, 0)
                    eventEndOnDay = new Date(day)
                    eventEndOnDay.setHours(newEndTime.getHours(), newEndTime.getMinutes(), 0, 0)

                    // Handle event spanning multiple days
                    if (eventEndOnDay <= eventStartOnDay) {
                        eventEndOnDay = addDays(eventEndOnDay, 1)
                    }
                }

                // Check if there's an overlap (not just touching)
                if (eventStartOnDay < existingEnd && eventEndOnDay > existingStart) {
                    // Found overlap with existing shift
                    const shiftName = existingShift.shiftData?.name || t("shift.unnamedShift")
                    return {
                        hasOverlap: true,
                        overlappingItem: {
                            startTime: existingStart,
                            endTime: existingEnd,
                            name: shiftName,
                            type: 'shift',
                            breakStart: existingShift.breakStartTime ? new Date(existingShift.breakStartTime) : undefined,
                            breakEnd: existingShift.breakEndTime ? new Date(existingShift.breakEndTime) : undefined
                        }
                    }
                }
            }

            // Get existing calendar entries for this employee on this day
            const existingCalendarEntries = getCalendarFromCache(employeeId, day)

            // Check overlaps with existing calendar entries
            for (const existingEntry of existingCalendarEntries) {
                const existingStart = new Date(existingEntry.startTime)
                const existingEnd = new Date(existingEntry.endTime)

                // For all-day events: they cannot overlap with ANY calendar entry (all-day or time-specific) on the same day
                // For time-specific events: check if times overlap
                const dayStart = new Date(day)
                dayStart.setHours(0, 0, 0, 0)
                const dayEnd = new Date(day)
                dayEnd.setHours(23, 59, 59, 999)

                let eventStartOnDay: Date
                let eventEndOnDay: Date

                if (allDay) {
                    eventStartOnDay = dayStart
                    eventEndOnDay = dayEnd
                } else {
                    eventStartOnDay = new Date(day)
                    eventStartOnDay.setHours(newStartTime.getHours(), newStartTime.getMinutes(), 0, 0)
                    eventEndOnDay = new Date(day)
                    eventEndOnDay.setHours(newEndTime.getHours(), newEndTime.getMinutes(), 0, 0)

                    // Handle event spanning multiple days
                    if (eventEndOnDay <= eventStartOnDay) {
                        eventEndOnDay = addDays(eventEndOnDay, 1)
                    }
                }

                // Check if existing entry is all-day
                // An entry is all-day if startTime and endTime are null, or if it spans the entire day
                const existingIsAllDay = (existingEntry.entry.startTime === null && existingEntry.entry.endTime === null) ||
                    (existingStart.getHours() === 0 && existingStart.getMinutes() === 0 &&
                        existingEnd.getHours() === 23 && existingEnd.getMinutes() === 59)

                // If either event is all-day, they overlap if on the same day
                // All-day events cannot overlap with ANY calendar entry (all-day or time-specific) on the same day
                if (allDay || existingIsAllDay) {
                    // All-day events cannot overlap with any calendar entry on the same day
                    if (eventStartOnDay < existingEnd && eventEndOnDay > existingStart) {
                        const entryType = calendarEntryLabel(existingEntry.entry)
                        return {
                            hasOverlap: true,
                            overlappingItem: {
                                startTime: existingStart,
                                endTime: existingEnd,
                                name: entryType,
                                type: 'calendar'
                            }
                        }
                    }
                } else {
                    // Both are time-specific: check if times overlap
                    if (eventStartOnDay < existingEnd && eventEndOnDay > existingStart) {
                        const entryType = calendarEntryLabel(existingEntry.entry)
                        return {
                            hasOverlap: true,
                            overlappingItem: {
                                startTime: existingStart,
                                endTime: existingEnd,
                                name: entryType,
                                type: 'calendar'
                            }
                        }
                    }
                }
            }
        }

        return { hasOverlap: false }
    }

    // Handler to create calendar event (extracted for reuse)
    const handleCreateCalendarEvent = async (values: AddWorkFormData, skipOverlapCheck: boolean = false) => {
        if (!selectedEmployee || !selectedDate) return

        // Check if date is read-only (past days for DEPCHIEF)
        if (isDateReadOnly(selectedDate)) {
            toast.error(t("shift.cannotModifyPastDays") || "Cannot modify past days")
            return
        }

        // Check for overlaps (unless user confirmed to proceed)
        if (!skipOverlapCheck && !skipOverlapCheckRef.current) {
            const overlapCheck = checkCalendarEventOverlaps(
                values.startedOn,
                values.endedOn,
                values.allDay || false,
                values.startTime,
                values.endTime,
                selectedEmployee.id
            )

            if (overlapCheck.hasOverlap && overlapCheck.overlappingItem) {
                // If this is a calendar-to-calendar overlap, it's not allowed at all
                if (overlapCheck.overlappingItem.type === 'calendar') {
                    // Calculate new calendar event times for display
                    let newStartTime: Date
                    let newEndTime: Date

                    if (values.allDay) {
                        newStartTime = new Date(values.startedOn)
                        newStartTime.setHours(0, 0, 0, 0)
                        newEndTime = new Date(values.endedOn)
                        newEndTime.setHours(23, 59, 59, 999)
                    } else {
                        newStartTime = new Date(values.startedOn)
                        newEndTime = new Date(values.endedOn)

                        if (values.startTime && values.endTime) {
                            const [startHours, startMinutes] = values.startTime.split(':').map(Number)
                            const [endHours, endMinutes] = values.endTime.split(':').map(Number)

                            newStartTime.setHours(startHours || 0, startMinutes || 0, 0, 0)
                            newEndTime.setHours(endHours || 0, endMinutes || 0, 0, 0)

                            if (newEndTime <= newStartTime) {
                                newEndTime = addDays(newEndTime, 1)
                            }
                        } else {
                            newStartTime.setHours(0, 0, 0, 0)
                            newEndTime.setHours(23, 59, 59, 999)
                        }
                    }

                    const eventType = values.type === CalendarEntryEntityTypeEnum.Custom || (typeof values.type === 'string' && values.type.startsWith('custom-'))
                        ? (values.customTypeId ? t(`employees.calendar.types.custom`) : t(`employees.calendar.types.${values.type.toLowerCase()}`))
                        : t(`employees.calendar.types.${values.type.toLowerCase()}`) || values.type

                    // Show overlap modal (without proceed button)
                    const { startedOn, endedOn, type, customTypeId, allDay, startTime, endTime, ...rest } = values
                    setOverlapData({
                        newShift: {
                            startTime: newStartTime,
                            endTime: newEndTime,
                            name: eventType
                        },
                        existingItem: overlapCheck.overlappingItem,
                        calendarEventData: {
                            startedOn,
                            endedOn,
                            allDay: allDay || false,
                            startTime,
                            endTime,
                            type: values.type,
                            customTypeId,
                            rest
                        }
                    })
                    setOpenOverlapModal(true)
                    return
                } else {
                    // This is a calendar-to-shift overlap, which can be allowed with confirmation
                    // Calculate new calendar event times for display
                    let newStartTime: Date
                    let newEndTime: Date

                    if (values.allDay) {
                        newStartTime = new Date(values.startedOn)
                        newStartTime.setHours(0, 0, 0, 0)
                        newEndTime = new Date(values.endedOn)
                        newEndTime.setHours(23, 59, 59, 999)
                    } else {
                        newStartTime = new Date(values.startedOn)
                        newEndTime = new Date(values.endedOn)

                        if (values.startTime && values.endTime) {
                            const [startHours, startMinutes] = values.startTime.split(':').map(Number)
                            const [endHours, endMinutes] = values.endTime.split(':').map(Number)

                            newStartTime.setHours(startHours || 0, startMinutes || 0, 0, 0)
                            newEndTime.setHours(endHours || 0, endMinutes || 0, 0, 0)

                            if (newEndTime <= newStartTime) {
                                newEndTime = addDays(newEndTime, 1)
                            }
                        } else {
                            newStartTime.setHours(0, 0, 0, 0)
                            newEndTime.setHours(23, 59, 59, 999)
                        }
                    }

                    const eventType = values.type === CalendarEntryEntityTypeEnum.Custom || (typeof values.type === 'string' && values.type.startsWith('custom-'))
                        ? (values.customTypeId ? t(`employees.calendar.types.custom`) : t(`employees.calendar.types.${values.type.toLowerCase()}`))
                        : t(`employees.calendar.types.${values.type.toLowerCase()}`) || values.type

                    // Show overlap modal (with proceed button for shift overlaps)
                    const { startedOn, endedOn, type, customTypeId, allDay, startTime, endTime, ...rest } = values
                    setOverlapData({
                        newShift: {
                            startTime: newStartTime,
                            endTime: newEndTime,
                            name: eventType
                        },
                        existingItem: overlapCheck.overlappingItem,
                        calendarEventData: {
                            startedOn,
                            endedOn,
                            allDay: allDay || false,
                            startTime,
                            endTime,
                            type: values.type,
                            customTypeId,
                            rest
                        }
                    })
                    setOpenOverlapModal(true)
                    return
                }
            }
        }

        try {
            const { startedOn, endedOn, type, customTypeId, allDay, startTime, endTime, ...rest } = values

            // Validate that startedOn and endedOn are provided
            if (!startedOn || !endedOn) {
                toast.error(t("shift.invalidDateRange") || "Invalid date range")
                return
            }

            // Convert custom type string back to proper enum value for backend
            const backendType = type === CalendarEntryEntityTypeEnum.Custom || (typeof type === 'string' && type.startsWith('custom-'))
                ? CalendarEntryEntityTypeEnum.Custom
                : type as CalendarEntryEntityTypeEnum

            // Convert to Date objects if they're not already
            const startDate = startedOn instanceof Date ? new Date(startedOn) : new Date(startedOn)
            const endDate = endedOn instanceof Date ? new Date(endedOn) : new Date(endedOn)

            // Validate dates are valid
            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                toast.error(t("shift.invalidDateRange") || "Invalid date range")
                return
            }

            // Format dates to avoid timezone issues
            // Create dates at local midnight, then format as ISO 8601 string
            const formatDateForBackend = (date: Date): string => {
                // Create a new date object to avoid mutating the original
                const localDate = new Date(date)
                // Set to local midnight
                localDate.setHours(0, 0, 0, 0)
                // Get the date components in local timezone
                const year = localDate.getFullYear()
                const month = String(localDate.getMonth() + 1).padStart(2, '0')
                const day = String(localDate.getDate()).padStart(2, '0')
                // Return ISO 8601 format string at midnight UTC
                return `${year}-${month}-${day}T00:00:00.000Z`
            }

            // Format as ISO 8601 date strings at midnight UTC
            const startedOnISO = formatDateForBackend(startDate)
            const endedOnISO = formatDateForBackend(endDate)

            // Validate the format is correct
            if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(startedOnISO) ||
                !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(endedOnISO)) {
                console.error('Invalid ISO date format:', { startedOnISO, endedOnISO })
                toast.error(t("shift.invalidDateRange") || "Invalid date format")
                return
            }

            // Explicitly construct request data to avoid any interference from rest object
            // Only include fields that the backend expects
            // Ensure dates are explicitly strings (not Date objects)
            const requestData: {
                type: CalendarEntryEntityTypeEnum
                startedOn: string
                endedOn: string
                customTypeId?: number
                managerNotes?: string
                publicNotes?: string
                startTime?: string
                endTime?: string
                absenceTypeId?: number
                certificateStatus?: string
                protocolNumber?: string
                certificateStartedOn?: string
                certificateEndedOn?: string
            } = {
                type: backendType,
                startedOn: String(startedOnISO),
                endedOn: String(endedOnISO),
            }

            // Only include customTypeId if it's defined
            if (customTypeId !== undefined && customTypeId !== null) {
                requestData.customTypeId = customTypeId
            }

            // Forward the absence causale (AbsenceType) + certificate fields. The backend
            // mirrors the causale's legacyType into `type`, so the entry keeps its real causale
            // instead of falling back to the legacy ILL/VACATION mapping.
            const absRest = rest as any
            if (absRest.absenceTypeId !== undefined && absRest.absenceTypeId !== null) {
                requestData.absenceTypeId = absRest.absenceTypeId
            }
            if (absRest.certificateStatus) {
                requestData.certificateStatus = absRest.certificateStatus
            }
            if (absRest.protocolNumber) {
                requestData.protocolNumber = absRest.protocolNumber
            }
            if (absRest.certificateStartedOn) {
                requestData.certificateStartedOn = absRest.certificateStartedOn instanceof Date
                    ? absRest.certificateStartedOn.toISOString() : absRest.certificateStartedOn
            }
            if (absRest.certificateEndedOn) {
                requestData.certificateEndedOn = absRest.certificateEndedOn instanceof Date
                    ? absRest.certificateEndedOn.toISOString() : absRest.certificateEndedOn
            }

            // Only include notes if they have values (convert empty strings to undefined)
            if (rest.managerNotes && rest.managerNotes.trim() !== '') {
                requestData.managerNotes = rest.managerNotes
            }
            if (rest.publicNotes && rest.publicNotes.trim() !== '') {
                requestData.publicNotes = rest.publicNotes
            }

            // Only include times if not all-day
            if (!allDay) {
                if (startTime) {
                    requestData.startTime = startTime
                }
                if (endTime) {
                    requestData.endTime = endTime
                }
            }


            await calendarEntriesApi.create(
                String(selectedEmployee.id),
                requestData,
                seasonId
            )

            toast.success(t("shift.calendarEventCreatedSuccessfully"))
            setOpenAddEventModal(false)
            setOpenOverlapModal(false)
            setOverlapData(null)

            // Refresh calendar entries
            if (selectedDate) {
                await fetchCalendarEntries()
            }

            // Refresh worked hours and update score
            await refreshHoursAndScore()
        } catch (error: any) {
            console.error("Error creating calendar event:", error)
            if (error?.response?.status === 403 && error?.response?.data?.message?.includes('DEPCHIEF users cannot')) {
                toast.error(t('employees.calendar.entry.cannotModifyPast'))
            } else {
                toast.error(error?.response?.data?.message || t("shift.errorCreatingCalendarEvent"))
            }
        }
    }

    // Quick action handler for creating full-day calendar events
    const handleQuickCalendarEvent = async (eventType: CalendarEntryEntityTypeEnum) => {
        if (!selectedEmployee || !selectedDate) return

        // Check if date is read-only (past days for DEPCHIEF)
        if (isDateReadOnly(selectedDate)) {
            toast.error(t("shift.cannotModifyPastDays") || "Cannot modify past days")
            return
        }

        // Check for calendar-to-calendar overlaps (these can't be overridden)
        const eventDate = new Date(selectedDate)
        eventDate.setHours(0, 0, 0, 0)
        const overlapCheck = checkCalendarEventOverlaps(
            eventDate,
            eventDate,
            true, // allDay
            undefined, // startTime
            undefined, // endTime
            selectedEmployee.id
        )

        if (overlapCheck.hasOverlap && overlapCheck.overlappingItem?.type === 'calendar') {
            const eventTypeName = eventType === CalendarEntryEntityTypeEnum.Vacation
                ? t("employees.calendar.types.vacation") || "Ferie"
                : t("employees.calendar.types.day_off") || "Giorno Libero"
            toast.error(
                t("shift.calendarEventOverlap") ||
                `Cannot create ${eventTypeName}: there is already a calendar event on this day. Please use the full calendar event modal to manage overlaps.`
            )
            return
        }

        try {
            // Format dates to avoid timezone issues (same logic as handleCreateCalendarEvent)
            const formatDateForBackend = (date: Date): string => {
                const localDate = new Date(date)
                localDate.setHours(0, 0, 0, 0)
                const year = localDate.getFullYear()
                const month = String(localDate.getMonth() + 1).padStart(2, '0')
                const day = String(localDate.getDate()).padStart(2, '0')
                return `${year}-${month}-${day}T00:00:00.000Z`
            }

            const startedOnISO = formatDateForBackend(eventDate)
            const endedOnISO = formatDateForBackend(eventDate)

            const requestData: {
                type: CalendarEntryEntityTypeEnum
                startedOn: string
                endedOn: string
            } = {
                type: eventType,
                startedOn: startedOnISO,
                endedOn: endedOnISO,
            }

            await calendarEntriesApi.create(
                String(selectedEmployee.id),
                requestData,
                seasonId
            )

            toast.success(t("shift.calendarEventCreatedSuccessfully") || "Calendar event created successfully")

            // Close the modal
            setOpenAddShiftModal(false)

            // Refresh calendar entries
            if (selectedDate) {
                await fetchCalendarEntries()
            }

            // Refresh worked hours and update score
            await refreshHoursAndScore()
        } catch (error: any) {
            console.error("Error creating quick calendar event:", error)
            if (error?.response?.status === 403 && error?.response?.data?.message?.includes('DEPCHIEF users cannot')) {
                toast.error(t('employees.calendar.entry.cannotModifyPast') || "Cannot modify past days")
            } else {
                toast.error(error?.response?.data?.message || t("shift.errorCreatingCalendarEvent") || "Error creating calendar event")
            }
        }
    }

    const handleAssignShift = async (shiftDetailId: number, skipOverlapCheck: boolean = false) => {
        if (!selectedEmployee || !selectedDate) return

        // Check if date is read-only (past days for DEPCHIEF)
        if (isDateReadOnly(selectedDate)) {
            toast.error(t("shift.cannotModifyPastDays") || "Cannot modify past days")
            return
        }

        // Find the shift detail being assigned
        const shiftDetail = availableShifts.find(sd => sd.id === shiftDetailId)
        if (!shiftDetail) {
            toast.error(t("shift.errorFetchingShifts"))
            return
        }

        // Check for overlaps (unless user confirmed to proceed)
        if (!skipOverlapCheck) {
            const overlapCheck = checkForOverlaps(shiftDetail, selectedEmployee.id, selectedDate)

            if (overlapCheck.hasOverlap && overlapCheck.overlappingItem) {
                // Calculate new shift times for display (using same parsing logic)
                const newStartTime = parseShiftTime(shiftDetail, selectedDate, true)
                let newEndTime = parseShiftTime(shiftDetail, selectedDate, false)

                if (newEndTime <= newStartTime) {
                    newEndTime = new Date(newEndTime)
                    newEndTime.setDate(newEndTime.getDate() + 1)
                }

                const shiftName = getShiftNameById(shiftDetail.shiftId) || t("shift.unnamedShift")

                // Show overlap modal
                setOverlapData({
                    newShift: {
                        startTime: newStartTime,
                        endTime: newEndTime,
                        name: shiftName,
                        breakStart: shiftDetail.breakStart ? new Date(shiftDetail.breakStart) : undefined,
                        breakEnd: shiftDetail.breakEnd ? new Date(shiftDetail.breakEnd) : undefined
                    },
                    existingItem: overlapCheck.overlappingItem,
                    shiftDetailId
                })
                setOpenOverlapModal(true)
                return
            }
        }

        // No overlap or user confirmed - proceed with assignment
        try {
            // Format date as YYYY-MM-DD and append midnight UTC to ensure correct calendar day
            const dateString = format(selectedDate, "yyyy-MM-dd")
            const dateISO = `${dateString}T00:00:00.000Z`

            // 1. Perform API Call
            await shiftApi.assignEmployee({
                date: dateISO,
                employeeId: selectedEmployee.id,
                seasonId,
                shiftDetailId
            })

            toast.success(t("shift.shiftAssignedSuccessfully"))

            // 2. Close UI immediately
            setOpenAddShiftModal(false)
            setSelectedShiftId(null)
            setSelectedWorkPositionId(null)
            setOpenOverlapModal(false)
            setOverlapData(null)

            // 3. OPTIMISTIC UPDATE (The Magic Performance Fix)
            // Instead of refetching everything, we manually insert the record into our local state.

            // A. Find the shift definition to get visual data (start/end times)
            const assignedDetail = availableShifts.find(s => s.id === shiftDetailId)
            const parentShift = dayShiftsData.find(s => s.id === assignedDetail?.shiftId)

            if (assignedDetail && parentShift) {
                // Create the new position object locally
                const newPosition: Position = {
                    id: shiftDetailId, // Or generate a temp ID
                    workPositionId: assignedDetail.workPositionId,
                    employeeId: selectedEmployee.id,
                    firstName: selectedEmployee.firstName,
                    lastName: selectedEmployee.lastName,
                    date: selectedDate,
                    // Use the helper function you already wrote to parse times
                    start: parseShiftTime(assignedDetail, selectedDate, true),
                    end: parseShiftTime(assignedDetail, selectedDate, false),
                    breakStartTime: assignedDetail.breakStart ? ignoreUTC(new Date(assignedDetail.breakStart), true) : undefined,
                    breakEndTime: assignedDetail.breakEnd ? ignoreUTC(new Date(assignedDetail.breakEnd), true) : undefined,
                    additionalBreaks: (assignedDetail as any).additionalBreaks?.map((ab: any) => ({
                        startTime: ignoreUTC(new Date(ab.startTime), true),
                        endTime: ignoreUTC(new Date(ab.endTime), true)
                    })),
                    leftPadding: 0,  // Will be calculated by memo
                    rightPadding: 0, // Will be calculated by memo
                    published: false,
                    deleted: false
                }

                // Update Resources State (triggering the shiftsCache useMemo)
                setResources(prev => {
                    // Find the shift index in your main 'shifts' array
                    const shiftIndex = shifts.findIndex(s => s.id === parentShift.id && s.date && isSameDay(s.date, selectedDate))

                    // If we found the shift index, append to it
                    if (shiftIndex > -1) {
                        const existing = prev[shiftIndex] || []
                        return {
                            ...prev,
                            [shiftIndex]: [...existing, newPosition]
                        }
                    }
                    return prev
                })

                // Update Worked Hours State locally
                setWorkedHours(prev => {
                    // Calculate duration
                    if (!newPosition.start || !newPosition.end) return prev
                    const start = newPosition.start.getTime()
                    let end = newPosition.end.getTime()
                    if (end < start) end += 24 * 60 * 60 * 1000 // Night shift

                    let duration = (end - start) / (1000 * 60 * 60) // hours

                    // Subtract break (main break + additional breaks)
                    if (newPosition.breakStartTime && newPosition.breakEndTime) {
                        const bStart = newPosition.breakStartTime.getTime()
                        const bEnd = newPosition.breakEndTime.getTime()
                        const breakDur = (bEnd - bStart) / (1000 * 60 * 60)
                        duration -= breakDur
                    }
                    // Subtract additional breaks
                    if (newPosition.additionalBreaks && newPosition.additionalBreaks.length > 0) {
                        newPosition.additionalBreaks.forEach(ab => {
                            const abStart = ab.startTime.getTime()
                            const abEnd = ab.endTime.getTime()
                            const abDur = (abEnd - abStart) / (1000 * 60 * 60)
                            duration -= abDur
                        })
                    }

                    return {
                        ...prev,
                        [selectedEmployee.id]: (prev[selectedEmployee.id] || 0) + (Math.round(duration * 10) / 10)
                    }
                })
            }

            // 4. Mark as assigned in the specific modal set (prevents double booking in this session)
            setAssignedShiftDetailIds(prev => {
                const newSet = new Set(prev)
                newSet.add(shiftDetailId)
                return newSet
            })

            // 5. Background Sync (Optional but recommended to ensure consistency)
            // Do this silently without blocking UI
            fetchData()
        } catch (error) {
            console.error("Error assigning shift:", error)
            toast.error(t("shift.errorAssigningShift"))
            // If optimistic update failed, you might want to trigger a fetchData here to reset state
            fetchData()
        }
    }

    // Helper functions to group shift details
    const getAvailableShiftsGrouped = () => {
        // Filter out already-assigned shift details
        const unassignedShifts = availableShifts.filter(detail => !assignedShiftDetailIds.has(detail.id))

        // Group by shift name instead of shiftId to avoid duplicates
        // Multiple shifts can have the same name but different IDs
        const groupedByName = new Map<string, { shiftIds: Set<number>, details: ShiftDetailEntity[], assignedCount: number }>()

        unassignedShifts.forEach(detail => {
            const shiftId = detail.shiftId
            const shiftName = getShiftNameById(shiftId)

            if (!groupedByName.has(shiftName)) {
                groupedByName.set(shiftName, { shiftIds: new Set(), details: [], assignedCount: 0 })
            }
            const group = groupedByName.get(shiftName)
            if (group) {
                group.shiftIds.add(shiftId)
                group.details.push(detail)
            }
        })

        return Array.from(groupedByName.entries()).map(([shiftName, group]) => {
            // Count only shift details that the employee CAN be assigned to (from availableShifts)
            // This filters out positions the employee cannot work in
            const allAssignableDetails = availableShifts.filter(detail => {
                const shiftId = detail.shiftId
                return group.shiftIds.has(shiftId)
            })

            // Total count is the number of assignable shift details (positions the employee can work)
            const totalCount = allAssignableDetails.length

            // Count how many of those assignable positions are already assigned
            let assignedCount = 0
            if (dayShiftsData && selectedDate) {
                const dayDateString = format(selectedDate, "yyyy-MM-dd")
                allAssignableDetails.forEach(detail => {
                    // Find the shift detail in template data to check assignments
                    const shiftsWithName = dayShiftsData.filter(shift => shift.name === shiftName)
                    for (const shift of shiftsWithName) {
                        const sd = shift.shiftDetails?.find(sd => sd.id === detail.id)
                        if (sd) {
                            const hasAssignment = sd.shiftAssignments?.some(assignment => {
                                const assignmentDateStr = format(new Date(assignment.date), "yyyy-MM-dd")
                                return assignmentDateStr === dayDateString && !assignment.deleted
                            })
                            if (hasAssignment) {
                                assignedCount++
                                break
                            }
                        }
                    }
                })
            } else {
                // Fallback: use assignedShiftDetailIds
                allAssignableDetails.forEach(detail => {
                    if (assignedShiftDetailIds.has(detail.id)) {
                        assignedCount++
                    }
                })
            }

            // Unassigned count is total assignable minus assigned
            const unassignedCount = Math.max(0, totalCount - assignedCount)

            return {
                shiftIds: Array.from(group.shiftIds),
                shiftId: Array.from(group.shiftIds)[0], // Use first shiftId as primary for compatibility
                shiftName,
                details: group.details,
                unassignedCount, // Count of unassigned positions (only assignable ones)
                assignedCount, // Count of already assigned positions (only assignable ones)
                totalCount // Total assignable positions for this employee
            }
        })
    }

    const getWorkPositionsForShift = (shiftId: number) => {
        // Find all shiftIds that have the same name (to handle duplicates)
        const shiftGroup = getAvailableShiftsGrouped().find(g => g.shiftIds.includes(shiftId))

        // Get all shift details for all shifts with this name, filtering out already-assigned ones
        const unassignedDetails = shiftGroup
            ? availableShifts.filter(d => shiftGroup.shiftIds.includes(d.shiftId) && !assignedShiftDetailIds.has(d.id))
            : availableShifts.filter(d => d.shiftId === shiftId && !assignedShiftDetailIds.has(d.id))

        const workPositionsMap = new Map<number, { details: ShiftDetailEntity[], assignedCount: number, totalCount: number }>()

        unassignedDetails.forEach(detail => {
            const workPosId = detail.workPositionId
            if (!workPositionsMap.has(workPosId)) {
                workPositionsMap.set(workPosId, { details: [], assignedCount: 0, totalCount: 0 })
            }
            const existing = workPositionsMap.get(workPosId)
            if (existing) {
                existing.details.push(detail)
            }
        })

        // Calculate total and assigned counts for each work position
        return Array.from(workPositionsMap.entries()).map(([workPosId, data]) => {
            // Count total shift details for this work position from template
            let totalCount = 0
            let assignedCount = 0

            if (dayShiftsData && shiftGroup) {
                dayShiftsData.forEach(shift => {
                    if (shiftGroup.shiftIds.includes(shift.id)) {
                        shift.shiftDetails?.forEach(sd => {
                            if (sd.workPositionId === workPosId) {
                                totalCount++
                                if (assignedShiftDetailIds.has(sd.id)) {
                                    assignedCount++
                                }
                            }
                        })
                    }
                })
            }

            return {
                workPositionId: workPosId,
                workPosition: data.details[0]?.workPosition,
                details: data.details,
                unassignedCount: data.details.length,
                assignedCount,
                totalCount: totalCount || data.details.length
            }
        })
    }

    const getUniqueShiftDetailsForWorkPosition = (shiftId: number, workPositionId: number) => {
        // Find all shiftIds that have the same name as this shiftId
        const shiftGroup = getAvailableShiftsGrouped().find(g => g.shiftIds.includes(shiftId))

        // Get all shift details for all shifts with this name and work position, filtering out already-assigned ones
        const unassignedDetails = shiftGroup
            ? availableShifts.filter(d => shiftGroup.shiftIds.includes(d.shiftId) && d.workPositionId === workPositionId && !assignedShiftDetailIds.has(d.id))
            : availableShifts.filter(d => d.shiftId === shiftId && d.workPositionId === workPositionId && !assignedShiftDetailIds.has(d.id))

        // Group by unique time combination
        const timeGroups = new Map<string, ShiftDetailEntity[]>()
        unassignedDetails.forEach(detail => {
            // Normalize null/undefined/empty to empty string for comparison
            const startTime = detail.startTime || ''
            const endTime = detail.endTime || ''
            const breakStart = detail.breakStart || ''
            const breakEnd = detail.breakEnd || ''
            const timeKey = `${startTime}-${endTime}-${breakStart}-${breakEnd}`
            if (!timeGroups.has(timeKey)) {
                timeGroups.set(timeKey, [])
            }
            const existing = timeGroups.get(timeKey)
            if (existing) {
                existing.push(detail)
            }
        })

        return Array.from(timeGroups.values()).map(group => ({
            firstDetail: group[0],
            allDetails: group,
            isUnique: group.length === 1
        }))
    }

    const fetchDayTemplate = async (day: Date) => {
        if (!selectedDepartment) return null

        try {
            setIsLoadingDayTemplate(true)
            // Format date as YYYY-MM-DD to ensure correct calendar day (avoid timezone issues)
            const dateString = format(day, "yyyy-MM-dd")
            const dateISO = `${dateString}T00:00:00.000Z`

            const response = await calendarEntriesShiftTemplateApi.findByDate(
                dateISO,
                selectedDepartment.id,
                seasonId
            )

            const calendarEntry = response.data
            const template = calendarEntry?.shiftTemplate

            if (!template) {
                toast.error(t("shift.noTemplateForDay"))
                return null
            }

            // Use shifts from the template response (they include shiftAssignments)
            // instead of fetching again via findByTemplate which might not include assignments
            const shifts = template.shifts || []

            // Build set of assigned shift detail IDs for this date
            const assignedIds = new Set<number>()

            // Use the same date format for comparison (avoid timezone issues)
            const dayDateString = format(day, "yyyy-MM-dd")

            shifts.forEach(shift => {
                shift.shiftDetails?.forEach(shiftDetail => {
                    // Check if this shift detail has assignments for the selected date
                    const hasAssignment = shiftDetail.shiftAssignments?.some(assignment => {
                        const assignmentDateStr = format(new Date(assignment.date), "yyyy-MM-dd")
                        return assignmentDateStr === dayDateString && !assignment.deleted
                    })

                    if (hasAssignment) {
                        assignedIds.add(shiftDetail.id)
                    }
                })
            })

            setAssignedShiftDetailIds(assignedIds)

            return { template, shifts }
        } catch (error) {
            console.error('Error fetching day template:', error)
            toast.error(t("shift.errorFetchingShifts"))
            return null
        } finally {
            setIsLoadingDayTemplate(false)
        }
    }

    const handleEditDay = async (day: Date) => {
        // Check if date is read-only (past days for DEPCHIEF)
        if (isDateReadOnly(day)) {
            toast.error(t("shift.cannotModifyPastDays") || "Cannot modify past days")
            return
        }

        setSelectedDayForEdit(day)
        setOpenDayEditModal(true)

        const result = await fetchDayTemplate(day)
        if (result) {
            setDayTemplateData(result.template)
            setDayShiftsData(result.shifts)
        } else {
            setOpenDayEditModal(false)
        }
    }

    const handleEditShiftFromDay = async (shift: ShiftEntity) => {
        if (!selectedDayForEdit || !dayTemplateData) return

        // Set the shift data with the date
        const shiftWithDate: ShiftData = {
            id: shift.id,
            index: 0, // Will be set correctly by the system
            name: shift.name,
            width: 0, // Not needed for editing
            startHour: shift.startTime ? new Date(shift.startTime).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) : "",
            endHour: shift.endTime ? new Date(shift.endTime).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) : "",
            date: selectedDayForEdit,
            notes: shift.notes || undefined
        }

        setSelectedShift(shiftWithDate)
        setShiftTemplateData(dayTemplateData)
        setShiftModalData(shift)
        setIsEditShiftModalOpen(true)
        setOpenDayEditModal(false) // Close day modal when opening shift edit
    }

    const handleAddNewShift = () => {
        if (!selectedDayForEdit || !dayTemplateData) return

        // Open AddShiftModal in create mode (no data, just template and date)
        setSelectedShift(undefined)
        setShiftTemplateData(dayTemplateData)
        setShiftModalData(undefined)
        setIsEditShiftModalOpen(true)
        setOpenDayEditModal(false) // Close day modal when opening shift create
    }

    const handleRefreshDayShifts = async () => {
        if (!selectedDayForEdit) return

        const result = await fetchDayTemplate(selectedDayForEdit)
        if (result) {
            setDayTemplateData(result.template)
            setDayShiftsData(result.shifts)
        }
    }

    const handleDeleteShift = async () => {
        if (isViewerOnly) return
        if (!shiftToDelete || !selectedDepartment) return

        try {
            await shiftApi.remove(shiftToDelete.id, selectedDepartment.id, seasonId)
            toast.success(t("shift.shiftDeletedSuccessfully") || "Shift deleted successfully")

            // Refresh the day template to update the shifts list
            await handleRefreshDayShifts()

            // Also refresh the main data
            await fetchData()

            // Close the dialog
            setOpenDeleteShiftDialog(false)
            setShiftToDelete(null)
        } catch (error: any) {
            console.error("Error deleting shift:", error)
            toast.error(error?.response?.data?.message || t("shift.errorDeletingShift") || "Error deleting shift")
        }
    }

    const handleOpenDeleteShiftDialog = (shift: ShiftEntity, event: React.MouseEvent) => {
        event.stopPropagation() // Prevent triggering the card's onClick
        setShiftToDelete(shift)
        setOpenDeleteShiftDialog(true)
    }

    const handleShiftClick = (shift: Position, employee: EmployeeEntity, date: Date) => {
        // Find the shift that contains this position in its resources
        const shiftData = shifts.find(s => {
            if (!s.date) return false
            const shiftResources = resources[s.index] || []
            return shiftResources.some(p => p.id === shift.id && isSameDay(s.date as Date, date))
        })
        if (shiftData) {
            setSelectedShiftDetails({
                shift,
                employee,
                date,
                shiftData
            })
            setOpenShiftDetailsModal(true)
        }
    }

    // 3. OPTIMIZATION: Pre-calculate Column Layouts and Counts for every day
    // This prevents O(Days * Employees) iterations during every render frame.
    const dailyDataCache = useMemo(() => {
        const layoutMap: Record<string, any> = {}
        const countsMap: Record<string, Map<string, number>> = {}

        weekDays.forEach(day => {
            const dayDateString = format(day, "yyyy-MM-dd")
            const hoursWithContent = new Set<number>()

            // Initialize counts for each hour and half-hour
            const counts = new Map<string, number>()
            for (let i = 0; i < 24; i++) {
                counts.set(`${i}:00`, 0)
                counts.set(`${i}:30`, 0)
            }

            // Iterate employees ONCE per day to determine:
            // 1. Which hours are occupied (for collapsing columns)
            // 2. How many people are working (for the count row)
            employees.forEach(employee => {
                const empShifts = getShiftsFromCache(employee.id, day)
                const empEntries = getCalendarFromCache(employee.id, day)

                // Check previous day night shifts
                const prevDay = addDays(day, -1)
                const prevDayShifts = getShiftsFromCache(employee.id, prevDay)
                const nightShifts = prevDayShifts.filter(s => s.isNightShift && s.endHourNextDay !== null)

                // MARK OCCUPIED HOURS & COUNT EMPLOYEES
                const markHour = (h: number) => {
                    hoursWithContent.add(h)
                    // Simple increment for full hour presence
                    counts.set(`${h}:00`, (counts.get(`${h}:00`) || 0) + 1)
                    counts.set(`${h}:30`, (counts.get(`${h}:30`) || 0) + 1)
                }

                empShifts.forEach(s => {
                    for (let h = s.startHour; h <= s.endHour; h++) markHour(h)
                })

                nightShifts.forEach(s => {
                    if (s.endHourNextDay !== null) {
                        for (let h = 0; h <= s.endHourNextDay; h++) markHour(h)
                    }
                })

                empEntries.forEach(e => {
                    // FIX 1: SKIP marking hours for full-day events.
                    // This ensures the timeline stays COLLAPSED (narrow) if it's just a vacation/day off.
                    const isFullDay = (e.startHour === 0 && e.endHour === 23) || (e.entry.startTime === null && e.entry.endTime === null)

                    if (isFullDay) return

                    for (let h = e.startHour; h <= e.endHour; h++) markHour(h)
                })
            })

            // --- CALCULATE COLLAPSED RANGES (Moved from JSX) ---
            const collapsedRanges: Array<{ startHour: number; endHour: number }> = []
            let rangeStart: number | null = null

            for (let hour = 0; hour <= 23; hour++) {
                if (!hoursWithContent.has(hour)) {
                    if (rangeStart === null) rangeStart = hour
                } else {
                    if (rangeStart !== null) {
                        const rangeLength = hour - rangeStart
                        if (rangeLength > 4) {
                            collapsedRanges.push({ startHour: rangeStart, endHour: hour - 1 })
                        }
                        rangeStart = null
                    }
                }
            }

            if (rangeStart !== null) {
                const rangeLength = 24 - rangeStart
                if (rangeLength > 4) {
                    collapsedRanges.push({ startHour: rangeStart, endHour: 23 })
                }
            }

            // --- BUILD RENDER COLUMNS (Moved from JSX) ---
            const columns: Array<{ type: 'hour' | 'collapsed'; hour?: number; range?: { startHour: number; endHour: number }; width: number }> = []

            for (let hour = 0; hour <= 23; hour++) {
                const collapsedRange = collapsedRanges.find(r => hour >= r.startHour && hour <= r.endHour)

                if (collapsedRange && collapsedRange.startHour === hour) {
                    columns.push({
                        type: 'collapsed',
                        range: collapsedRange,
                        width: 2.5 * HOUR_COLUMN_WIDTH // Fixed width for collapsed
                    })
                    hour = collapsedRange.endHour
                } else if (!collapsedRange) {
                    columns.push({
                        type: 'hour',
                        hour,
                        width: HOUR_COLUMN_WIDTH
                    })
                }
            }

            const totalWidth = columns.reduce((sum, col) => sum + col.width, 0)

            layoutMap[dayDateString] = {
                columns,
                totalWidth,
                isFullyOccupied: hoursWithContent.size === 24,
                hoursWithContent, // Keep set for helper functions
                collapsedRanges
            }
            countsMap[dayDateString] = counts
        })

        return { layoutMap, countsMap }
    }, [weekDays, employees, shiftsCache, calendarCache, HOUR_COLUMN_WIDTH])

    // Updated Helper using the pre-calculated layout
    const getLeftPosition = (day: Date, hour: number, offsetPercent: number = 0): number => {
        const dateString = format(day, "yyyy-MM-dd")
        const layout = dailyDataCache.layoutMap[dateString]
        if (!layout) return 0

        let position = 0
        for (const col of layout.columns) {
            if (col.type === 'hour' && col.hour !== undefined) {
                if (col.hour === hour) {
                    return position + (offsetPercent / 100) * col.width
                } else if (col.hour < hour) {
                    position += col.width
                }
            } else if (col.type === 'collapsed' && col.range) {
                if (hour >= col.range.startHour && hour <= col.range.endHour) {
                    const hoursInRange = hour - col.range.startHour
                    const totalHours = col.range.endHour - col.range.startHour + 1
                    position += (hoursInRange / totalHours) * col.width
                    return position + (offsetPercent / 100) * (col.width / totalHours)
                } else if (col.range.endHour < hour) {
                    position += col.width
                }
            }
        }
        return position
    }

    const getRightPosition = (day: Date, hour: number, offsetPercent: number = 0) => {
        return getLeftPosition(day, hour, offsetPercent)
    }

    // --- DRAG-TO-RESIZE HELPERS ---

    // Walks a layout's columns for a given pixelX and returns total minutes into the day.
    const walkLayoutColumns = (columns: any[], pixelX: number): number => {
        let accumulatedWidth = 0
        let totalMinutes = 0
        for (const col of columns) {
            const colEnd = accumulatedWidth + col.width
            if (pixelX <= colEnd) {
                const offsetInCol = Math.max(0, pixelX - accumulatedWidth)
                const fraction = offsetInCol / col.width
                if (col.type === 'hour' && col.hour !== undefined) {
                    totalMinutes = col.hour * 60 + fraction * 60
                } else if (col.type === 'collapsed' && col.range) {
                    const rangeHours = col.range.endHour - col.range.startHour + 1
                    totalMinutes = (col.range.startHour + fraction * rangeHours) * 60
                }
                break
            }
            accumulatedWidth = colEnd
            if (col.type === 'hour' && col.hour !== undefined) {
                totalMinutes = (col.hour + 1) * 60
            } else if (col.type === 'collapsed' && col.range) {
                totalMinutes = (col.range.endHour + 1) * 60
            }
        }
        return totalMinutes
    }

    // Inverse of getLeftPosition: converts a pixel X position within a day column back to a Date, snapped to 15-min intervals.
    // When allowNextDay is true, pixelX values past the day's totalWidth map into the following day.
    const getTimeFromPosition = useCallback((day: Date, pixelX: number, allowNextDay = false): Date => {
        const dateString = format(day, "yyyy-MM-dd")
        const layout = dailyDataCache.layoutMap[dateString]
        if (!layout) {
            const fallback = new Date(day)
            fallback.setHours(0, 0, 0, 0)
            return fallback
        }

        // If pixel is past the end of the day and cross-midnight is allowed, resolve in next day's layout
        if (allowNextDay && pixelX >= layout.totalWidth) {
            const nextDay = addDays(day, 1)
            const nextDateString = format(nextDay, "yyyy-MM-dd")
            const nextLayout = dailyDataCache.layoutMap[nextDateString] || layout
            const excessPx = pixelX - layout.totalWidth
            const totalMinutes = walkLayoutColumns(nextLayout.columns, excessPx)
            const snapped = Math.round(totalMinutes / 15) * 15
            const clampedMinutes = Math.max(0, Math.min(snapped, 23 * 60 + 45))
            const result = new Date(nextDay)
            result.setHours(Math.floor(clampedMinutes / 60), clampedMinutes % 60, 0, 0)
            return result
        }

        const totalMinutes = walkLayoutColumns(layout.columns, pixelX)

        // Snap to nearest 15-minute interval
        const snapped = Math.round(totalMinutes / 15) * 15
        const clampedMinutes = Math.max(0, Math.min(snapped, 23 * 60 + 45)) // Clamp to 0:00 - 23:45

        const result = new Date(day)
        result.setHours(Math.floor(clampedMinutes / 60), clampedMinutes % 60, 0, 0)
        return result
    }, [dailyDataCache])

    const handleResizeStart = useCallback((
        e: React.MouseEvent,
        shift: any,
        edge: 'left' | 'right',
        day: Date,
        employee: EmployeeEntity,
        shiftLeftPx: number,
        shiftRightPx: number
    ) => {
        e.stopPropagation()
        e.preventDefault()

        if (isDateReadOnly(day)) return

        const state = {
            shift,
            edge,
            day,
            employee,
            initialMouseX: e.clientX,
            initialLeft: shiftLeftPx,
            initialRight: shiftRightPx,
            currentDeltaPx: 0,
            currentMouseX: e.clientX,
            currentMouseY: e.clientY,
        }
        setResizingShift(state)
        resizingShiftRef.current = state
    }, [isDateReadOnly])

    const handleMoveStart = useCallback((
        e: React.MouseEvent,
        shift: any,
        day: Date,
        employee: EmployeeEntity,
        shiftLeftPx: number,
        shiftRightPx: number
    ) => {
        e.stopPropagation()
        e.preventDefault()

        if (isDateReadOnly(day)) return
        // Ignore if a resize handle or a break drag already captured this
        if (resizingShiftRef.current) return
        if (draggingBreakRef.current) return

        const state = {
            shift,
            day,
            employee,
            initialMouseX: e.clientX,
            initialLeft: shiftLeftPx,
            initialRight: shiftRightPx,
            currentDeltaPx: 0,
            currentMouseX: e.clientX,
            currentMouseY: e.clientY,
            hasMoved: false,
        }
        setMovingShift(state)
        movingShiftRef.current = state
    }, [isDateReadOnly])
    useEffect(() => {
        if (!resizingShift) return

        const handleMouseMove = (e: MouseEvent) => {
            const current = resizingShiftRef.current
            if (!current) return
            const delta = e.clientX - current.initialMouseX
            const updated = { ...current, currentDeltaPx: delta, currentMouseX: e.clientX, currentMouseY: e.clientY }
            resizingShiftRef.current = updated
            setResizingShift(updated)
        }

        const handleMouseUp = async () => {
            const current = resizingShiftRef.current
            if (!current || !selectedDepartment) {
                setResizingShift(null)
                resizingShiftRef.current = null
                return
            }

            const { shift, edge, day, currentDeltaPx } = current

            // Calculate new pixel positions
            let newLeftPx = current.initialLeft
            let newRightPx = current.initialRight
            if (edge === 'left') {
                newLeftPx = current.initialLeft + currentDeltaPx
            } else {
                newRightPx = current.initialRight + currentDeltaPx
            }

            // Convert pixel positions to times (snapped to 15 min)
            // Right-edge drag allows cross-midnight: pixelX beyond dayWidth maps to next day
            const newStartTime = edge === 'left'
                ? getTimeFromPosition(day, newLeftPx)
                : shift.startTime as Date
            const newEndTime = edge === 'right'
                ? getTimeFromPosition(day, newRightPx, true)
                : shift.endTime as Date

            // Validate: start must be before end, minimum 15 minutes
            const diffMs = newEndTime.getTime() - newStartTime.getTime()
            if (diffMs < 15 * 60 * 1000) {
                setResizingShift(null)
                resizingShiftRef.current = null
                return
            }

            // Check if time actually changed (accounting for 15-min snap)
            const origStart = shift.startTime as Date
            const origEnd = shift.endTime as Date
            if (newStartTime.getTime() === origStart.getTime() && newEndTime.getTime() === origEnd.getTime()) {
                setResizingShift(null)
                resizingShiftRef.current = null
                return
            }

            // Clear resize state
            setResizingShift(null)
            resizingShiftRef.current = null

            // Format times as ISO strings. Night shifts use 1970-01-02 for the next-day end time.
            const formatTimeForDto = (time: Date): string => {
                const h = String(time.getHours()).padStart(2, '0')
                const m = String(time.getMinutes()).padStart(2, '0')
                const isNextDay = !isSameDay(time, day)
                return isNextDay ? `1970-01-02T${h}:${m}:00.000Z` : `1970-01-01T${h}:${m}:00.000Z`
            }

            try {
                await shiftApi.updateDetailWithChoice(shift.id, {
                    choice: UpdateShiftDetailWithChoiceDtoChoiceEnum.Single,
                    updateShiftDetailDto: {
                        startTime: formatTimeForDto(newStartTime),
                        endTime: formatTimeForDto(newEndTime),
                    },
                    date: format(day, "yyyy-MM-dd"),
                    departmentId: selectedDepartment.id,
                    seasonId,
                })

                toast.success(t("shift.shiftUpdatedSuccessfully") || "Shift updated successfully")

                // Refresh all data
                fetchData()
                setTimeout(async () => {
                    await refreshHoursAndScore()
                }, 500)
            } catch (error) {
                console.error("Error updating shift times:", error)
                toast.error(t("shift.errorUpdatingShift") || "Error updating shift")
                fetchData()
            }
        }

        // Prevent text selection and set resize cursor globally during drag
        document.body.style.userSelect = 'none'
        document.body.style.cursor = 'ew-resize'

        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)

        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
            document.body.style.userSelect = ''
            document.body.style.cursor = ''
        }
    }, [resizingShift, selectedDepartment, seasonId, getTimeFromPosition, t])

    // Helper to get the current visual position of a shift during resize.
    // dayWidthCap prevents the card from visually overflowing into the next day's column
    // (the actual cross-midnight time is shown in the global fixed tooltip instead).
    const getResizeAdjustedPositions = useCallback((shift: any, originalLeft: number, originalRight: number, dayWidthCap?: number): { left: number; width: number } => {
        if (!resizingShift || resizingShift.shift !== shift) {
            return { left: originalLeft, width: originalRight - originalLeft }
        }
        const { edge, currentDeltaPx } = resizingShift
        let newLeft = originalLeft
        let newRight = originalRight
        if (edge === 'left') {
            newLeft = originalLeft + currentDeltaPx
            newLeft = Math.min(newLeft, newRight - 10)
        } else {
            newRight = originalRight + currentDeltaPx
            // Cap at day width to prevent visual overflow into the next day column
            if (dayWidthCap !== undefined) newRight = Math.min(newRight, dayWidthCap)
            newRight = Math.max(newRight, newLeft + 10)
        }
        return { left: newLeft, width: newRight - newLeft }
    }, [resizingShift])

    // Move effect — handles dragging the whole shift body left/right
    useEffect(() => {
        if (!movingShift) return

        const MOVE_THRESHOLD_PX = 4

        const handleMouseMove = (e: MouseEvent) => {
            const current = movingShiftRef.current
            if (!current) return
            const delta = e.clientX - current.initialMouseX
            const hasMoved = current.hasMoved || Math.abs(delta) > MOVE_THRESHOLD_PX
            const updated = { ...current, currentDeltaPx: delta, currentMouseX: e.clientX, currentMouseY: e.clientY, hasMoved }
            movingShiftRef.current = updated
            setMovingShift(updated)
        }

        const handleMouseUp = async () => {
            const current = movingShiftRef.current
            if (!current || !selectedDepartment) {
                setMovingShift(null)
                movingShiftRef.current = null
                return
            }
            // If not moved far enough, treat as a click — let the onClick handler fire
            if (!current.hasMoved) {
                setMovingShift(null)
                movingShiftRef.current = null
                return
            }

            const { shift, day, currentDeltaPx } = current
            const shiftWidth = current.initialRight - current.initialLeft
            const newLeftPx = current.initialLeft + currentDeltaPx
            const newRightPx = newLeftPx + shiftWidth

            const newStartTime = getTimeFromPosition(day, newLeftPx)
            const newEndTime = getTimeFromPosition(day, newRightPx, true)

            const diffMs = newEndTime.getTime() - newStartTime.getTime()
            if (diffMs < 15 * 60 * 1000) {
                setMovingShift(null)
                movingShiftRef.current = null
                return
            }

            const origStart = shift.startTime as Date
            const origEnd = shift.endTime as Date
            if (newStartTime.getTime() === origStart.getTime() && newEndTime.getTime() === origEnd.getTime()) {
                setMovingShift(null)
                movingShiftRef.current = null
                return
            }

            setMovingShift(null)
            movingShiftRef.current = null

            const formatTimeForDto = (time: Date): string => {
                const h = String(time.getHours()).padStart(2, '0')
                const m = String(time.getMinutes()).padStart(2, '0')
                const isNextDay = !isSameDay(time, day)
                return isNextDay ? `1970-01-02T${h}:${m}:00.000Z` : `1970-01-01T${h}:${m}:00.000Z`
            }

            try {
                await shiftApi.updateDetailWithChoice(shift.id, {
                    choice: UpdateShiftDetailWithChoiceDtoChoiceEnum.Single,
                    updateShiftDetailDto: {
                        startTime: formatTimeForDto(newStartTime),
                        endTime: formatTimeForDto(newEndTime),
                    },
                    date: format(day, "yyyy-MM-dd"),
                    departmentId: selectedDepartment.id,
                    seasonId,
                })
                toast.success(t("shift.shiftUpdatedSuccessfully") || "Shift updated successfully")
                fetchData()
                setTimeout(async () => { await refreshHoursAndScore() }, 500)
            } catch (error) {
                console.error("Error moving shift:", error)
                toast.error(t("shift.errorUpdatingShift") || "Error updating shift")
                fetchData()
            }
        }

        document.body.style.userSelect = 'none'
        document.body.style.cursor = 'grabbing'

        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)

        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
            document.body.style.userSelect = ''
            document.body.style.cursor = ''
        }
    }, [movingShift, selectedDepartment, seasonId, getTimeFromPosition, t])

    // Returns the adjusted left position for a shift being dragged (width stays the same)
    const getMoveAdjustedLeft = useCallback((shift: any, originalLeft: number, originalWidth: number, dayWidthCap?: number): number => {
        if (!movingShift || movingShift.shift !== shift || !movingShift.hasMoved) return originalLeft
        const newLeft = originalLeft + movingShift.currentDeltaPx
        const capped = dayWidthCap !== undefined ? Math.min(newLeft, dayWidthCap - originalWidth) : newLeft
        return Math.max(0, capped)
    }, [movingShift])

    // --- BREAK DRAG ---
    // Starts a break drag. `mode` decides whether the whole break moves or one edge resizes.
    const handleBreakDragStart = useCallback((
        e: React.MouseEvent,
        shift: any,
        day: Date,
        employee: EmployeeEntity,
        breakKind: 'main' | 'additional',
        breakIndex: number,
        mode: 'move' | 'resize-left' | 'resize-right',
        origStartTime: Date,
        origEndTime: Date,
        startLeftPx: number,
        endLeftPx: number,
    ) => {
        e.stopPropagation()
        e.preventDefault()

        if (isDateReadOnly(day)) return

        const state = {
            shift,
            day,
            employee,
            breakKind,
            breakIndex,
            mode,
            origStartTime,
            origEndTime,
            initialMouseX: e.clientX,
            initialStartLeft: startLeftPx,
            initialEndLeft: endLeftPx,
            currentDeltaPx: 0,
            currentMouseX: e.clientX,
            currentMouseY: e.clientY,
            hasMoved: false,
        }
        setDraggingBreak(state)
        draggingBreakRef.current = state
    }, [isDateReadOnly])

    // Break drag effect — handles moving/resizing a break and persisting the new break set
    useEffect(() => {
        if (!draggingBreak) return

        const MOVE_THRESHOLD_PX = 3

        const handleMouseMove = (e: MouseEvent) => {
            const current = draggingBreakRef.current
            if (!current) return
            const delta = e.clientX - current.initialMouseX
            const hasMoved = current.hasMoved || Math.abs(delta) > MOVE_THRESHOLD_PX
            const updated = { ...current, currentDeltaPx: delta, currentMouseX: e.clientX, currentMouseY: e.clientY, hasMoved }
            draggingBreakRef.current = updated
            setDraggingBreak(updated)
        }

        const handleMouseUp = async () => {
            const current = draggingBreakRef.current
            if (!current || !selectedDepartment) {
                setDraggingBreak(null)
                draggingBreakRef.current = null
                return
            }
            // Treat a non-drag as a no-op (don't accidentally rewrite breaks on a click)
            if (!current.hasMoved) {
                setDraggingBreak(null)
                draggingBreakRef.current = null
                return
            }

            const { shift, day, mode, breakKind, breakIndex, origStartTime, origEndTime, currentDeltaPx } = current

            // Break Date objects can carry an arbitrary calendar date (e.g. 1970-01-01) while
            // getTimeFromPosition returns Dates on `day`. Normalize everything onto `day` (time-of-day
            // only) so getTime() comparisons are meaningful. Breaks only render for non-night shifts,
            // so they always fall within a single calendar day.
            const onDay = (t: Date): Date => {
                const r = new Date(day)
                r.setHours(t.getHours(), t.getMinutes(), 0, 0)
                return r
            }

            const shiftStart = shift.startTime as Date
            const shiftEnd = shift.endTime as Date
            const origStart = onDay(origStartTime)
            const origEnd = onDay(origEndTime)
            const MIN_BREAK_MS = 15 * 60 * 1000

            // Compute the dragged break's new times from pixel positions (snapped to 15 min)
            let newStart: Date
            let newEnd: Date
            if (mode === 'move') {
                // Move the whole break while preserving its duration
                const durMs = origEnd.getTime() - origStart.getTime()
                newStart = getTimeFromPosition(day, current.initialStartLeft + currentDeltaPx)
                newEnd = new Date(newStart.getTime() + durMs)
                // Keep the break inside the shift bounds (clamp instead of rejecting)
                if (newStart.getTime() < shiftStart.getTime()) {
                    newStart = new Date(shiftStart)
                    newEnd = new Date(shiftStart.getTime() + durMs)
                }
                if (newEnd.getTime() > shiftEnd.getTime()) {
                    newEnd = new Date(shiftEnd)
                    newStart = new Date(shiftEnd.getTime() - durMs)
                }
            } else if (mode === 'resize-left') {
                newEnd = origEnd
                newStart = getTimeFromPosition(day, current.initialStartLeft + currentDeltaPx)
                if (newStart.getTime() < shiftStart.getTime()) newStart = new Date(shiftStart)
                if (newStart.getTime() > newEnd.getTime() - MIN_BREAK_MS) newStart = new Date(newEnd.getTime() - MIN_BREAK_MS)
            } else {
                newStart = origStart
                newEnd = getTimeFromPosition(day, current.initialEndLeft + currentDeltaPx, true)
                if (newEnd.getTime() > shiftEnd.getTime()) newEnd = new Date(shiftEnd)
                if (newEnd.getTime() < newStart.getTime() + MIN_BREAK_MS) newEnd = new Date(newStart.getTime() + MIN_BREAK_MS)
            }

            // Bail if nothing effectively changed
            if (newStart.getTime() === origStart.getTime() && newEnd.getTime() === origEnd.getTime()) {
                setDraggingBreak(null)
                draggingBreakRef.current = null
                return
            }

            // Build the full break set (all times normalized onto `day`), replacing only the dragged break
            interface BreakRange { start: Date; end: Date }
            let mainBreak: BreakRange | null = (shift.breakStartTime && shift.breakEndTime)
                ? { start: onDay(shift.breakStartTime as Date), end: onDay(shift.breakEndTime as Date) }
                : null
            const additionalBreaks: BreakRange[] = (shift.additionalBreaks || []).map((ab: { startTime: Date; endTime: Date }) => ({
                start: onDay(ab.startTime),
                end: onDay(ab.endTime),
            }))

            if (breakKind === 'main') {
                mainBreak = { start: newStart, end: newEnd }
            } else if (breakIndex >= 0 && breakIndex < additionalBreaks.length) {
                additionalBreaks[breakIndex] = { start: newStart, end: newEnd }
            }

            // Reject if the dragged break now overlaps another break
            const others: BreakRange[] = []
            if (breakKind !== 'main' && mainBreak) others.push(mainBreak)
            additionalBreaks.forEach((b, i) => {
                if (breakKind === 'additional' && i === breakIndex) return
                others.push(b)
            })
            const overlaps = others.some(o => newStart.getTime() < o.end.getTime() && newEnd.getTime() > o.start.getTime())
            if (overlaps) {
                toast.error(t("shift.breaksCannotOverlap") || "Breaks cannot overlap")
                setDraggingBreak(null)
                draggingBreakRef.current = null
                return
            }

            setDraggingBreak(null)
            draggingBreakRef.current = null

            const formatTimeForDto = (time: Date): string => {
                const h = String(time.getHours()).padStart(2, '0')
                const m = String(time.getMinutes()).padStart(2, '0')
                const isNextDay = !isSameDay(time, day)
                return isNextDay ? `1970-01-02T${h}:${m}:00.000Z` : `1970-01-01T${h}:${m}:00.000Z`
            }

            try {
                await shiftApi.updateDetailWithChoice(shift.id, {
                    choice: UpdateShiftDetailWithChoiceDtoChoiceEnum.Single,
                    updateShiftDetailDto: {
                        // Preserve the shift's own start/end — the backend resets them to defaults
                        // when they are omitted, so we always send the current values explicitly.
                        startTime: formatTimeForDto(shiftStart),
                        endTime: formatTimeForDto(shiftEnd),
                        breakStart: mainBreak ? formatTimeForDto(mainBreak.start) : null,
                        breakEnd: mainBreak ? formatTimeForDto(mainBreak.end) : null,
                        additionalBreaks: additionalBreaks.map(b => ({
                            startTime: formatTimeForDto(b.start),
                            endTime: formatTimeForDto(b.end),
                        })),
                    },
                    date: format(day, "yyyy-MM-dd"),
                    departmentId: selectedDepartment.id,
                    seasonId,
                })
                toast.success(t("shift.shiftUpdatedSuccessfully") || "Shift updated successfully")
                fetchData()
                setTimeout(async () => { await refreshHoursAndScore() }, 500)
            } catch (error) {
                console.error("Error updating break:", error)
                toast.error(t("shift.errorUpdatingShift") || "Error updating shift")
                fetchData()
            }
        }

        document.body.style.userSelect = 'none'
        document.body.style.cursor = draggingBreak.mode === 'move' ? 'grabbing' : 'ew-resize'

        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)

        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
            document.body.style.userSelect = ''
            document.body.style.cursor = ''
        }
    }, [draggingBreak, selectedDepartment, seasonId, getTimeFromPosition, t])

    // Returns a break's adjusted pixel positions while it is being dragged (for live preview)
    const getBreakDragAdjusted = useCallback((
        shift: any,
        breakKind: 'main' | 'additional',
        breakIndex: number,
        startLeft: number,
        endLeft: number,
    ): { startLeft: number; endLeft: number } => {
        const d = draggingBreak
        if (!d || d.shift !== shift || d.breakKind !== breakKind || d.breakIndex !== breakIndex || !d.hasMoved) {
            return { startLeft, endLeft }
        }
        const delta = d.currentDeltaPx
        const MIN_PX = 6
        if (d.mode === 'move') return { startLeft: startLeft + delta, endLeft: endLeft + delta }
        if (d.mode === 'resize-left') return { startLeft: Math.min(startLeft + delta, endLeft - MIN_PX), endLeft }
        return { startLeft, endLeft: Math.max(endLeft + delta, startLeft + MIN_PX) }
    }, [draggingBreak])


    return (
        <>
            <Toaster position="top-center" reverseOrder={false} />
            {/* Global fixed-position resize tooltip — always visible regardless of scroll or container overflow */}
            {resizingShift && (() => {
                const { edge, day, initialLeft, initialRight, currentDeltaPx, currentMouseX, currentMouseY, shift } = resizingShift
                let newLeft = initialLeft
                let newRight = initialRight
                if (edge === 'left') newLeft = initialLeft + currentDeltaPx
                else newRight = initialRight + currentDeltaPx
                const previewStart = edge === 'left' ? getTimeFromPosition(day, newLeft) : shift.startTime as Date
                const previewEnd = edge === 'right' ? getTimeFromPosition(day, newRight, true) : shift.endTime as Date
                const isNextDay = !isSameDay(previewEnd, day)
                const label = `${format(previewStart, "HH:mm")} - ${format(previewEnd, "HH:mm")}${isNextDay ? ' +1d' : ''}`
                return (
                    <Box
                        sx={{
                            position: 'fixed',
                            left: currentMouseX,
                            top: currentMouseY - 40,
                            transform: 'translateX(-50%)',
                            zIndex: 9999,
                            pointerEvents: 'none',
                        }}
                    >
                        <Box sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            boxShadow: 2,
                        }}>
                            {label}
                        </Box>
                    </Box>
                )
            })()}
            {/* Global fixed-position move tooltip */}
            {movingShift?.hasMoved && (() => {
                const { day, initialLeft, initialRight, currentDeltaPx, currentMouseX, currentMouseY } = movingShift
                const shiftWidth = initialRight - initialLeft
                const newLeft = initialLeft + currentDeltaPx
                const newRight = newLeft + shiftWidth
                const previewStart = getTimeFromPosition(day, newLeft)
                const previewEnd = getTimeFromPosition(day, newRight, true)
                const isNextDay = !isSameDay(previewEnd, day)
                const label = `${format(previewStart, "HH:mm")} - ${format(previewEnd, "HH:mm")}${isNextDay ? ' +1d' : ''}`
                return (
                    <Box
                        sx={{
                            position: 'fixed',
                            left: currentMouseX,
                            top: currentMouseY - 40,
                            transform: 'translateX(-50%)',
                            zIndex: 9999,
                            pointerEvents: 'none',
                        }}
                    >
                        <Box sx={{
                            bgcolor: 'success.main',
                            color: 'white',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            boxShadow: 2,
                        }}>
                            {label}
                        </Box>
                    </Box>
                )
            })()}
            {/* Global fixed-position break drag tooltip */}
            {draggingBreak?.hasMoved && (() => {
                const { day, mode, origStartTime, origEndTime, initialStartLeft, initialEndLeft, currentDeltaPx, currentMouseX, currentMouseY } = draggingBreak
                let previewStart = origStartTime
                let previewEnd = origEndTime
                if (mode === 'move') {
                    const durMs = origEndTime.getTime() - origStartTime.getTime()
                    previewStart = getTimeFromPosition(day, initialStartLeft + currentDeltaPx)
                    previewEnd = new Date(previewStart.getTime() + durMs)
                } else if (mode === 'resize-left') {
                    previewStart = getTimeFromPosition(day, initialStartLeft + currentDeltaPx)
                } else {
                    previewEnd = getTimeFromPosition(day, initialEndLeft + currentDeltaPx, true)
                }
                const label = `${t("shift.break") || "Break"}: ${format(previewStart, "HH:mm")} - ${format(previewEnd, "HH:mm")}`
                return (
                    <Box
                        sx={{
                            position: 'fixed',
                            left: currentMouseX,
                            top: currentMouseY - 40,
                            transform: 'translateX(-50%)',
                            zIndex: 9999,
                            pointerEvents: 'none',
                        }}
                    >
                        <Box sx={{
                            bgcolor: 'warning.main',
                            color: 'white',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            boxShadow: 2,
                        }}>
                            {label}
                        </Box>
                    </Box>
                )
            })()}
            <PublishShiftsDatePicker
                onClose={() => {
                    setOpenSendShift(false)
                }}
                onSubmit={sendShifts}
                department={selectedDepartment ?? { id: 0, label: "" }}
                open={openSendShift}
                defaultStartDate={selectedDay}
                defaultEndDate={selectedEndDay}
            />
            <RequestAviability
                onClose={() => { setOpenAskForAviability(false) }}
                onSubmit={requestAviability}
                department={selectedDepartment ?? { id: 0, label: "" }}
                open={openAskForAviability}
            />
            {shiftTemplateData && (
                <AddShiftModal
                    date={selectedShift?.date || selectedDayForEdit || undefined}
                    template={shiftTemplateData}
                    data={shiftModalData}
                    fetchData={async () => {
                        fetchData()
                        fetchCalendarEntries()
                        // If we're editing a day, refresh the day's shifts
                        if (selectedDayForEdit) {
                            await handleRefreshDayShifts()
                        }
                    }}
                    onClose={async () => {
                        setIsEditShiftModalOpen(false)
                        setSelectedShift(undefined)
                        // Only clear template data if we're not in day edit mode
                        if (!selectedDayForEdit) {
                            setShiftTemplateData(undefined)
                        }
                        setShiftModalData(undefined)
                        fetchData()
                        fetchCalendarEntries()
                        // If we're editing a day, refresh the day's shifts and reopen the modal
                        if (selectedDayForEdit) {
                            await handleRefreshDayShifts()
                            setOpenDayEditModal(true)
                        }
                    }}
                    open={isEditShiftModalOpen}
                    setData={setShiftModalData}
                    departmentId={selectedDepartment?.id ?? 0}
                    openedFrom="assignment-page"
                />
            )}

            {/* Day Shift Edit Modal */}
            <Modal
                open={openDayEditModal}
                onClose={() => {
                    setOpenDayEditModal(false)
                    setSelectedDayForEdit(null)
                    setDayTemplateData(null)
                    setDayShiftsData([])
                }}
                aria-labelledby="day-shift-edit-modal"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Paper
                    elevation={24}
                    sx={{
                        width: '90%',
                        maxWidth: 800,
                        maxHeight: '90vh',
                        overflow: 'auto',
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                        border: `1px solid ${theme.palette.divider}`,
                        position: 'relative'
                    }}
                >
                    <Box p={3}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Typography variant="h5" fontWeight="bold" color="text.primary">
                                    {t("shift.editDayShifts")}
                                </Typography>
                                {selectedDayForEdit && (
                                    <Typography variant="body2" color="text.secondary">
                                        {format(selectedDayForEdit, "EEEE, dd MMMM yyyy", { locale: getLocale() })} · {weekLabel(selectedDayForEdit)}
                                    </Typography>
                                )}
                            </Box>
                            <Button
                                onClick={() => {
                                    setOpenDayEditModal(false)
                                    setSelectedDayForEdit(null)
                                    setDayTemplateData(null)
                                    setDayShiftsData([])
                                }}
                                sx={{ minWidth: 'auto', p: 1 }}
                            >
                                ✕
                            </Button>
                        </Box>

                        {isLoadingDayTemplate ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                                <Box textAlign="center">
                                    <CircularProgress size={48} sx={{ mb: 2 }} />
                                    <Typography variant="body1" color="text.secondary">
                                        {t("shift.loadingShifts")}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : dayTemplateData ? (
                            <Grid container spacing={2}>
                                {/* Add New Shift Card */}
                                {!isViewerOnly && (
                                <Grid item xs={12} sm={6}>
                                    <Paper
                                        elevation={2}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            border: `2px dashed ${theme.palette.primary.main}`,
                                            transition: "all 0.3s ease",
                                            cursor: "pointer",
                                            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
                                            "&:hover": {
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                                                borderColor: theme.palette.primary.dark,
                                                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[100]
                                            }
                                        }}
                                        onClick={handleAddNewShift}
                                    >
                                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1} minHeight={100}>
                                            <Add sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                                            <Typography variant="h6" fontWeight="bold" color="primary">
                                                {t("shift.addNewShift")}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                                {t("shift.addNewShiftDescription")}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                                )}
                                {/* Existing Shifts */}
                                {dayShiftsData.map((shift) => (
                                    <Grid item xs={12} sm={6} key={shift.id}>
                                        <Paper
                                            elevation={2}
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                border: `2px solid ${theme.palette.divider}`,
                                                transition: "all 0.3s ease",
                                                cursor: "pointer",
                                                "&:hover": {
                                                    transform: "translateY(-2px)",
                                                    boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                                                    borderColor: theme.palette.primary.main
                                                }
                                            }}
                                            onClick={isViewerOnly ? undefined : async () => handleEditShiftFromDay(shift)}
                                        >
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                <Typography variant="h6" fontWeight="bold" color="text.primary">
                                                    {shift.name}
                                                </Typography>
                                                {!isViewerOnly && (
                                                <Box display="flex" gap={0.5}>
                                                    <IconButtonMui
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            handleEditShiftFromDay(shift)
                                                        }}
                                                        sx={{
                                                            color: theme.palette.primary.main,
                                                            "&:hover": {
                                                                backgroundColor: theme.palette.primary.main + "20"
                                                            }
                                                        }}
                                                    >
                                                        <Edit />
                                                    </IconButtonMui>
                                                    <IconButtonMui
                                                        size="small"
                                                        onClick={(e) => handleOpenDeleteShiftDialog(shift, e)}
                                                        sx={{
                                                            color: theme.palette.error.main,
                                                            "&:hover": {
                                                                backgroundColor: theme.palette.error.main + "20"
                                                            }
                                                        }}
                                                    >
                                                        <DeleteForever />
                                                    </IconButtonMui>
                                                </Box>
                                                )}
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" mb={0.5}>
                                                {shift.startTime
                                                    ? new Date(shift.startTime).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })
                                                    : t("shift.noStartTime")} - {shift.endTime
                                                        ? new Date(shift.endTime).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })
                                                        : t("shift.noEndTime")}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {shift.shiftDetails?.length || 0} {t("shift.positions")}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Box textAlign="center" py={4}>
                                <Typography variant="h6" color="text.secondary" mb={1}>
                                    {t("shift.noShiftsForDay")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("shift.noShiftsForDayDescription")}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Modal>

            {/* Delete Shift Confirmation Dialog */}
            <Dialog
                open={openDeleteShiftDialog}
                onClose={() => {
                    setOpenDeleteShiftDialog(false)
                    setShiftToDelete(null)
                }}
                aria-labelledby="delete-shift-dialog-title"
                aria-describedby="delete-shift-dialog-description"
            >
                <DialogTitle id="delete-shift-dialog-title">
                    {t("shift.deleteShift") || "Delete Shift"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-shift-dialog-description">
                        {t("shift.deleteShiftConfirmation") || "This cannot be undone, are you sure?"}
                        {shiftToDelete && (
                            <Box mt={2}>
                                <Typography variant="body2" fontWeight="bold" color="text.primary">
                                    {shiftToDelete.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {shiftToDelete.startTime
                                        ? new Date(shiftToDelete.startTime).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })
                                        : t("shift.noStartTime")} - {shiftToDelete.endTime
                                            ? new Date(shiftToDelete.endTime).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })
                                            : t("shift.noEndTime")}
                                </Typography>
                            </Box>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setOpenDeleteShiftDialog(false)
                            setShiftToDelete(null)
                        }}
                        color="inherit"
                    >
                        {t("common.forms.button.cancel") || "Cancel"}
                    </Button>
                    <Button
                        onClick={handleDeleteShift}
                        color="error"
                        variant="contained"
                        startIcon={<DeleteForever />}
                    >
                        {t("common.forms.button.delete") || "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Grid mt={0.5} container spacing={1} alignItems="center" mb={0.5}>
                <Grid item xs={12} sm={3} display="flex" gap={1} alignItems="center">
                    <BetaChip label="BETA" size="small" />
                    <Button
                        variant="outlined"
                        onClick={handleOpenDateRangePicker}
                        startIcon={<CalendarTodayIcon />}
                        sx={{
                            flex: 1,
                            justifyContent: "flex-start",
                            textTransform: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                            "&:hover": {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.04)
                            }
                        }}
                    >
                        <Typography variant="body2" noWrap>
                            {format(selectedDay, "dd.MM.yyyy", { locale: getLocale() })} - {format(selectedEndDay, "dd.MM.yyyy", { locale: getLocale() })} · {weekLabel(selectedDay, selectedEndDay)}
                        </Typography>
                    </Button>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Autocomplete
                        id="department-selector"
                        size="small"
                        options={departments?.map(d => ({ label: d.name, id: d.id })) || []}
                        value={selectedDepartment}
                        onChange={(event, val) => {
                            const newDepartment = val ? { label: val.label, id: val.id } : null
                            setSelectedDepartment(newDepartment)
                            // Save to localStorage
                            if (newDepartment) {
                                localStorage.setItem('shift-assignment-selected-department-id', String(newDepartment.id))
                            } else {
                                localStorage.removeItem('shift-assignment-selected-department-id')
                            }
                        }}
                        renderInput={params => (
                            <AppTextField {...params} placeholder={t("department.departmentSelector.select")} label={t("department.itemName")} size="small" />
                        )}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                    />
                </Grid>
                <Grid item sm display="flex" justifyContent="flex-end" gap={1} alignItems="center">
                    {/* Assignment Score Indicator */}
                    {currentAssignmentScore !== null && employees.length > 0 && (() => {
                        const getScoreColor = (score: number) => {
                            if (score >= 90) return theme.palette.success.main
                            if (score >= 70) return theme.palette.warning.main
                            return theme.palette.error.main
                        }
                        const getScoreLabel = (score: number) => {
                            if (score >= 95) return t("shift.score.excellent") || "Excellent"
                            if (score >= 85) return t("shift.score.good") || "Good"
                            if (score >= 70) return t("shift.score.fair") || "Fair"
                            if (score >= 50) return t("shift.score.needsImprovement") || "Needs Improvement"
                            return t("shift.score.poor") || "Poor"
                        }
                        return (
                            <Tooltip title={t("shift.assignmentScore.tooltip") || "Click to view detailed assignment score"}>
                                <Box
                                    onClick={handleOpenAssignmentScoreModal}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        cursor: "pointer",
                                        p: 0.5,
                                        borderRadius: 1.5,
                                        border: `2px solid ${getScoreColor(currentAssignmentScore)}`,
                                        bgcolor: alpha(getScoreColor(currentAssignmentScore), 0.1),
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            transform: "scale(1.05)",
                                            boxShadow: `0 4px 12px ${alpha(getScoreColor(currentAssignmentScore), 0.3)}`
                                        }
                                    }}
                                >
                                    <Box sx={{ position: "relative", display: "inline-flex" }}>
                                        <CircularProgress
                                            variant="determinate"
                                            value={currentAssignmentScore}
                                            size={32}
                                            thickness={4}
                                            sx={{
                                                color: getScoreColor(currentAssignmentScore),
                                                transform: "rotate(-90deg)"
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                top: 0,
                                                left: 0,
                                                bottom: 0,
                                                right: 0,
                                                position: "absolute",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center"
                                            }}
                                        >
                                            <Typography variant="caption" component="div" fontWeight="bold" sx={{ fontSize: "0.6rem" }}>
                                                {currentAssignmentScore}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography variant="caption" fontWeight="bold" color={getScoreColor(currentAssignmentScore)} sx={{ fontSize: "0.65rem", lineHeight: 1.2, whiteSpace: "nowrap" }}>
                                        {getScoreLabel(currentAssignmentScore)}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        )
                    })()}
                    <Tooltip title={t("shift.checkAviability")}>
                        <IconButtonMui
                            onClick={() => {
                                if (requestsHasBeenSent) setCountAviability(!countAviability)
                            }}
                            sx={{
                                color: countAviability && requestsHasBeenSent ?
                                    theme.palette.primary.main : theme.palette.grey[600]
                            }}
                        >
                            <FactCheck />
                        </IconButtonMui>
                    </Tooltip>
                    <Tooltip title={t("shift.copyShiftPlan")}>
                        <IconButtonMui
                            onClick={() => {
                                setOpenCopyModal(true)
                            }}
                            sx={{
                                color: theme.palette.primary.main
                            }}
                        >
                            <ContentCopy />
                        </IconButtonMui>
                    </Tooltip>
                    <Tooltip title={t("shift.downloadReport")}>
                        <IconButtonMui
                            onClick={() => {
                                setOpenReportModal(true)
                            }}
                            sx={{
                                color: theme.palette.primary.main
                            }}
                        >
                            <Download />
                        </IconButtonMui>
                    </Tooltip>
                    <Tooltip title={complianceIssueCount > 0 ? t("compliance.issuesFound", { count: complianceIssueCount }) : (complianceMode === 'off' ? t("compliance.title") : t("compliance.noIssues"))}>
                        <IconButtonMui
                            onClick={() => { setOpenComplianceModal(true) }}
                            sx={{ color: complianceIssueCount > 0 ? theme.palette.error.main : theme.palette.primary.main }}
                        >
                            <Badge badgeContent={complianceIssueCount} color="error" max={99}>
                                <GppMaybe />
                            </Badge>
                        </IconButtonMui>
                    </Tooltip>
                    <Tooltip title={t("shift.requestAviability")}>
                        <IconButtonMui
                            onClick={() => {
                                setOpenAskForAviability(true)
                            }}
                            sx={{
                                color: theme.palette.primary.main
                            }}
                        >
                            <FactCheck />
                        </IconButtonMui>
                    </Tooltip>
                    <Tooltip title={t("shift.publish")}>
                        <IconButtonMui
                            onClick={() => {
                                setOpenSendShift(true)
                            }}
                            sx={{
                                color: theme.palette.primary.main
                            }}
                        >
                            <Send />
                        </IconButtonMui>
                    </Tooltip>
                    <Tooltip title={viewMode === 'detailed' ? t("shift.switchToCompactView") : t("shift.switchToDetailedView")}>
                        <IconButtonMui
                            onClick={() => {
                                setViewMode(viewMode === 'detailed' ? 'compact' : 'detailed')
                            }}
                            sx={{
                                color: theme.palette.primary.main,
                                border: `1px solid ${theme.palette.primary.main}`,
                                ml: 1
                            }}
                        >
                            {viewMode === 'detailed' ? <ViewModule /> : <ViewList />}
                        </IconButtonMui>
                    </Tooltip>
                    <Tooltip title={t("shift.excludeNoContract")}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={excludeNoContract}
                                    onChange={(e) => setExcludeNoContract(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <PersonOff sx={{ fontSize: 16, color: excludeNoContract ? theme.palette.primary.main : theme.palette.text.secondary }} />
                                    <Typography variant="caption" color={excludeNoContract ? "primary" : "text.secondary"} sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                                        {t("shift.excludeNoContract")}
                                    </Typography>
                                </Box>
                            }
                            sx={{ ml: 1, mr: 0 }}
                        />
                    </Tooltip>
                    {hiddenEmployeeIds.size > 0 && !editOrder && (
                        <Tooltip title={t("shift.hiddenEmployeesTooltip") || "These employees are hidden from the shifts view. Click to manage."}>
                            <Chip
                                icon={<VisibilityOff sx={{ fontSize: 16 }} />}
                                label={
                                    hiddenEmployeeIds.size === 1
                                        ? (t("shift.oneEmployeeHidden") || "1 employee hidden")
                                        : (t("shift.employeesHidden") || "{{count}} employees hidden").replace("{{count}}", String(hiddenEmployeeIds.size))
                                }
                                size="small"
                                variant="outlined"
                                onClick={isViewerOnly ? undefined : handleEnterEditOrder}
                                sx={{
                                    ml: 1,
                                    height: 24,
                                    color: theme.palette.text.secondary,
                                    borderColor: theme.palette.divider,
                                    cursor: isViewerOnly ? 'default' : 'pointer',
                                    '& .MuiChip-label': { fontSize: '0.7rem' }
                                }}
                            />
                        </Tooltip>
                    )}
                </Grid>
                {overlappingRequests.length > 0 && !requestsHasBeenSent && (
                    <Grid item xs={12}>
                        <Tooltip
                            title={
                                <Box>
                                    {overlappingRequests.map(r => {
                                        const sd = new Date(r.startDate).toLocaleDateString("it-IT")
                                        const ed = new Date(r.endDate).toLocaleDateString("it-IT")
                                        const cb = r.createdBy ? r.createdBy.firstName + " " + r.createdBy.lastName : t("common.deletedUser")
                                        return sd + " - " + ed + " " + t("employees.calendar.entry.createdBy") + " " + cb + " " + r.createdAt
                                    })}
                                </Box>
                            }
                            placement="bottom-start"
                        >
                            <Alert severity="warning">{t("shift.ovelappingRequests")}</Alert>
                        </Tooltip>
                    </Grid>
                )}
            </Grid>

            {/* Date Range Picker Popover */}
            <Popover
                open={Boolean(dateRangePickerAnchor)}
                anchorEl={dateRangePickerAnchor}
                onClose={handleCloseDateRangePicker}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ p: 3, minWidth: 350 }}>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                        {t("shift.selectDateRange") || "Select Date Range"}
                    </Typography>

                    {/* Quick Period Selection */}
                    <Box mb={3}>
                        <Typography variant="body2" color="text.secondary" mb={1.5} fontWeight={500}>
                            {t("shift.quickPeriods") || "Quick Periods"}
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleSetTodayAsStart}
                                sx={{ textTransform: "none" }}
                            >
                                {t("shift.today") || "Today"}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleQuickPeriodSelect('thisWeek')}
                                sx={{ textTransform: "none" }}
                            >
                                {t("shift.thisWeek") || "This Week"}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleQuickPeriodSelect('nextWeek')}
                                sx={{ textTransform: "none" }}
                            >
                                {t("shift.nextWeek") || "Next Week"}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleQuickPeriodSelect('thisMonth')}
                                sx={{ textTransform: "none" }}
                            >
                                {t("shift.thisMonth") || "This Month"}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleQuickPeriodSelect('nextMonth')}
                                sx={{ textTransform: "none" }}
                            >
                                {t("shift.nextMonth") || "Next Month"}
                            </Button>
                        </Box>
                    </Box>

                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getLocale()}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <DatePicker
                                    label={t("shift.startDate") || "Start Date"}
                                    value={tempStartDate}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            setTempStartDate(newValue)
                                            // If the new start date is after the end date, auto-adjust end date to a week after start
                                            if (tempEndDate && newValue > tempEndDate) {
                                                setTempEndDate(addWeeks(newValue, 1))
                                            }
                                        }
                                    }}
                                    format="dd/MM/yyyy"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            variant: "outlined"
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DatePicker
                                    label={t("shift.endDate") || "End Date"}
                                    value={tempEndDate}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            setTempEndDate(newValue)
                                        }
                                    }}
                                    minDate={tempStartDate}
                                    format="dd/MM/yyyy"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            variant: "outlined"
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                    <Box display="flex" justifyContent="flex-end" gap={1} mt={3}>
                        <Button onClick={handleCloseDateRangePicker} variant="outlined">
                            {t("common.forms.button.cancel") || "Cancel"}
                        </Button>
                        <Button onClick={handleApplyDateRange} variant="contained" color="primary">
                            {t("common.forms.button.apply") || "Apply"}
                        </Button>
                    </Box>
                </Box>
            </Popover>

            <Card sx={{ overflow: "hidden", borderRadius: 3, boxShadow: "0 8px 32px rgba(0,0,0,0.1)" }}>
                {editOrder ? (
                    // EDIT ORDER MODE - Drag and Drop UI
                    <Box sx={{ p: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h6">
                                {t("shift.editEmployeeOrder") || "Edit Employee Order"}
                            </Typography>
                            <Box display="flex" gap={1}>
                                <Button
                                    variant="outlined"
                                    startIcon={<Cancel />}
                                    onClick={handleCancelEditOrder}
                                    disabled={isSavingOrder}
                                >
                                    {t("common.forms.button.cancel") || "Cancel"}
                                </Button>
                                {!isViewerOnly && (
                                <Button
                                    variant="contained"
                                    startIcon={isSavingOrder ? <CircularProgress size={16} color="inherit" /> : <Save />}
                                    onClick={handleSaveOrder}
                                    disabled={isSavingOrder}
                                >
                                    {t("common.forms.button.save") || "Save"}
                                </Button>
                                )}
                            </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            {t("shift.dragToReorder") || "Drag and drop employees to reorder them. Employees with custom order will appear first, followed by others sorted by work position."}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            {t("shift.hideToReorderHint") || "Use the eye icon to hide an employee from the shifts view. Hidden employees stay listed here so you can show them again."}
                        </Typography>
                        {employeesInEditMode.length > 0 && (
                            <DragDropContext onDragEnd={handleDragEnd}>
                                <Droppable droppableId="employees">
                                    {(provided: any, snapshot: any) => (
                                        <div
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            style={{
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: theme.shape.borderRadius,
                                                backgroundColor: theme.palette.background.paper,
                                                minHeight: snapshot.isDraggingOver ? 200 : 'auto'
                                            }}
                                        >
                                            {employeesInEditMode.map((employee, index) => {
                                                // Use employee ID as string - react-beautiful-dnd requires string IDs
                                                const draggableId = String(employee.id)
                                                const isHidden = hiddenInEditMode.has(employee.id)
                                                return (
                                                    <Draggable key={`drag-${employee.id}`} draggableId={draggableId} index={index}>
                                                        {(provided: any, snapshot: any) => {
                                                            // Merge styles properly - react-beautiful-dnd requires the style prop
                                                            const style = provided.draggableProps.style
                                                            return (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    style={{
                                                                        ...style,
                                                                        ...(snapshot.isDragging && {
                                                                            boxShadow: theme.shadows[8]
                                                                        })
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            p: 1.5,
                                                                            borderBottom: index < employeesInEditMode.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                                                            bgcolor: snapshot.isDragging ? theme.palette.action.hover : 'transparent',
                                                                            transition: snapshot.isDragging ? 'none' : 'background-color 0.2s',
                                                                            cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                                                            '&:hover': {
                                                                                bgcolor: snapshot.isDragging ? theme.palette.action.hover : theme.palette.action.hover
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                mr: 2,
                                                                                pointerEvents: 'none'
                                                                            }}
                                                                        >
                                                                            <DragIndicator color="action" />
                                                                        </Box>
                                                                        <Avatar
                                                                            src={getEmployeeImageUrl(employee.filePath)}
                                                                            sx={{ width: 40, height: 40, mr: 2, opacity: isHidden ? 0.4 : 1 }}
                                                                        >
                                                                            {!employee.filePath && `${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`}
                                                                        </Avatar>
                                                                        <Box flex={1} sx={{ opacity: isHidden ? 0.4 : 1 }}>
                                                                            <Typography variant="body1" fontWeight="medium">
                                                                                {employee.firstName} {employee.lastName}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {t("shift.position") || "Position"}: {workPositions.find(wp => wp.id === employeeWorkPositions[employee.id])?.name || t("shift.noPosition")}
                                                                            </Typography>
                                                                        </Box>
                                                                        {isHidden && (
                                                                            <Chip
                                                                                label={t("shift.hidden") || "Hidden"}
                                                                                size="small"
                                                                                color="default"
                                                                                variant="outlined"
                                                                                sx={{ mr: 1 }}
                                                                            />
                                                                        )}
                                                                        <Chip
                                                                            label={`#${index + 1}`}
                                                                            size="small"
                                                                            color="primary"
                                                                            variant="outlined"
                                                                            sx={{ opacity: isHidden ? 0.4 : 1 }}
                                                                        />
                                                                        {!isViewerOnly && (
                                                                            <Tooltip title={isHidden ? (t("shift.showEmployee") || "Show in shifts view") : (t("shift.hideEmployee") || "Hide from shifts view")}>
                                                                                <IconButtonMui
                                                                                    size="small"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation()
                                                                                        handleToggleHiddenInEditMode(employee.id)
                                                                                    }}
                                                                                    sx={{ ml: 1, color: isHidden ? theme.palette.text.secondary : theme.palette.primary.main }}
                                                                                >
                                                                                    {isHidden ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                                                </IconButtonMui>
                                                                            </Tooltip>
                                                                        )}
                                                                    </Box>
                                                                </div>
                                                            )
                                                        }}
                                                    </Draggable>
                                                )
                                            })}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        )}
                    </Box>
                ) : employees.length > 0 ? (
                    viewMode === 'compact' ? (
                        // COMPACT VIEW
                        <Box
                            sx={{
                                overflowX: 'scroll',
                                overflowY: 'auto',
                                maxHeight: 'calc(100vh - 150px)',
                                '&::-webkit-scrollbar': {
                                    height: '8px',
                                    width: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
                                    borderRadius: '4px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
                                    borderRadius: '4px',
                                },
                                scrollbarWidth: 'thin',
                                scrollbarColor: `${theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400]} ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200]}`,
                            }}
                        >
                            <Grid container sx={{ minWidth: "max-content" }}>
                                {/* Kalenderwoche band: one segment per ISO week, spanning that week's day columns */}
                                <Grid container sx={{ width: "100%", position: "sticky", top: 0, zIndex: 1006, bgcolor: theme.palette.background.paper }}>
                                    <Grid item sx={{
                                        width: '120px',
                                        minWidth: '120px',
                                        flexShrink: 0,
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 1007,
                                        bgcolor: theme.palette.background.paper
                                    }}>
                                        <Box height={16} borderRight={1} borderColor="divider" bgcolor={theme.palette.primary.dark} />
                                    </Grid>
                                    {weekBands.map((band) => (
                                        <Grid item key={band.key} sx={{
                                            width: `${band.days.length * 80}px`,
                                            minWidth: `${band.days.length * 80}px`,
                                            flexShrink: 0
                                        }}>
                                            <Box
                                                height={16}
                                                borderLeft={1}
                                                borderColor="divider"
                                                bgcolor={theme.palette.primary.dark}
                                                color="white"
                                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                                            >
                                                <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.55rem', lineHeight: 1, letterSpacing: 0.3, whiteSpace: 'nowrap' }}>
                                                    {band.label}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                                {/* Compact Header Row */}
                                <Grid container sx={{ width: "100%", position: "sticky", top: 16, zIndex: 1001, bgcolor: theme.palette.background.paper }}>
                                    {/* Employee Column Header */}
                                    <Grid item sx={{
                                        width: '120px',
                                        minWidth: '120px',
                                        flexShrink: 0,
                                        position: "sticky",
                                        left: 0,
                                        top: 0,
                                        zIndex: 1002,
                                        bgcolor: theme.palette.background.paper
                                    }}>
                                        <Box
                                            p={0.25}
                                            borderBottom={1}
                                            borderRight={1}
                                            borderColor="divider"
                                            bgcolor={theme.palette.primary.main}
                                            color="white"
                                            height={24}
                                            sx={{
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 1003,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 0.5
                                            }}
                                        >
                                            <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.6rem' }}>
                                                {t("menu.management.employees")}
                                            </Typography>
                                            {!editOrder && !isViewerOnly && (
                                                <Tooltip title={t("shift.editOrder") || "Edit Employee Order"}>
                                                    <IconButtonMui
                                                        size="small"
                                                        onClick={handleEnterEditOrder}
                                                        sx={{
                                                            color: 'white',
                                                            p: 0.5,
                                                            minWidth: 'auto',
                                                            '&:hover': {
                                                                backgroundColor: alpha(theme.palette.common.white, 0.2)
                                                            }
                                                        }}
                                                    >
                                                        <DragIndicator sx={{ fontSize: 16 }} />
                                                    </IconButtonMui>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Grid>
                                    {/* Day Headers - Compact */}
                                    {weekDays.map((day, dayIndex) => {
                                        const dayDateString = format(day, "yyyy-MM-dd")
                                        const isReadOnly = isDateReadOnly(day)
                                        return (
                                            <Grid item key={dayIndex} sx={{ minWidth: '80px', width: '80px' }}>
                                                <Box
                                                    p={0.25}
                                                    borderBottom={1}
                                                    borderLeft={1}
                                                    borderColor="divider"
                                                    bgcolor={daysWithoutTemplate.has(dayDateString) ? theme.palette.warning.main : theme.palette.primary.main}
                                                    color="white"
                                                    height={24}
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: 0,
                                                        opacity: isReadOnly ? 0.6 : 1,
                                                        position: 'relative',
                                                        '&::after': isReadOnly ? {
                                                            content: '""',
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            bgcolor: alpha(theme.palette.common.black, 0.2),
                                                            pointerEvents: 'none'
                                                        } : {}
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, width: '100%', justifyContent: 'center' }}>
                                                        <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.55rem', lineHeight: 1.1 }}>
                                                            {format(day, "EEE", { locale: getLocale() })}
                                                        </Typography>
                                                        {daysWithoutTemplate.has(dayDateString) && !isReadOnly && (
                                                            <Box
                                                                component="span"
                                                                onClick={async (e) => {
                                                                    e.stopPropagation()
                                                                    handleAssignTemplateToDay(day)
                                                                }}
                                                                sx={{
                                                                    width: '8px',
                                                                    height: '8px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: 'white',
                                                                    cursor: 'pointer',
                                                                    flexShrink: 0,
                                                                    boxShadow: '0 0 2px rgba(0,0,0,0.3)',
                                                                    '&:hover': {
                                                                        transform: 'scale(1.2)',
                                                                        boxShadow: '0 0 4px rgba(0,0,0,0.5)'
                                                                    },
                                                                    transition: 'all 0.2s ease'
                                                                }}
                                                                title={t("shift.noTemplateForDay") || "No template assigned. Click to assign a template."}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                        <Typography variant="caption" sx={{ fontSize: '0.5rem', lineHeight: 1.1 }}>
                                                            {format(day, "dd.MM")}
                                                        </Typography>
                                                        {canManageShiftNotes && (
                                                            <Tooltip title={hasUnresolvedShiftNotes(day) ? t("shiftNotes.tooltipWithNotes") : t("shiftNotes.tooltip")}>
                                                                <Badge color="warning" variant="dot" overlap="circular" invisible={!hasUnresolvedShiftNotes(day)}>
                                                                    <IconButtonMui
                                                                        size="small"
                                                                        onClick={(e) => { e.stopPropagation(); handleOpenShiftNotes(day) }}
                                                                        sx={{ color: 'white', p: 0, width: '14px', height: '14px' }}
                                                                    >
                                                                        <EditNote sx={{ fontSize: '0.7rem' }} />
                                                                    </IconButtonMui>
                                                                </Badge>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        )
                                    })}
                                </Grid>
                                {/* Compact Employee Rows */}
                                {employees.filter(emp => !hiddenEmployeeIds.has(emp.id)).map((employee, empIndex) => {
                                    const employeeWeeklyHours = weeklyWorkHours[employee.id] ?? defaultWeeklyHours
                                    const workPercentage = (employee.employeesToSeasons?.[0]?.contract?.workPercentage ?? 100) / 100
                                    const expectedHours = (eachDayOfInterval({ start: selectedDay, end: selectedEndDay }).length / 7) * employeeWeeklyHours * workPercentage
                                    // startOfDay normalizes UTC-midnight strings to local
                                    // midnight so the boundary day compares correctly
                                    // (otherwise contracts read as 02:00 local in CEST and
                                    // their start day was wrongly painted as "before start").
                                    const compactContractEnd = employee.employeesToSeasons?.[0]?.contract?.endedOn
                                        ? startOfDay(new Date(employee.employeesToSeasons[0].contract.endedOn))
                                        : null
                                    const compactContractStart = employee.employeesToSeasons?.[0]?.contract?.startedOn
                                        ? startOfDay(new Date(employee.employeesToSeasons[0].contract.startedOn))
                                        : null

                                    return (
                                        <Grid container key={employee.id} sx={{ borderBottom: `1px solid ${theme.palette.divider}`, overflow: 'visible', position: 'relative' }}>
                                            {/* Employee Info - Compact */}
                                            <Grid item sx={{
                                                width: '120px',
                                                minWidth: '120px',
                                                flexShrink: 0,
                                                position: "sticky",
                                                left: 0,
                                                zIndex: 10,
                                                bgcolor: theme.palette.background.paper,
                                                borderRight: `1px solid ${theme.palette.divider}`,
                                                p: 0.25,
                                                display: 'flex',
                                                alignItems: 'center',
                                                height: '24px'
                                            }}>
                                                <Tooltip
                                                    title={
                                                        <Box>
                                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                                                {employee.firstName} {employee.lastName}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <AccessTime sx={{ fontSize: 12 }} />
                                                                {workedHours[employee.id] || 0}h / {+expectedHours.toFixed(2)}h
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    arrow
                                                >
                                                    <Box display="flex" alignItems="center" gap={0.4} width="100%" minWidth={0}>
                                                        <Avatar
                                                            src={getEmployeeImageUrl(employee.filePath)}
                                                            sx={{ width: 16, height: 16, fontSize: '0.5rem', flexShrink: 0 }}
                                                        >
                                                            {!employee.filePath && `${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`}
                                                        </Avatar>
                                                        <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.6rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, lineHeight: 1.2 }}>
                                                            {employee.firstName} {employee.lastName}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            </Grid>
                                            {/* Day Columns - Compact */}
                                            {weekDays.map((day, dayIndex) => {
                                                // Use the new O(1) helpers
                                                const dayShifts = getShiftsFromCache(employee.id, day)
                                                const calendarEntriesForDay = getCalendarFromCache(employee.id, day)
                                                const isReadOnly = isDateReadOnly(day)
                                                const isCompactContractEnded = compactContractEnd ? day > compactContractEnd : false
                                                const isCompactBeforeContractStart = compactContractStart ? day < compactContractStart : false
                                                const isCompactOutsideContract = isCompactContractEnded || isCompactBeforeContractStart

                                                return (
                                                    <Grid item key={dayIndex} sx={{
                                                        minWidth: '80px',
                                                        width: '80px',
                                                        position: 'relative',
                                                        height: '24px',
                                                        borderLeft: `1px solid ${theme.palette.divider}`,
                                                        opacity: isReadOnly ? 0.6 : 1,
                                                        overflow: 'visible', // Allow multi-day events to overflow
                                                        '&::after': isReadOnly ? {
                                                            content: '""',
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            bgcolor: alpha(theme.palette.common.black, 0.05),
                                                            pointerEvents: 'none',
                                                            zIndex: 1
                                                        } : {}
                                                    }}>
                                                        {/* Manager note flag - compact */}
                                                        {canManageShiftNotes && hasEmployeeDayNotes(employee.id, day) && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                top: 1,
                                                                right: 1,
                                                                width: 6,
                                                                height: 6,
                                                                borderRadius: '50%',
                                                                bgcolor: theme.palette.warning.main,
                                                                border: `1px solid ${theme.palette.background.paper}`,
                                                                zIndex: 11,
                                                                pointerEvents: 'none'
                                                            }} />
                                                        )}
                                                        {/* Contract ended overlay - compact */}
                                                        {isCompactOutsideContract && (
                                                            <Tooltip title={isCompactContractEnded ? t("shift.contractEnded") : t("shift.contractNotStarted")} arrow>
                                                                <Box sx={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    right: 0,
                                                                    bottom: 0,
                                                                    zIndex: 10,
                                                                    background: `repeating-linear-gradient(
                                                                        -45deg,
                                                                        transparent,
                                                                        transparent 4px,
                                                                        ${alpha(theme.palette.grey[400], 0.15)} 4px,
                                                                        ${alpha(theme.palette.grey[400], 0.15)} 8px
                                                                    )`,
                                                                    bgcolor: alpha(theme.palette.grey[300], 0.25),
                                                                    pointerEvents: 'auto',
                                                                    cursor: 'not-allowed',
                                                                }} />
                                                            </Tooltip>
                                                        )}
                                                        {/* Compact Shift Bars */}
                                                        {dayShifts.map((shift, shiftIndex) => {
                                                            // Calculate position based on hours (0-24) as percentage
                                                            const startHourDecimal = shift.startHour + (shift.leftOffset / 100)
                                                            const endHourDecimal = shift.endHour + (shift.rightOffset / 100)
                                                            const left = (startHourDecimal / 24) * 100
                                                            const right = 100 - ((endHourDecimal / 24) * 100)

                                                            // Find the Position object for this shift
                                                            const position = getEmployeeShiftsForDate(employee.id, day).find(p =>
                                                                p.workPositionId === shift.workPositionId
                                                            )

                                                            // Use primary color for compact view (work positions don't have color in the simple array)
                                                            const shiftColor = theme.palette.primary.main

                                                            return (
                                                                <Tooltip
                                                                    key={shiftIndex}
                                                                    title={
                                                                        <Box sx={{ p: 1 }}>
                                                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                                                <WorkOutline fontSize="small" />
                                                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                                                    {shift.workPositionName}
                                                                                </Typography>
                                                                            </Stack>
                                                                            <Divider sx={{ mb: 1, borderColor: 'rgba(255,255,255,0.2)' }} />
                                                                            <Stack spacing={0.5}>
                                                                                {shift.shiftData?.name && (
                                                                                    <Typography variant="body2" sx={{ mb: 0.5, fontStyle: 'italic' }}>
                                                                                        {shift.shiftData.name}
                                                                                    </Typography>
                                                                                )}
                                                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                                                    <AccessTime fontSize="small" sx={{ opacity: 0.7 }} />
                                                                                    <Typography variant="body2">
                                                                                        {t('shift.tooltip.time')}: {format(shift.startTime, 'HH:mm')} - {format(shift.endTime, 'HH:mm')}
                                                                                        {shift.isNightShift && (
                                                                                            <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'warning.main', fontWeight: 'bold' }}>
                                                                                                ({t('shift.tooltip.nightShift')})
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Typography>
                                                                                </Stack>

                                                                                {(shift.breakStartTime && shift.breakEndTime) && (
                                                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                                                        <FreeBreakfast fontSize="small" sx={{ opacity: 0.7 }} />
                                                                                        <Typography variant="body2">
                                                                                            {t("shift.break")}: {format(shift.breakStartTime, 'HH:mm')} - {format(shift.breakEndTime, 'HH:mm')}
                                                                                        </Typography>
                                                                                    </Stack>
                                                                                )}

                                                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                                                    <Timer fontSize="small" sx={{ opacity: 0.7 }} />
                                                                                    <Typography variant="body2">
                                                                                        {t("shiftStatistics.totalHours")}: {shift.totalHours}h
                                                                                    </Typography>
                                                                                </Stack>

                                                                                {shift.notes && (
                                                                                    <Stack direction="row" alignItems="start" spacing={1} sx={{ mt: 0.5 }}>
                                                                                        <Description fontSize="small" sx={{ opacity: 0.7, mt: 0.3 }} />
                                                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                                                            {shift.notes}
                                                                                        </Typography>
                                                                                    </Stack>
                                                                                )}
                                                                            </Stack>
                                                                        </Box>
                                                                    }
                                                                    arrow
                                                                    placement="top"
                                                                >
                                                                    <Box
                                                                        onClick={() => {
                                                                            // For read-only dates, still allow viewing but not editing
                                                                            if (position && shift.shiftData) {
                                                                                setSelectedShiftDetails({
                                                                                    shift: position,
                                                                                    employee,
                                                                                    date: day,
                                                                                    shiftData: shift.shiftData
                                                                                })
                                                                                setOpenShiftDetailsModal(true)
                                                                            }
                                                                        }}
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            left: `${left}%`,
                                                                            right: `${right}%`,
                                                                            top: '2px',
                                                                            bottom: '2px',
                                                                            height: 'calc(100% - 4px)',
                                                                            bgcolor: shiftColor,
                                                                            borderRadius: '1px',
                                                                            cursor: isReadOnly ? 'default' : 'pointer',
                                                                            border: `1px solid ${alpha(theme.palette.common.black, 0.1)}`,
                                                                            opacity: isReadOnly ? 0.7 : 1,
                                                                            '&:hover': {
                                                                                opacity: isReadOnly ? 0.7 : 0.8,
                                                                                zIndex: 5
                                                                            },
                                                                            zIndex: 2
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            )
                                                        })}
                                                        {/* Compact Calendar Events */}
                                                        {calendarEntriesForDay.map((entry, entryIndex) => {
                                                            // Check if this is a multi-day event
                                                            const entryStartDate = new Date(entry.entry.startedOn)
                                                            const entryEndDate = new Date(entry.entry.endedOn)
                                                            const entryStartDateOnly = startOfDay(entryStartDate)
                                                            const entryEndDateOnly = startOfDay(entryEndDate)
                                                            const isMultiDay = !isSameDay(entryStartDateOnly, entryEndDateOnly)

                                                            // FIX 2: DETECT ALL DAY VS TIME SPECIFIC
                                                            const isAllDay = (entry.entry.startTime === null && entry.entry.endTime === null) ||
                                                                (entry.startHour === 0 && entry.endHour === 23)

                                                            // For all-day events, use 100% width and 0 left
                                                            let left = "0%"
                                                            let right = "0%"

                                                            if (!isAllDay) {
                                                                // Calculate position based on hours (0-24) as percentage
                                                                const startHourDecimal = entry.startHour + (entry.leftOffset / 100)
                                                                const endHourDecimal = entry.endHour + (entry.rightOffset / 100)
                                                                left = `${(startHourDecimal / 24) * 100}%`
                                                                right = `${100 - ((endHourDecimal / 24) * 100)}%`
                                                            }

                                                            return (
                                                                <Tooltip
                                                                    key={`entry-${entry.entry.id}-${dayIndex}`}
                                                                    title={
                                                                        <Box sx={{ p: 1 }}>
                                                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                                                <Event fontSize="small" />
                                                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                                                    {calendarEntryLabel(entry.entry)}
                                                                                </Typography>
                                                                            </Stack>
                                                                            <Divider sx={{ mb: 1, borderColor: 'rgba(255,255,255,0.2)' }} />
                                                                            <Stack spacing={0.5}>
                                                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                                                    <AccessTime fontSize="small" sx={{ opacity: 0.7 }} />
                                                                                    <Typography variant="body2">
                                                                                        {isAllDay
                                                                                            ? t("calendar.tooltip.allDay")
                                                                                            : `${format(entry.startTime, 'HH:mm')} - ${format(entry.endTime, 'HH:mm')}`
                                                                                        }
                                                                                    </Typography>
                                                                                </Stack>

                                                                                {entry.entry.notes && (
                                                                                    <Stack direction="row" alignItems="start" spacing={1} sx={{ mt: 0.5 }}>
                                                                                        <Description fontSize="small" sx={{ opacity: 0.7, mt: 0.3 }} />
                                                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                                                                                            {entry.entry.notes}
                                                                                        </Typography>
                                                                                    </Stack>
                                                                                )}
                                                                            </Stack>
                                                                        </Box>
                                                                    }
                                                                    arrow
                                                                    placement="top"
                                                                >
                                                                    <Box
                                                                        onClick={() => {
                                                                            // Allow viewing calendar entries even for read-only dates
                                                                            setSelectedCalendarEntry({
                                                                                entry: entry.entry,
                                                                                employee,
                                                                                date: day,
                                                                                startTime: entry.startTime,
                                                                                endTime: entry.endTime,
                                                                                color: entry.color,
                                                                                type: entry.type
                                                                            })
                                                                            setOpenCalendarEntryModal(true)
                                                                        }}
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            left: isAllDay ? "0px" : `${left}`,
                                                                            right: isAllDay ? "0px" : `${right}`,
                                                                            width: isAllDay ? "100%" : undefined,
                                                                            top: '2px',
                                                                            bottom: '2px',
                                                                            height: 'calc(100% - 4px)',
                                                                            bgcolor: entry.color,
                                                                            borderRadius: isMultiDay ? '1px' : '1px',
                                                                            cursor: 'pointer',
                                                                            border: `1px solid ${alpha(theme.palette.common.black, 0.1)}`,
                                                                            zIndex: 3,
                                                                            '&:hover': {
                                                                                opacity: 0.8,
                                                                                zIndex: 5
                                                                            }
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            )
                                                        })}
                                                        {/* Compact employee day notes - all-day strip (a note has no shift/time) */}
                                                        {canManageShiftNotes && (() => {
                                                            const dayNotes = getUnresolvedEmployeeDayNotes(employee.id, day)
                                                            if (dayNotes.length === 0) return null
                                                            return (
                                                                <Tooltip title={employeeDayNotesTooltip(dayNotes)} arrow placement="top">
                                                                    <Box
                                                                        onClick={() => handleAddShift(employee, day)}
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            left: 0,
                                                                            right: 0,
                                                                            bottom: 0,
                                                                            height: '8px',
                                                                            bgcolor: noteStrip.accent,
                                                                            borderTop: `1px solid ${alpha(theme.palette.common.black, 0.1)}`,
                                                                            opacity: 0.9,
                                                                            cursor: 'pointer',
                                                                            zIndex: 4,
                                                                            // Same folded-corner motif as the detailed view strip
                                                                            clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 0 100%)',
                                                                            '&::after': {
                                                                                content: '""',
                                                                                position: 'absolute',
                                                                                top: 0,
                                                                                right: 0,
                                                                                width: '6px',
                                                                                height: '6px',
                                                                                background: 'linear-gradient(225deg, transparent 50%, rgba(0, 0, 0, 0.25) 50%)'
                                                                            },
                                                                            '&:hover': { opacity: 1 }
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            )
                                                        })()}
                                                    </Grid>
                                                )
                                            })}
                                        </Grid>
                                    )
                                })}
                            </Grid>
                        </Box>
                    ) : (
                        // DETAILED VIEW (existing code)
                        <Box
                            sx={{
                                overflowX: 'scroll',
                                overflowY: 'auto',
                                maxHeight: 'calc(100vh - 200px)',
                                '&::-webkit-scrollbar': {
                                    height: '12px',
                                },
                                '&::-webkit-scrollbar:horizontal': {
                                    display: 'block',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
                                    borderRadius: '6px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
                                    borderRadius: '6px',
                                    '&:hover': {
                                        background: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[500],
                                    },
                                },
                                // Force scrollbar to always be visible (Firefox)
                                scrollbarWidth: 'thin',
                                scrollbarColor: `${theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400]} ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200]}`,
                            }}
                        >
                            <Grid container sx={{ minWidth: "max-content" }}>
                                {/* Kalenderwoche band: one segment per ISO week, spanning that week's day columns */}
                                <Grid container sx={{ width: "100%", position: "sticky", top: 0, zIndex: 1006, bgcolor: theme.palette.background.paper }}>
                                    <Grid item sx={{
                                        width: `${EMPLOYEE_COLUMN_WIDTH}px`,
                                        minWidth: `${EMPLOYEE_COLUMN_WIDTH}px`,
                                        flexShrink: 0,
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 1007,
                                        bgcolor: theme.palette.background.paper
                                    }}>
                                        <Box height={20} borderColor="divider" bgcolor={theme.palette.primary.dark} />
                                    </Grid>
                                    {weekBands.map((band) => {
                                        // Day columns are individually sized, so the band spans the sum of its days' widths
                                        const bandWidth = band.days.reduce((sum, day) => {
                                            const layout = dailyDataCache.layoutMap[format(day, "yyyy-MM-dd")]
                                            return sum + (layout ? layout.totalWidth : 80)
                                        }, 0)
                                        return (
                                            <Grid item key={band.key} sx={{
                                                width: `${bandWidth}px`,
                                                minWidth: `${bandWidth}px`,
                                                flexShrink: 0
                                            }}>
                                                <Box
                                                    height={20}
                                                    borderLeft={1}
                                                    borderColor="divider"
                                                    bgcolor={theme.palette.primary.dark}
                                                    color="white"
                                                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                                                >
                                                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem', lineHeight: 1, letterSpacing: 0.4, whiteSpace: 'nowrap' }}>
                                                        {band.label}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )
                                    })}
                                </Grid>
                                {/* Row 1: Day Name Headers */}
                                <Grid container sx={{ width: "100%", position: "sticky", top: 20, zIndex: 1001, bgcolor: theme.palette.background.paper }}>
                                    {/* Employee Column Header - spans two rows */}
                                    <Grid item sx={{
                                        width: `${EMPLOYEE_COLUMN_WIDTH}px`,
                                        minWidth: `${EMPLOYEE_COLUMN_WIDTH}px`,
                                        flexShrink: 0,
                                        position: "sticky",
                                        left: 0,
                                        top: 0,
                                        zIndex: 1002,
                                        bgcolor: theme.palette.background.paper
                                    }}>
                                        <Box
                                            p={2}
                                            borderBottom={1}
                                            borderColor="divider"
                                            bgcolor={theme.palette.primary.main}
                                            color="white"
                                            height={60}
                                            sx={{
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 1003,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight="bold" display="flex" alignItems="center" gap={1}>
                                                <Person />
                                                {t("menu.management.employees")}
                                            </Typography>
                                            {!editOrder && !isViewerOnly && (
                                                <Tooltip title={t("shift.editOrder") || "Edit Employee Order"}>
                                                    <IconButtonMui
                                                        size="small"
                                                        onClick={handleEnterEditOrder}
                                                        sx={{
                                                            color: 'white',
                                                            '&:hover': {
                                                                backgroundColor: alpha(theme.palette.common.white, 0.2)
                                                            }
                                                        }}
                                                    >
                                                        <DragIndicator />
                                                    </IconButtonMui>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Grid>
                                    {/* Day Name Headers */}
                                    {weekDays.map((day, dayIndex) => {
                                        // READ FROM CACHE
                                        const dayDateString = format(day, "yyyy-MM-dd")
                                        const layout = dailyDataCache.layoutMap[dayDateString]
                                        const isReadOnly = isDateReadOnly(day)

                                        // Use the pre-calculated width
                                        const dayWidth = layout ? layout.totalWidth : 80

                                        return (
                                            <Grid key={dayIndex} item sx={{
                                                width: `${dayWidth}px`,
                                                minWidth: `${dayWidth}px`,
                                                flexShrink: 0,
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 1001,
                                                bgcolor: theme.palette.background.paper
                                            }}>
                                                <Box
                                                    p={1}
                                                    pb={1.8}
                                                    borderBottom={1}
                                                    borderRight={1}
                                                    borderColor="divider"
                                                    textAlign="center"
                                                    bgcolor={daysWithoutTemplate.has(dayDateString)
                                                        ? theme.palette.warning.main
                                                        : theme.palette.primary.main}
                                                    color="white"
                                                    height={65}
                                                    minHeight={65}
                                                    sx={{
                                                        position: "relative",
                                                        top: 0,
                                                        zIndex: 1003,
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        gap: 0.5,
                                                        opacity: isReadOnly ? 0.6 : 1,
                                                        '&::after': isReadOnly ? {
                                                            content: '""',
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            bgcolor: alpha(theme.palette.common.black, 0.2),
                                                            pointerEvents: 'none'
                                                        } : {}
                                                    }}
                                                >
                                                    {/* Top row: Date info and edit button */}
                                                    <Box width="100%" display="flex" alignItems="flex-start" justifyContent="space-between" gap={0.5}>
                                                        <Box flex={1} display="flex" flexDirection="column" alignItems="center" gap={0.25}>
                                                            <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                                                                {format(day, "EEE", { locale: getLocale() })}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ opacity: 0.9, lineHeight: 1.2 }}>
                                                                {format(day, "dd.MM", { locale: getLocale() })}
                                                            </Typography>
                                                        </Box>
                                                        {canManageShiftNotes && (
                                                            <Tooltip title={hasUnresolvedShiftNotes(day) ? t("shiftNotes.tooltipWithNotes") : t("shiftNotes.tooltip")}>
                                                                <Badge color="warning" variant="dot" overlap="circular" invisible={!hasUnresolvedShiftNotes(day)}>
                                                                    <IconButtonMui
                                                                        size="small"
                                                                        onClick={() => handleOpenShiftNotes(day)}
                                                                        sx={{
                                                                            color: "white",
                                                                            p: 0.25,
                                                                            minWidth: "auto",
                                                                            width: "20px",
                                                                            height: "20px",
                                                                            "&:hover": {
                                                                                backgroundColor: "rgba(255, 255, 255, 0.2)"
                                                                            }
                                                                        }}
                                                                    >
                                                                        <EditNote sx={{ fontSize: "0.9rem" }} />
                                                                    </IconButtonMui>
                                                                </Badge>
                                                            </Tooltip>
                                                        )}
                                                        {!daysWithoutTemplate.has(format(day, "yyyy-MM-dd")) && !isViewerOnly && (
                                                            <Tooltip title={isDateReadOnly(day) ? (t("shift.readOnlyDay") || "Read-only day") : t("common.forms.button.edit")}>
                                                                <IconButtonMui
                                                                    size="small"
                                                                    disabled={isDateReadOnly(day)}
                                                                    onClick={async () => handleEditDay(day)}
                                                                    sx={{
                                                                        color: "white",
                                                                        p: 0.25,
                                                                        minWidth: "auto",
                                                                        width: "20px",
                                                                        height: "20px",
                                                                        "&:hover": {
                                                                            backgroundColor: "rgba(255, 255, 255, 0.2)"
                                                                        }
                                                                    }}
                                                                >
                                                                    <Edit sx={{ fontSize: "0.75rem" }} />
                                                                </IconButtonMui>
                                                            </Tooltip>
                                                        )}
                                                    </Box>

                                                    {/* Bottom row: No template indicator */}
                                                    {daysWithoutTemplate.has(format(day, "yyyy-MM-dd")) && !isReadOnly && (
                                                        <Tooltip title={t("shift.noTemplateForDay") || "No template assigned. Click to assign a template."}>
                                                            <Chip
                                                                label="!"
                                                                size="small"
                                                                onClick={async () => handleAssignTemplateToDay(day)}
                                                                sx={{
                                                                    height: "18px",
                                                                    minWidth: "18px",
                                                                    fontSize: "0.7rem",
                                                                    fontWeight: "bold",
                                                                    color: theme.palette.warning.main,
                                                                    backgroundColor: "white",
                                                                    cursor: "pointer",
                                                                    "&:hover": {
                                                                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                                                                        transform: "scale(1.1)"
                                                                    },
                                                                    transition: "all 0.2s ease"
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </Grid>
                                        )
                                    })}
                                </Grid>
                                {/* Row 2: Hour Column Headers */}
                                <Grid container sx={{ width: "100%", position: "sticky", top: 50, zIndex: 1001 }}>
                                    {/* Empty space for employee column (already filled above) */}
                                    <Grid item sx={{
                                        width: `${EMPLOYEE_COLUMN_WIDTH}px`,
                                        minWidth: `${EMPLOYEE_COLUMN_WIDTH}px`,
                                        flexShrink: 0,
                                        position: "sticky",
                                        left: 0,
                                        bgcolor: theme.palette.background.paper,
                                        height: 30
                                    }} />
                                    {/* Hour Column Headers for each day */}
                                    {weekDays.map((day, dayIndex) => {
                                        // READ FROM CACHE
                                        const dayDateString = format(day, "yyyy-MM-dd")
                                        const layout = dailyDataCache.layoutMap[dayDateString]
                                        const columnsToRender = layout ? layout.columns : []

                                        return (
                                            <React.Fragment key={dayIndex}>
                                                {columnsToRender.map((column: any, colIndex: number) => {
                                                    if (column.type === 'collapsed' && column.range) {
                                                        return (
                                                            <Grid key={`collapsed-${dayIndex}-${colIndex}`} item sx={{
                                                                width: `${column.width}px`,
                                                                minWidth: `${column.width}px`,
                                                                flexShrink: 0,
                                                                bgcolor: theme.palette.background.paper
                                                            }}>
                                                                <Box
                                                                    p={0.5}
                                                                    borderBottom={1}
                                                                    borderRight={1}
                                                                    borderColor="divider"
                                                                    textAlign="center"
                                                                    bgcolor={theme.palette.grey[300]}
                                                                    color="text.primary"
                                                                    height={30}
                                                                    sx={{
                                                                        border: `1px dashed ${theme.palette.grey[500]}`
                                                                    }}
                                                                >
                                                                    <Typography variant="caption" fontWeight="bold" fontSize="0.6rem">
                                                                        {String(column.range.startHour).padStart(2, "0")}-{String(column.range.endHour).padStart(2, "0")}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                        )
                                                    } else if (column.type === 'hour' && column.hour !== undefined) {
                                                        return (
                                                            <Grid key={`${dayIndex}-${column.hour}`} item sx={{
                                                                width: `${column.width}px`,
                                                                minWidth: `${column.width}px`,
                                                                flexShrink: 0,
                                                                bgcolor: theme.palette.background.paper
                                                            }}>
                                                                <Box
                                                                    p={0.5}
                                                                    borderBottom={1}
                                                                    borderRight={1}
                                                                    borderColor="divider"
                                                                    textAlign="center"
                                                                    bgcolor={column.hour % 2 === 0 ? theme.palette.primary.main : theme.palette.primary.dark}
                                                                    color="white"
                                                                    height={30}
                                                                >
                                                                    <Typography variant="caption" fontWeight="bold" fontSize="0.65rem">
                                                                        {column.hour.toString().padStart(2, "0")}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                        )
                                                    }
                                                    return null
                                                })}
                                            </React.Fragment>
                                        )
                                    })}
                                </Grid>
                                {/* Row 3: Employee Count per Hour */}
                                <Grid container sx={{ width: "100%", position: "sticky", top: 80, zIndex: 1001 }}>
                                    {/* Label for employee column */}
                                    <Grid item sx={{
                                        width: `${EMPLOYEE_COLUMN_WIDTH}px`,
                                        minWidth: `${EMPLOYEE_COLUMN_WIDTH}px`,
                                        flexShrink: 0,
                                        position: "sticky",
                                        left: 0,
                                        bgcolor: theme.palette.background.paper,
                                        height: 30,
                                        zIndex: 1002
                                    }}>
                                        <Box
                                            p={0.5}
                                            borderBottom={1}
                                            borderRight={1}
                                            borderColor="divider"
                                            textAlign="center"
                                            bgcolor={theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200]}
                                            color="text.primary"
                                            height={30}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Typography variant="caption" fontWeight="bold" fontSize="0.65rem">
                                                {t("shift.employeesOnDuty")}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    {/* Employee count for each day */}
                                    {weekDays.map((day, dayIndex) => {
                                        // Helper function to count employees at a specific time (hour + minutes)
                                        const getEmployeeCountAtTime = (hour: number, minutes: number): number => {
                                            const checkTime = new Date(day)
                                            checkTime.setHours(hour, minutes, 0, 0)

                                            let count = 0

                                            employees.forEach(employee => {
                                                const employeeShifts = getShiftsFromCache(employee.id, day)

                                                // Also check for night shifts from previous day
                                                const prevDay = addDays(day, -1)
                                                const prevDayShifts = getShiftsFromCache(employee.id, prevDay)
                                                const nightShiftsFromPrevDay = prevDayShifts.filter(shift => shift.isNightShift && shift.endHourNextDay !== null)

                                                // Check if employee has an active shift at this time (and not on break)
                                                const hasActiveShift = employeeShifts.some(shift => {
                                                    const shiftStart = shift.startTime
                                                    const shiftEnd = shift.endTime

                                                    // Shift must start before or at checkTime and end after checkTime
                                                    // If shift ends exactly at checkTime, it doesn't count (must go until at least checkTime + 1 minute)
                                                    if (checkTime < shiftStart || checkTime >= shiftEnd) {
                                                        return false
                                                    }

                                                    // Helper function to check if a time is within a break period
                                                    const isTimeInBreak = (breakStart: Date, breakEnd: Date, checkTime: Date): boolean => {
                                                        const breakStartHours = breakStart.getHours()
                                                        const breakStartMinutes = breakStart.getMinutes()
                                                        const breakEndHours = breakEnd.getHours()
                                                        const breakEndMinutes = breakEnd.getMinutes()

                                                        const breakStartOnCheckDay = new Date(checkTime)
                                                        breakStartOnCheckDay.setHours(breakStartHours, breakStartMinutes, 0, 0)

                                                        const breakEndOnCheckDay = new Date(checkTime)
                                                        breakEndOnCheckDay.setHours(breakEndHours, breakEndMinutes, 0, 0)

                                                        // Handle breaks that might span midnight (e.g., 23:00-01:00)
                                                        if (breakEndOnCheckDay < breakStartOnCheckDay) {
                                                            breakEndOnCheckDay.setDate(breakEndOnCheckDay.getDate() + 1)
                                                        }

                                                        return checkTime >= breakStartOnCheckDay && checkTime < breakEndOnCheckDay
                                                    }

                                                    // Check if employee is on main break at this time
                                                    if (shift.breakStartTime && shift.breakEndTime) {
                                                        if (isTimeInBreak(shift.breakStartTime, shift.breakEndTime, checkTime)) {
                                                            return false
                                                        }
                                                    }

                                                    // Check if employee is on any additional break at this time
                                                    if (shift.additionalBreaks && shift.additionalBreaks.length > 0) {
                                                        const isOnAdditionalBreak = (shift.additionalBreaks as Array<{ startTime: Date; endTime: Date }>).some((ab) =>
                                                            isTimeInBreak(ab.startTime, ab.endTime, checkTime)
                                                        )
                                                        if (isOnAdditionalBreak) {
                                                            return false
                                                        }
                                                    }

                                                    return true
                                                })

                                                // Check night shifts from previous day
                                                const hasActiveNightShift = nightShiftsFromPrevDay.some(shift => {
                                                    // The shift started on previous day and continues into current day
                                                    // Use the actual endTime if available, otherwise use endHourNextDay
                                                    let shiftEndOnCurrentDay: Date | null = null

                                                    if (shift.endTime) {
                                                        shiftEndOnCurrentDay = new Date(shift.endTime)
                                                    } else if (shift.endHourNextDay !== null) {
                                                        // Fallback to using endHourNextDay
                                                        shiftEndOnCurrentDay = new Date(day)
                                                        shiftEndOnCurrentDay.setHours(shift.endHourNextDay, 0, 0, 0)

                                                        // If the shift ends at exactly midnight (00:00), it doesn't count
                                                        if (shift.endHourNextDay === 0) {
                                                            return false
                                                        }
                                                    }

                                                    if (!shiftEndOnCurrentDay) {
                                                        return false
                                                    }

                                                    // Shift must end after checkTime (not at or before)
                                                    if (checkTime >= shiftEndOnCurrentDay) {
                                                        return false
                                                    }

                                                    // Helper function to check if a time is within a break period
                                                    const isTimeInBreak = (breakStart: Date, breakEnd: Date, checkTime: Date): boolean => {
                                                        const breakStartHours = breakStart.getHours()
                                                        const breakStartMinutes = breakStart.getMinutes()
                                                        const breakEndHours = breakEnd.getHours()
                                                        const breakEndMinutes = breakEnd.getMinutes()

                                                        const breakStartOnCheckDay = new Date(checkTime)
                                                        breakStartOnCheckDay.setHours(breakStartHours, breakStartMinutes, 0, 0)

                                                        const breakEndOnCheckDay = new Date(checkTime)
                                                        breakEndOnCheckDay.setHours(breakEndHours, breakEndMinutes, 0, 0)

                                                        // Handle breaks that might span midnight (e.g., 23:00-01:00)
                                                        if (breakEndOnCheckDay < breakStartOnCheckDay) {
                                                            breakEndOnCheckDay.setDate(breakEndOnCheckDay.getDate() + 1)
                                                        }

                                                        return checkTime >= breakStartOnCheckDay && checkTime < breakEndOnCheckDay
                                                    }

                                                    // Check if employee is on main break at this time
                                                    if (shift.breakStartTime && shift.breakEndTime) {
                                                        if (isTimeInBreak(shift.breakStartTime, shift.breakEndTime, checkTime)) {
                                                            return false
                                                        }
                                                    }

                                                    // Check if employee is on any additional break at this time
                                                    if (shift.additionalBreaks && shift.additionalBreaks.length > 0) {
                                                        const isOnAdditionalBreak = (shift.additionalBreaks as Array<{ startTime: Date; endTime: Date }>).some((ab) =>
                                                            isTimeInBreak(ab.startTime, ab.endTime, checkTime)
                                                        )
                                                        if (isOnAdditionalBreak) {
                                                            return false
                                                        }
                                                    }

                                                    return true
                                                })

                                                if (hasActiveShift || hasActiveNightShift) {
                                                    count++
                                                }
                                            })

                                            return count
                                        }

                                        // Calculate employee count for each half-hour segment
                                        const employeeCountByHalfHour = new Map<string, number>()

                                        // Initialize all half-hours
                                        for (let hour = 0; hour <= 23; hour++) {
                                            employeeCountByHalfHour.set(`${hour}:00`, getEmployeeCountAtTime(hour, 0))
                                            employeeCountByHalfHour.set(`${hour}:30`, getEmployeeCountAtTime(hour, 30))
                                        }

                                        // Find min and max counts for color intensity
                                        const counts = Array.from(employeeCountByHalfHour.values())
                                        const minCount = counts.length > 0 ? Math.min(...counts) : 0
                                        const maxCount = counts.length > 0 ? Math.max(...counts) : 0

                                        // Helper function to get color intensity based on count
                                        const getColorForCount = (count: number) => {
                                            if (maxCount === 0 || maxCount === minCount) {
                                                // No variation, use neutral color
                                                return theme.palette.mode === 'dark'
                                                    ? theme.palette.grey[800]
                                                    : theme.palette.grey[200]
                                            }

                                            // Calculate intensity (0 to 1)
                                            const intensity = (count - minCount) / (maxCount - minCount)

                                            // Use success color (green) with varying alpha/intensity
                                            const baseColor = theme.palette.success.main

                                            if (theme.palette.mode === 'dark') {
                                                // Dark mode: lighter/more opaque = more intense
                                                return alpha(baseColor, 0.2 + intensity * 0.5)
                                            } else {
                                                // Light mode: more opaque = more intense
                                                return alpha(baseColor, 0.15 + intensity * 0.5)
                                            }
                                        }

                                        // Reuse the same collapsed ranges logic from hour headers
                                        const hoursWithContent = new Set<number>()
                                        employees.forEach(employee => {
                                            const employeeShifts = getShiftsFromCache(employee.id, day)
                                            const employeeEntries = getCalendarFromCache(employee.id, day)

                                            const prevDay = addDays(day, -1)
                                            const prevDayShifts = getShiftsFromCache(employee.id, prevDay)
                                            const nightShiftsFromPrevDay = prevDayShifts.filter(shift => shift.isNightShift && shift.endHourNextDay !== null)

                                            employeeShifts.forEach(shift => {
                                                for (let hour = shift.startHour; hour <= shift.endHour; hour++) {
                                                    hoursWithContent.add(hour)
                                                }
                                            })

                                            nightShiftsFromPrevDay.forEach(shift => {
                                                if (shift.endHourNextDay !== null) {
                                                    for (let hour = 0; hour <= shift.endHourNextDay; hour++) {
                                                        hoursWithContent.add(hour)
                                                    }
                                                }
                                            })

                                            employeeEntries.forEach(entry => {
                                                if (entry.startHour === 0 && entry.endHour === 23) {
                                                    return
                                                }
                                                for (let hour = entry.startHour; hour <= entry.endHour; hour++) {
                                                    hoursWithContent.add(hour)
                                                }
                                            })
                                        })

                                        const collapsedRanges: Array<{ startHour: number; endHour: number }> = []
                                        let rangeStart: number | null = null

                                        for (let hour = 0; hour <= 23; hour++) {
                                            if (!hoursWithContent.has(hour)) {
                                                if (rangeStart === null) {
                                                    rangeStart = hour
                                                }
                                            } else {
                                                if (rangeStart !== null) {
                                                    const rangeLength = hour - rangeStart
                                                    if (rangeLength > 4) {
                                                        collapsedRanges.push({ startHour: rangeStart, endHour: hour - 1 })
                                                    }
                                                    rangeStart = null
                                                }
                                            }
                                        }

                                        if (rangeStart !== null) {
                                            const rangeLength = 24 - rangeStart
                                            if (rangeLength > 4) {
                                                collapsedRanges.push({ startHour: rangeStart, endHour: 23 })
                                            }
                                        }

                                        // Build columns with half-hourly segments where needed
                                        const columnsToRender: Array<{
                                            type: 'hour' | 'halfHour' | 'collapsed';
                                            hour?: number;
                                            halfHour?: 'first' | 'second';
                                            range?: { startHour: number; endHour: number };
                                            width: number
                                        }> = []

                                        for (let hour = 0; hour <= 23; hour++) {
                                            const collapsedRange = collapsedRanges.find(range =>
                                                hour >= range.startHour && hour <= range.endHour
                                            )

                                            if (collapsedRange && collapsedRange.startHour === hour) {
                                                const rangeWidth = 2.5 * HOUR_COLUMN_WIDTH
                                                columnsToRender.push({
                                                    type: 'collapsed',
                                                    range: collapsedRange,
                                                    width: rangeWidth
                                                })
                                                hour = collapsedRange.endHour
                                            } else if (!collapsedRange) {
                                                // Check if counts differ between first and second half of the hour
                                                const countAt00 = employeeCountByHalfHour.get(`${hour}:00`) || 0
                                                const countAt30 = employeeCountByHalfHour.get(`${hour}:30`) || 0

                                                if (countAt00 === countAt30) {
                                                    // Same count for whole hour - render one box
                                                    columnsToRender.push({
                                                        type: 'hour',
                                                        hour,
                                                        width: HOUR_COLUMN_WIDTH
                                                    })
                                                } else {
                                                    // Different counts - render two half-hour boxes
                                                    const halfHourWidth = HOUR_COLUMN_WIDTH / 2
                                                    columnsToRender.push({
                                                        type: 'halfHour',
                                                        hour,
                                                        halfHour: 'first',
                                                        width: halfHourWidth
                                                    })
                                                    columnsToRender.push({
                                                        type: 'halfHour',
                                                        hour,
                                                        halfHour: 'second',
                                                        width: halfHourWidth
                                                    })
                                                }
                                            }
                                        }

                                        return (
                                            <React.Fragment key={dayIndex}>
                                                {columnsToRender.map((column: any, colIndex: number) => {
                                                    if (column.type === 'collapsed' && column.range) {
                                                        // For collapsed ranges, calculate average of all half-hour counts
                                                        let totalCount = 0
                                                        let halfHourCount = 0
                                                        for (let hour = column.range.startHour; hour <= column.range.endHour; hour++) {
                                                            totalCount += employeeCountByHalfHour.get(`${hour}:00`) || 0
                                                            totalCount += employeeCountByHalfHour.get(`${hour}:30`) || 0
                                                            halfHourCount += 2
                                                        }
                                                        const avgCount = halfHourCount > 0 ? Math.round(totalCount / halfHourCount) : 0

                                                        return (
                                                            <Grid key={`collapsed-count-${dayIndex}-${colIndex}`} item sx={{
                                                                width: `${column.width}px`,
                                                                minWidth: `${column.width}px`,
                                                                flexShrink: 0,
                                                                bgcolor: theme.palette.background.paper
                                                            }}>
                                                                <Box
                                                                    p={0.5}
                                                                    borderBottom={1}
                                                                    borderRight={1}
                                                                    borderColor="divider"
                                                                    textAlign="center"
                                                                    bgcolor={getColorForCount(avgCount)}
                                                                    color="text.primary"
                                                                    height={30}
                                                                    sx={{
                                                                        border: `1px dashed ${theme.palette.grey[500]}`
                                                                    }}
                                                                >
                                                                    <Typography variant="caption" fontWeight="bold" fontSize="0.65rem">
                                                                        {avgCount}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                        )
                                                    } else if (column.type === 'hour' && column.hour !== undefined) {
                                                        // Use count at :00 (same as :30 since we only render this if they're equal)
                                                        const count = employeeCountByHalfHour.get(`${column.hour}:00`) || 0

                                                        return (
                                                            <Grid key={`count-${dayIndex}-${column.hour}`} item sx={{
                                                                width: `${column.width}px`,
                                                                minWidth: `${column.width}px`,
                                                                flexShrink: 0,
                                                                bgcolor: theme.palette.background.paper
                                                            }}>
                                                                <Box
                                                                    p={0.5}
                                                                    borderBottom={1}
                                                                    borderRight={1}
                                                                    borderColor="divider"
                                                                    textAlign="center"
                                                                    bgcolor={getColorForCount(count)}
                                                                    color="text.primary"
                                                                    height={30}
                                                                >
                                                                    <Typography variant="caption" fontWeight="bold" fontSize="0.65rem">
                                                                        {count}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                        )
                                                    } else if (column.type === 'halfHour' && column.hour !== undefined && column.halfHour) {
                                                        // Get count for the specific half-hour
                                                        const timeKey = column.halfHour === 'first' ? `${column.hour}:00` : `${column.hour}:30`
                                                        const count = employeeCountByHalfHour.get(timeKey) || 0

                                                        return (
                                                            <Grid key={`count-${dayIndex}-${column.hour}-${column.halfHour}`} item sx={{
                                                                width: `${column.width}px`,
                                                                minWidth: `${column.width}px`,
                                                                flexShrink: 0,
                                                                bgcolor: theme.palette.background.paper
                                                            }}>
                                                                <Box
                                                                    p={0.5}
                                                                    borderBottom={1}
                                                                    borderRight={column.halfHour === 'second' ? 1 : 0}
                                                                    borderColor="divider"
                                                                    textAlign="center"
                                                                    bgcolor={getColorForCount(count)}
                                                                    color="text.primary"
                                                                    height={30}
                                                                    sx={{
                                                                        borderLeft: column.halfHour === 'first' ? 1 : 0,
                                                                        borderLeftColor: 'divider'
                                                                    }}
                                                                >
                                                                    <Typography variant="caption" fontWeight="bold" fontSize="0.65rem">
                                                                        {count}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                        )
                                                    }
                                                    return null
                                                })}
                                            </React.Fragment>
                                        )
                                    })}
                                </Grid>
                                {/* Row 4: Required Employees per Hour (from Template) */}
                                {!hideEmployeesRequiredRow && (
                                    <Grid container sx={{ width: "100%", position: "sticky", top: 110, zIndex: 1001 }}>
                                        {/* Label for employee column */}
                                        <Grid item sx={{
                                            width: `${EMPLOYEE_COLUMN_WIDTH}px`,
                                            minWidth: `${EMPLOYEE_COLUMN_WIDTH}px`,
                                            flexShrink: 0,
                                            position: "sticky",
                                            left: 0,
                                            bgcolor: theme.palette.background.paper,
                                            height: 30,
                                            zIndex: 1002
                                        }}>
                                            <Box
                                                p={0.5}
                                                borderBottom={1}
                                                borderRight={1}
                                                borderColor="divider"
                                                textAlign="center"
                                                bgcolor={theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200]}
                                                color="text.primary"
                                                height={30}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <Typography variant="caption" fontWeight="bold" fontSize="0.65rem">
                                                    {t("shift.employeesRequired") || "Required"}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        {/* Required employee count for each day */}
                                        {weekDays.map((day, dayIndex) => {
                                            // Calculate required employees from template for each hour
                                            const requiredCountByHour = new Map<number, number>()

                                            // Initialize all hours to 0
                                            for (let hour = 0; hour <= 23; hour++) {
                                                requiredCountByHour.set(hour, 0)
                                            }

                                            // Get template for this day
                                            const dayDateString = format(day, "yyyy-MM-dd")
                                            const dayTemplate = dayTemplatesCache[dayDateString]

                                            if (dayTemplate) {
                                                // Helper to parse time from template shift detail
                                                const parseTemplateShiftTime = (shiftDetail: ShiftDetailEntity, shift: ShiftEntity, date: Date, isStart: boolean): Date => {
                                                    const timeValue = isStart ? shiftDetail.startTime : shiftDetail.endTime

                                                    // If shift detail has its own time, use it
                                                    if (timeValue) {
                                                        const timeDate = new Date(date)
                                                        let timeStr: string

                                                        if (typeof timeValue === 'string') {
                                                            // Check if it's already in HH:mm format or ISO string
                                                            if (timeValue.includes('T') || timeValue.includes('Z')) {
                                                                // ISO string - extract time
                                                                const dateObj = new Date(timeValue)
                                                                const hours = dateObj.getUTCHours()
                                                                const minutes = dateObj.getUTCMinutes()
                                                                timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
                                                            } else {
                                                                // Assume HH:mm format
                                                                timeStr = timeValue
                                                            }
                                                        } else {
                                                            // It's a Date object - extract time part
                                                            const dateObj = new Date(timeValue)
                                                            const hours = dateObj.getUTCHours()
                                                            const minutes = dateObj.getUTCMinutes()
                                                            timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
                                                        }

                                                        const [hours, minutes] = timeStr.split(':').map(Number)
                                                        timeDate.setHours(hours || 0, minutes || 0, 0, 0)
                                                        return timeDate
                                                    }

                                                    // Fallback to shift template time
                                                    if (shift) {
                                                        const shiftTimeValue = isStart ? shift.startTime : shift.endTime
                                                        if (shiftTimeValue) {
                                                            const timeDate = new Date(date)
                                                            let timeStr: string

                                                            if (typeof shiftTimeValue === 'string') {
                                                                if (shiftTimeValue.includes('T') || shiftTimeValue.includes('Z')) {
                                                                    const dateObj = new Date(shiftTimeValue)
                                                                    const hours = dateObj.getUTCHours()
                                                                    const minutes = dateObj.getUTCMinutes()
                                                                    timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
                                                                } else {
                                                                    timeStr = shiftTimeValue
                                                                }
                                                            } else {
                                                                const dateObj = new Date(shiftTimeValue)
                                                                const hours = dateObj.getUTCHours()
                                                                const minutes = dateObj.getUTCMinutes()
                                                                timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
                                                            }

                                                            const [hours, minutes] = timeStr.split(':').map(Number)
                                                            timeDate.setHours(hours || 0, minutes || 0, 0, 0)
                                                            return timeDate
                                                        }
                                                    }

                                                    // Default fallback
                                                    const timeDate = new Date(date)
                                                    timeDate.setHours(isStart ? 0 : 23, isStart ? 0 : 59, 0, 0)
                                                    return timeDate
                                                }

                                                // For each shift in the template
                                                dayTemplate.shifts.forEach(shift => {
                                                    // For each shift detail (position) in the shift
                                                    shift.shiftDetails?.forEach(shiftDetail => {
                                                        try {
                                                            // Parse start and end times using template-specific helper
                                                            const startTime = parseTemplateShiftTime(shiftDetail, shift, day, true)
                                                            let endTime = parseTemplateShiftTime(shiftDetail, shift, day, false)

                                                            // Handle night shift (end time on next day)
                                                            if (endTime <= startTime) {
                                                                endTime = addDays(endTime, 1)
                                                            }

                                                            // Count hours covered by this shift detail
                                                            const startHour = getStartHourColumn(startTime)
                                                            let endHour = getEndHourColumn(endTime)

                                                            // If end time is on next day, endHour on current day is 23
                                                            const endDay = new Date(endTime)
                                                            endDay.setHours(0, 0, 0, 0)
                                                            const startDay = new Date(startTime)
                                                            startDay.setHours(0, 0, 0, 0)
                                                            const isNightShift = !isSameDay(startDay, endDay)

                                                            if (isNightShift) {
                                                                endHour = 23 // On the start day, night shift ends at hour 23
                                                            }

                                                            // Only count if we have valid hours
                                                            if (startHour >= 0 && startHour <= 23 && endHour >= 0 && endHour <= 23 && startHour <= endHour) {
                                                                // Increment count for each hour this shift detail covers
                                                                for (let hour = startHour; hour <= endHour; hour++) {
                                                                    const currentCount = requiredCountByHour.get(hour) || 0
                                                                    requiredCountByHour.set(hour, currentCount + 1)
                                                                }
                                                            }
                                                        } catch (error) {
                                                            // Skip this shift detail if there's an error parsing times
                                                            console.warn('Error parsing shift detail times:', error, { shiftDetail, shift })
                                                        }
                                                    })
                                                })
                                            }

                                            // Find min and max counts for color intensity
                                            const counts = Array.from(requiredCountByHour.values())
                                            const minCount = counts.length > 0 ? Math.min(...counts) : 0
                                            const maxCount = counts.length > 0 ? Math.max(...counts) : 0

                                            // Helper function to get color intensity based on count (use info color for required)
                                            const getColorForRequiredCount = (count: number) => {
                                                if (maxCount === 0 || maxCount === minCount) {
                                                    // No variation, use neutral color
                                                    return theme.palette.mode === 'dark'
                                                        ? theme.palette.grey[800]
                                                        : theme.palette.grey[200]
                                                }

                                                // Calculate intensity (0 to 1)
                                                const intensity = (count - minCount) / (maxCount - minCount)

                                                // Use info color (blue) with varying alpha/intensity
                                                const baseColor = theme.palette.info.main

                                                if (theme.palette.mode === 'dark') {
                                                    // Dark mode: lighter/more opaque = more intense
                                                    return alpha(baseColor, 0.2 + intensity * 0.5)
                                                } else {
                                                    // Light mode: more opaque = more intense
                                                    return alpha(baseColor, 0.15 + intensity * 0.5)
                                                }
                                            }

                                            // Reuse the same collapsed ranges logic from hour headers
                                            const hoursWithContent = new Set<number>()
                                            employees.forEach(employee => {
                                                const employeeShifts = getShiftsFromCache(employee.id, day)
                                                const employeeEntries = getCalendarFromCache(employee.id, day)

                                                const prevDay = addDays(day, -1)
                                                const prevDayShifts = getShiftsFromCache(employee.id, prevDay)
                                                const nightShiftsFromPrevDay = prevDayShifts.filter(shift => shift.isNightShift && shift.endHourNextDay !== null)

                                                employeeShifts.forEach(shift => {
                                                    for (let hour = shift.startHour; hour <= shift.endHour; hour++) {
                                                        hoursWithContent.add(hour)
                                                    }
                                                })

                                                nightShiftsFromPrevDay.forEach(shift => {
                                                    if (shift.endHourNextDay !== null) {
                                                        for (let hour = 0; hour <= shift.endHourNextDay; hour++) {
                                                            hoursWithContent.add(hour)
                                                        }
                                                    }
                                                })

                                                employeeEntries.forEach(entry => {
                                                    if (entry.startHour === 0 && entry.endHour === 23) {
                                                        return
                                                    }
                                                    for (let hour = entry.startHour; hour <= entry.endHour; hour++) {
                                                        hoursWithContent.add(hour)
                                                    }
                                                })
                                            })

                                            const collapsedRanges: Array<{ startHour: number; endHour: number }> = []
                                            let rangeStart: number | null = null

                                            for (let hour = 0; hour <= 23; hour++) {
                                                if (!hoursWithContent.has(hour)) {
                                                    if (rangeStart === null) {
                                                        rangeStart = hour
                                                    }
                                                } else {
                                                    if (rangeStart !== null) {
                                                        const rangeLength = hour - rangeStart
                                                        if (rangeLength > 4) {
                                                            collapsedRanges.push({ startHour: rangeStart, endHour: hour - 1 })
                                                        }
                                                        rangeStart = null
                                                    }
                                                }
                                            }

                                            if (rangeStart !== null) {
                                                const rangeLength = 24 - rangeStart
                                                if (rangeLength > 4) {
                                                    collapsedRanges.push({ startHour: rangeStart, endHour: 23 })
                                                }
                                            }

                                            const columnsToRender: Array<{ type: 'hour' | 'collapsed'; hour?: number; range?: { startHour: number; endHour: number }; width: number }> = []

                                            for (let hour = 0; hour <= 23; hour++) {
                                                const collapsedRange = collapsedRanges.find(range =>
                                                    hour >= range.startHour && hour <= range.endHour
                                                )

                                                if (collapsedRange && collapsedRange.startHour === hour) {
                                                    const rangeWidth = 2.5 * HOUR_COLUMN_WIDTH
                                                    columnsToRender.push({
                                                        type: 'collapsed',
                                                        range: collapsedRange,
                                                        width: rangeWidth
                                                    })
                                                    hour = collapsedRange.endHour
                                                } else if (!collapsedRange) {
                                                    columnsToRender.push({
                                                        type: 'hour',
                                                        hour,
                                                        width: HOUR_COLUMN_WIDTH
                                                    })
                                                }
                                            }

                                            return (
                                                <React.Fragment key={dayIndex}>
                                                    {columnsToRender.map((column: any, colIndex: number) => {
                                                        if (column.type === 'collapsed' && column.range) {
                                                            // For collapsed ranges, find maximum count and build breakdown for tooltip
                                                            let maxCount = 0
                                                            const hourBreakdown: Array<{ hour: number; count: number }> = []

                                                            for (let hour = column.range.startHour; hour <= column.range.endHour; hour++) {
                                                                const count = requiredCountByHour.get(hour) || 0
                                                                maxCount = Math.max(maxCount, count)
                                                                if (count > 0) {
                                                                    hourBreakdown.push({ hour, count })
                                                                }
                                                            }

                                                            // Group consecutive hours with same count for cleaner tooltip
                                                            const groupedBreakdown: Array<{ startHour: number; endHour: number; count: number }> = []
                                                            if (hourBreakdown.length > 0) {
                                                                let currentGroup = { startHour: hourBreakdown[0].hour, endHour: hourBreakdown[0].hour, count: hourBreakdown[0].count }

                                                                for (let i = 1; i < hourBreakdown.length; i++) {
                                                                    if (hourBreakdown[i].hour === currentGroup.endHour + 1 && hourBreakdown[i].count === currentGroup.count) {
                                                                        currentGroup.endHour = hourBreakdown[i].hour
                                                                    } else {
                                                                        groupedBreakdown.push(currentGroup)
                                                                        currentGroup = { startHour: hourBreakdown[i].hour, endHour: hourBreakdown[i].hour, count: hourBreakdown[i].count }
                                                                    }
                                                                }
                                                                groupedBreakdown.push(currentGroup)
                                                            }

                                                            const tooltipContent = groupedBreakdown.length > 0 ? (
                                                                <Box>
                                                                    <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                                                                        {t("shift.requiredBreakdown") || "Required employees by hour:"}
                                                                    </Typography>
                                                                    {groupedBreakdown.map((group, idx) => (
                                                                        <Typography key={idx} variant="caption" display="block">
                                                                            {String(group.startHour).padStart(2, "0")}:00 - {String(group.endHour).padStart(2, "0")}:00: {group.count}
                                                                        </Typography>
                                                                    ))}
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="caption">
                                                                    {t("shift.noEmployeesRequired") || "No employees required"}
                                                                </Typography>
                                                            )

                                                            return (
                                                                <Grid key={`collapsed-required-${dayIndex}-${colIndex}`} item sx={{
                                                                    width: `${column.width}px`,
                                                                    minWidth: `${column.width}px`,
                                                                    flexShrink: 0,
                                                                    bgcolor: theme.palette.background.paper
                                                                }}>
                                                                    <Tooltip title={tooltipContent} arrow>
                                                                        <Box
                                                                            p={0.5}
                                                                            borderBottom={1}
                                                                            borderRight={1}
                                                                            borderColor="divider"
                                                                            textAlign="center"
                                                                            bgcolor={getColorForRequiredCount(maxCount)}
                                                                            color="text.primary"
                                                                            height={30}
                                                                            sx={{
                                                                                border: `1px dashed ${theme.palette.grey[500]}`,
                                                                                cursor: "help"
                                                                            }}
                                                                        >
                                                                            <Typography variant="caption" fontWeight="bold" fontSize="0.65rem">
                                                                                {maxCount}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Tooltip>
                                                                </Grid>
                                                            )
                                                        } else if (column.type === 'hour' && column.hour !== undefined) {
                                                            const count = requiredCountByHour.get(column.hour) || 0

                                                            return (
                                                                <Grid key={`required-${dayIndex}-${column.hour}`} item sx={{
                                                                    width: `${column.width}px`,
                                                                    minWidth: `${column.width}px`,
                                                                    flexShrink: 0,
                                                                    bgcolor: theme.palette.background.paper
                                                                }}>
                                                                    <Box
                                                                        p={0.5}
                                                                        borderBottom={1}
                                                                        borderRight={1}
                                                                        borderColor="divider"
                                                                        textAlign="center"
                                                                        bgcolor={getColorForRequiredCount(count)}
                                                                        color="text.primary"
                                                                        height={30}
                                                                    >
                                                                        <Typography variant="caption" fontWeight="bold" fontSize="0.65rem">
                                                                            {count}
                                                                        </Typography>
                                                                    </Box>
                                                                </Grid>
                                                            )
                                                        }
                                                        return null
                                                    })}
                                                </React.Fragment>
                                            )
                                        })}
                                    </Grid>
                                )}
                                {/* Employee Rows */}
                                {employees.filter(emp => !hiddenEmployeeIds.has(emp.id)).map((employee, empIndex) => {
                                    // See compact view above: normalize UTC-midnight contract
                                    // dates to local midnight so the boundary day compares right.
                                    const contractEndDate = employee.employeesToSeasons?.[0]?.contract?.endedOn
                                        ? startOfDay(new Date(employee.employeesToSeasons[0].contract.endedOn))
                                        : null
                                    const contractStartDate = employee.employeesToSeasons?.[0]?.contract?.startedOn
                                        ? startOfDay(new Date(employee.employeesToSeasons[0].contract.startedOn))
                                        : null
                                    return (
                                        <Grid key={employee.id} container>
                                            {/* Employee Tile */}
                                            <Grid item sx={{
                                                width: `${EMPLOYEE_COLUMN_WIDTH}px`,
                                                minWidth: `${EMPLOYEE_COLUMN_WIDTH}px`,
                                                flexShrink: 0,
                                                position: "sticky",
                                                left: 0,
                                                zIndex: 1000,
                                                bgcolor: theme.palette.background.paper,
                                                height: EMPLOYEE_ROW_HEIGHT,
                                                minHeight: EMPLOYEE_ROW_HEIGHT,
                                                p: 0.25
                                            }}>
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        p: 0.75,
                                                        borderRadius: 1.5,
                                                        height: "100%",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "flex-start",
                                                        alignItems: "flex-start",
                                                        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                                                        border: `1px solid ${theme.palette.divider}`,
                                                        borderRight: `2px solid ${theme.palette.divider}`,
                                                        transition: "all 0.3s ease",
                                                        position: "sticky",
                                                        top: 0,
                                                        "&:hover": {
                                                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                                            borderColor: theme.palette.primary.main
                                                        }
                                                    }}
                                                >
                                                    {/* Line 1: Avatar + Name + Work Position + Hours */}
                                                    <Box display="flex" alignItems="center" gap={1} mb={0.2} width="100%">
                                                        <Avatar
                                                            src={getEmployeeImageUrl(employee.filePath)}
                                                            sx={{
                                                                width: 32,
                                                                height: 32,
                                                                border: `2px solid ${theme.palette.primary.main}`,
                                                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                                                fontSize: "0.75rem",
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            {!employee.filePath && employee.firstName?.[0]}{!employee.filePath && employee.lastName?.[0]}
                                                        </Avatar>
                                                        <Box flex={1} minWidth={0} display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                                                            <Typography variant="body2" fontWeight="bold" color="text.primary" sx={{ fontSize: "0.85rem", lineHeight: 1.2, whiteSpace: "nowrap" }}>
                                                                {`${employee.firstName} ${employee.lastName}`}
                                                            </Typography>
                                                            <Box display="flex" alignItems="center" gap={0.4}>
                                                                <Work sx={{ fontSize: 11, color: theme.palette.text.secondary }} />
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", lineHeight: 1.2, whiteSpace: "nowrap" }}>
                                                                    {employee.employeesToDepartments?.[0]?.workPosition?.name || t("shift.noPosition")}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <Box display="flex" alignItems="center" gap={0.5} flexShrink={0} ml="auto">
                                                            <AccessTime sx={{ fontSize: 11, color: theme.palette.primary.main }} />
                                                            <Typography variant="body2" fontWeight="bold" color="text.primary" sx={{ fontSize: "0.75rem", lineHeight: 1.2, whiteSpace: "nowrap" }}>
                                                                {workedHours[employee.id] || 0}h
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", lineHeight: 1.2, whiteSpace: "nowrap" }}>
                                                                / {(() => {
                                                                    const daysInRange = eachDayOfInterval({ start: selectedDay, end: selectedEndDay }).length
                                                                    const weeksInRange = daysInRange / 7
                                                                    const employeeWeeklyHours = weeklyWorkHours[employee.id] ?? defaultWeeklyHours
                                                                    const workPercentage = (employee.employeesToSeasons?.[0]?.contract?.workPercentage ?? 100) / 100
                                                                    const expectedHours = employeeWeeklyHours * weeksInRange * workPercentage
                                                                    return +expectedHours.toFixed(2)
                                                                })()}h
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    {/* Line 2: Status chips */}
                                                    <Box display="flex" gap={0.4} flexWrap="wrap" alignItems="center">
                                                        {employee.employeesToSeasons?.[0]?.status?.name && (
                                                            <Chip
                                                                label={employee.employeesToSeasons[0].status.name}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{ fontSize: "0.55rem", height: "18px", "& .MuiChip-label": { px: 0.5 } }}
                                                            />
                                                        )}
                                                        {employee.employeesToSeasons?.[0]?.contract?.isPartTime && (
                                                            <Chip
                                                                label={t("shift.partTime")}
                                                                size="small"
                                                                color="secondary"
                                                                variant="outlined"
                                                                sx={{ fontSize: "0.55rem", height: "18px", "& .MuiChip-label": { px: 0.5 } }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                            {/* Day Columns with Hour Columns */}
                                            {weekDays.map((day, dayIndex) => {
                                                const positionedShifts = getShiftsFromCache(employee.id, day)
                                                const calendarEntriesForDay = getCalendarFromCache(employee.id, day)

                                                // READ FROM CACHE
                                                const dayDateString = format(day, "yyyy-MM-dd")
                                                const layout = dailyDataCache.layoutMap[dayDateString]
                                                const columnsToRenderForDay = layout ? layout.columns : []
                                                const dayWidthForNightShift = layout ? layout.totalWidth : 0

                                                // Helper functions to calculate position for night shift extension
                                                const getLeftPositionForNightShift = (hour: number, offsetPercent: number = 0): number => {
                                                    let position = 0
                                                    for (const col of columnsToRenderForDay) {
                                                        if (col.type === 'hour' && col.hour !== undefined) {
                                                            if (col.hour === hour) {
                                                                position += (offsetPercent / 100) * col.width
                                                                return position
                                                            } else if (col.hour < hour) {
                                                                position += col.width
                                                            }
                                                        } else if (col.type === 'collapsed' && col.range) {
                                                            if (hour >= col.range.startHour && hour <= col.range.endHour) {
                                                                const hoursInRange = hour - col.range.startHour
                                                                const totalHoursInRange = col.range.endHour - col.range.startHour + 1
                                                                position += (hoursInRange / totalHoursInRange) * col.width
                                                                position += (offsetPercent / 100) * (col.width / totalHoursInRange)
                                                                return position
                                                            } else if (col.range.endHour < hour) {
                                                                position += col.width
                                                            }
                                                        }
                                                    }
                                                    return position
                                                }

                                                const getRightPositionForNightShift = (hour: number, offsetPercent: number = 0): number => {
                                                    return getLeftPositionForNightShift(hour, offsetPercent)
                                                }

                                                // Get next day layout for calculating night shift extension width
                                                const nextDay = dayIndex < weekDays.length - 1 ? weekDays[dayIndex + 1] : null
                                                const nextDayDateString = nextDay ? format(nextDay, "yyyy-MM-dd") : null
                                                const nextDayLayout = nextDayDateString ? dailyDataCache.layoutMap[nextDayDateString] : null
                                                const nextDayColumns = nextDayLayout ? nextDayLayout.columns : []

                                                // Helper to calculate position on next day
                                                const getNextDayPosition = (hour: number, offsetPercent: number = 0): number => {
                                                    if (!nextDayColumns.length) return hour * HOUR_COLUMN_WIDTH
                                                    let position = 0
                                                    for (const col of nextDayColumns) {
                                                        if (col.type === 'hour' && col.hour !== undefined) {
                                                            if (col.hour === hour) {
                                                                position += (offsetPercent / 100) * col.width
                                                                return position
                                                            } else if (col.hour < hour) {
                                                                position += col.width
                                                            }
                                                        } else if (col.type === 'collapsed' && col.range) {
                                                            if (hour >= col.range.startHour && hour <= col.range.endHour) {
                                                                const hoursInRange = hour - col.range.startHour
                                                                const totalHoursInRange = col.range.endHour - col.range.startHour + 1
                                                                position += (hoursInRange / totalHoursInRange) * col.width
                                                                position += (offsetPercent / 100) * (col.width / totalHoursInRange)
                                                                return position
                                                            } else if (col.range.endHour < hour) {
                                                                position += col.width
                                                            }
                                                        }
                                                    }
                                                    return position
                                                }

                                                // For night shifts on the start day, extend them beyond the day to span into next day
                                                const adjustedShifts = positionedShifts.map(shift => {
                                                    if (shift.isNightShift && shift.endHourNextDay !== null && nextDay) {
                                                        // Calculate position on current day
                                                        const left = getLeftPositionForNightShift(shift.startHour, shift.leftOffset)
                                                        const rightOnCurrentDay = getRightPositionForNightShift(23, 100) // End of current day

                                                        // Calculate width needed for next day portion (from hour 0 to endHourNextDay)
                                                        // Use next day's actual column structure
                                                        const nextDayStartPosition = 0 // Start of next day (hour 0)
                                                        const nextDayEndPosition = getNextDayPosition(shift.endHourNextDay, shift.rightOffset)
                                                        const nextDayPortion = nextDayEndPosition - nextDayStartPosition

                                                        // Total width spans current day + next day portion
                                                        const totalWidth = (rightOnCurrentDay - left) + nextDayPortion

                                                        return {
                                                            ...shift,
                                                            endHour: 23, // Render to end of current day visually
                                                            rightOffset: 100,
                                                            // Store extension info for rendering
                                                            extendsToNextDay: true,
                                                            nextDayExtensionWidth: nextDayPortion,
                                                            totalWidth,
                                                            nextDayEndHour: shift.endHourNextDay,
                                                            nextDayRightOffset: shift.rightOffset
                                                        }
                                                    }
                                                    return shift
                                                })

                                                // Filter out continuation shifts from previous day - we don't want separate boxes
                                                // Night shifts are rendered as a single box on the start day that extends into next day
                                                const allShiftsForDay = adjustedShifts.filter(shift => !shift.isNightShiftContinuation)

                                                const isReadOnlyDay = isDateReadOnly(day)
                                                const isContractEnded = contractEndDate ? day > contractEndDate : false
                                                const isBeforeContractStart = contractStartDate ? day < contractStartDate : false
                                                const isOutsideContract = isContractEnded || isBeforeContractStart
                                                return (
                                                    <Grid
                                                        key={dayIndex}
                                                        item
                                                        sx={{
                                                            width: `${dayWidthForNightShift}px`,
                                                            minWidth: `${dayWidthForNightShift}px`,
                                                            flexShrink: 0,
                                                            height: EMPLOYEE_ROW_HEIGHT,
                                                            minHeight: EMPLOYEE_ROW_HEIGHT,
                                                            position: "relative",
                                                            // Allow overflow so night shift cards can extend into next day visually
                                                            overflow: "visible",
                                                            opacity: isReadOnlyDay ? 0.6 : 1,
                                                            '&::after': isReadOnlyDay ? {
                                                                content: '""',
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                bgcolor: alpha(theme.palette.common.black, 0.05),
                                                                pointerEvents: 'none',
                                                                zIndex: 1
                                                            } : {}
                                                        }}
                                                    >
                                                        {/* Manager note flag */}
                                                        {canManageShiftNotes && hasEmployeeDayNotes(employee.id, day) && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                top: 2,
                                                                right: 2,
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: '50%',
                                                                bgcolor: theme.palette.warning.main,
                                                                border: `1px solid ${theme.palette.background.paper}`,
                                                                zIndex: 51,
                                                                pointerEvents: 'none'
                                                            }} />
                                                        )}
                                                        {/* Contract ended overlay */}
                                                        {isOutsideContract && (
                                                            <Tooltip title={isContractEnded ? t("shift.contractEnded") : t("shift.contractNotStarted")} arrow>
                                                                <Box sx={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    right: 0,
                                                                    bottom: 0,
                                                                    zIndex: 50,
                                                                    background: `repeating-linear-gradient(
                                                                        -45deg,
                                                                        transparent,
                                                                        transparent 6px,
                                                                        ${alpha(theme.palette.grey[400], 0.15)} 6px,
                                                                        ${alpha(theme.palette.grey[400], 0.15)} 12px
                                                                    )`,
                                                                    bgcolor: alpha(theme.palette.grey[300], 0.25),
                                                                    pointerEvents: 'auto',
                                                                    cursor: 'not-allowed',
                                                                }} />
                                                            </Tooltip>
                                                        )}
                                                        {/* Hour column backgrounds - match header structure exactly */}
                                                        <Box sx={{ display: "flex", height: "100%", width: "100%", position: "relative" }}>
                                                            {columnsToRenderForDay.map((column: any, colIndex: number) => {
                                                                if (column.type === 'collapsed' && column.range) {
                                                                    return (
                                                                        <Box
                                                                            key={`collapsed-bg-${colIndex}`}
                                                                            sx={{
                                                                                width: `${column.width}px`,
                                                                                height: "100%",
                                                                                borderRight: 1,
                                                                                borderColor: "divider",
                                                                                bgcolor: theme.palette.grey[100],
                                                                                position: "relative"
                                                                            }}
                                                                        />
                                                                    )
                                                                } else if (column.type === 'hour' && column.hour !== undefined) {
                                                                    return (
                                                                        <Box
                                                                            key={`hour-bg-${column.hour}`}
                                                                            sx={{
                                                                                width: `${column.width}px`,
                                                                                height: "100%",
                                                                                borderRight: 1,
                                                                                borderColor: "divider",
                                                                                bgcolor: column.hour % 2 === 0 ? theme.palette.background.paper : theme.palette.grey[50],
                                                                                position: "relative"
                                                                            }}
                                                                        >
                                                                            {/* Middle of hour indicator - very light dashed line */}
                                                                            <Box
                                                                                sx={{
                                                                                    position: "absolute",
                                                                                    left: "50%",
                                                                                    top: 0,
                                                                                    bottom: 0,
                                                                                    width: "1px",
                                                                                    borderLeft: "1px dashed",
                                                                                    borderColor: theme.palette.mode === 'dark'
                                                                                        ? theme.palette.grey[700]
                                                                                        : theme.palette.grey[300],
                                                                                    opacity: 0.3,
                                                                                    pointerEvents: "none"
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    )
                                                                }
                                                                return null
                                                            })}
                                                        </Box>

                                                        {/* Current Time Indicator - Red Line */}
                                                        {(() => {
                                                            // Check if current time is within this day
                                                            const currentTimeDate = new Date(currentTime)
                                                            const dayDate = new Date(day)
                                                            dayDate.setHours(0, 0, 0, 0)
                                                            const currentDay = new Date(currentTimeDate)
                                                            currentDay.setHours(0, 0, 0, 0)

                                                            const isCurrentTimeInDay = isSameDay(currentDay, dayDate)

                                                            if (!isCurrentTimeInDay) {
                                                                return null
                                                            }

                                                            // Calculate position for current time
                                                            const currentHour = currentTimeDate.getHours()
                                                            const currentMinute = currentTimeDate.getMinutes()
                                                            const currentOffsetPercent = (currentMinute / 60) * 100

                                                            const getCurrentTimePosition = (): number => {
                                                                let position = 0
                                                                for (const col of columnsToRenderForDay) {
                                                                    if (col.type === 'hour' && col.hour !== undefined) {
                                                                        if (col.hour === currentHour) {
                                                                            position += (currentOffsetPercent / 100) * col.width
                                                                            return position
                                                                        } else if (col.hour < currentHour) {
                                                                            position += col.width
                                                                        }
                                                                    } else if (col.type === 'collapsed' && col.range) {
                                                                        if (currentHour >= col.range.startHour && currentHour <= col.range.endHour) {
                                                                            const hoursInRange = currentHour - col.range.startHour
                                                                            const totalHoursInRange = col.range.endHour - col.range.startHour + 1
                                                                            position += (hoursInRange / totalHoursInRange) * col.width
                                                                            position += (currentOffsetPercent / 100) * (col.width / totalHoursInRange)
                                                                            return position
                                                                        } else if (col.range.endHour < currentHour) {
                                                                            position += col.width
                                                                        }
                                                                    }
                                                                }
                                                                return position
                                                            }

                                                            const currentTimePosition = getCurrentTimePosition()

                                                            return (
                                                                <Box
                                                                    sx={{
                                                                        position: "absolute",
                                                                        left: `${currentTimePosition}px`,
                                                                        top: 0,
                                                                        bottom: 0,
                                                                        width: "2px",
                                                                        backgroundColor: theme.palette.error.main,
                                                                        zIndex: 100,
                                                                        pointerEvents: "none",
                                                                        boxShadow: `0 0 4px ${theme.palette.error.main}`
                                                                    }}
                                                                />
                                                            )
                                                        })()}

                                                        {/* Helper function to calculate position based on column structure */}
                                                        {(() => {
                                                            // Use the global cached helper functions
                                                            const getLeftPositionLocal = (hour: number, offsetPercent: number = 0): number => {
                                                                return getLeftPosition(day, hour, offsetPercent)
                                                            }

                                                            const getRightPositionLocal = (hour: number, offsetPercent: number = 0): number => {
                                                                return getRightPosition(day, hour, offsetPercent)
                                                            }

                                                            // Find an empty spot for the Add Shift button
                                                            // Check which hours have content (shifts or calendar entries)
                                                            // Note: allShiftsForDay already includes night shift continuations from previous day
                                                            const hoursWithContent = new Set<number>()
                                                            allShiftsForDay.forEach(shift => {
                                                                for (let hour = shift.startHour; hour <= shift.endHour; hour++) {
                                                                    hoursWithContent.add(hour)
                                                                }
                                                            })
                                                            calendarEntriesForDay.forEach(entry => {
                                                                for (let hour = entry.startHour; hour <= entry.endHour; hour++) {
                                                                    hoursWithContent.add(hour)
                                                                }
                                                            })

                                                            // Check if the entire day is occupied (all 24 hours have content)
                                                            const isDayFullyOccupied = hoursWithContent.size === 24

                                                            // Calculate actual shift positions to avoid overlaps
                                                            const shiftRanges: Array<{ left: number; right: number }> = []
                                                            allShiftsForDay.forEach(shift => {
                                                                const left = getLeftPositionLocal(shift.startHour, shift.leftOffset)
                                                                const right = getRightPositionLocal(shift.endHour, shift.rightOffset)
                                                                shiftRanges.push({ left, right })
                                                            })
                                                            calendarEntriesForDay.forEach(entry => {
                                                                const left = getLeftPositionLocal(entry.startHour, entry.leftOffset)
                                                                const right = getRightPositionLocal(entry.endHour, entry.rightOffset)
                                                                shiftRanges.push({ left, right })
                                                            })

                                                            // Find a safe position that doesn't overlap with any shift
                                                            const BUTTON_WIDTH = 28
                                                            const BUTTON_PADDING = 10 // Extra padding to ensure visibility
                                                            let buttonLeft = 0
                                                            let foundSafePosition = false

                                                            // Try preferred hours first
                                                            const preferredHours = [10, 11, 12, 13, 14, 9, 15, 8, 16, 7, 17, 6, 18]
                                                            for (const hour of preferredHours) {
                                                                if (hoursWithContent.has(hour)) continue

                                                                const candidateLeft = getLeftPositionLocal(hour, 50)

                                                                // Check if this position overlaps with any shift
                                                                // Button center is at candidateLeft, so left edge is candidateLeft - 14px, right edge is candidateLeft + 14px
                                                                const buttonLeftEdge = candidateLeft - (BUTTON_WIDTH / 2) - BUTTON_PADDING
                                                                const buttonRightEdge = candidateLeft + (BUTTON_WIDTH / 2) + BUTTON_PADDING

                                                                const overlaps = shiftRanges.some(range => {
                                                                    // Check if button overlaps with shift range (with padding)
                                                                    return !(buttonRightEdge < range.left || buttonLeftEdge > range.right)
                                                                })

                                                                if (!overlaps) {
                                                                    buttonLeft = candidateLeft
                                                                    foundSafePosition = true
                                                                    break
                                                                }
                                                            }

                                                            // If no preferred hour works, find any safe position
                                                            if (!foundSafePosition) {
                                                                for (let hour = 0; hour <= 23; hour++) {
                                                                    if (hoursWithContent.has(hour)) continue

                                                                    const candidateLeft = getLeftPositionLocal(hour, 50)
                                                                    const buttonLeftEdge = candidateLeft - (BUTTON_WIDTH / 2) - BUTTON_PADDING
                                                                    const buttonRightEdge = candidateLeft + (BUTTON_WIDTH / 2) + BUTTON_PADDING

                                                                    const overlaps = shiftRanges.some(range => {
                                                                        // Check if button overlaps with shift range (with padding)
                                                                        return !(buttonRightEdge < range.left || buttonLeftEdge > range.right)
                                                                    })

                                                                    if (!overlaps) {
                                                                        buttonLeft = candidateLeft
                                                                        foundSafePosition = true
                                                                        break
                                                                    }
                                                                }
                                                            }

                                                            // If still no safe position found, place it at the start of the first empty hour
                                                            if (!foundSafePosition && !isDayFullyOccupied) {
                                                                for (let hour = 0; hour <= 23; hour++) {
                                                                    if (!hoursWithContent.has(hour)) {
                                                                        buttonLeft = getLeftPositionLocal(hour, 10) // Place at 10% of the hour to avoid edge
                                                                        foundSafePosition = true
                                                                        break
                                                                    }
                                                                }
                                                            }

                                                            const buttonTop = "50%"
                                                            const dayDateString = format(day, "yyyy-MM-dd")
                                                            const hasTemplate = !daysWithoutTemplate.has(dayDateString)

                                                            return (
                                                                <>
                                                                    {/* Add Shift button - only show if there's available time AND template is assigned AND date is not read-only */}
                                                                    {hasTemplate && !isDayFullyOccupied && foundSafePosition && !isDateReadOnly(day) && !isOutsideContract && (
                                                                        <Box
                                                                            sx={{
                                                                                position: "absolute",
                                                                                top: buttonTop,
                                                                                left: `${buttonLeft}px`,
                                                                                transform: "translate(-50%, -50%)",
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                width: "28px",
                                                                                height: "28px",
                                                                                borderRadius: "8px",
                                                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                                                                color: theme.palette.primary.main,
                                                                                cursor: "pointer",
                                                                                transition: "all 0.2s ease",
                                                                                zIndex: 200, // Higher z-index to ensure it's always on top
                                                                                opacity: 0.7,
                                                                                "&:hover": {
                                                                                    opacity: 1,
                                                                                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                                                                    borderColor: alpha(theme.palette.primary.main, 0.5),
                                                                                    transform: "translate(-50%, -50%) scale(1.1)",
                                                                                    zIndex: 201,
                                                                                },
                                                                                "&:active": {
                                                                                    transform: "translate(-50%, -50%) scale(0.95)",
                                                                                }
                                                                            }}
                                                                            onClick={async () => handleAddShift(employee, day)}
                                                                        >
                                                                            <Add sx={{ fontSize: 16 }} />
                                                                        </Box>
                                                                    )}

                                                                    {/* Render shifts as single elements spanning their full duration, with visual breaks */}
                                                                    {allShiftsForDay.map((shift, shiftIndex) => {
                                                                        const originalLeft = getLeftPositionLocal(shift.startHour, shift.leftOffset)
                                                                        const originalRight = getRightPositionLocal(shift.endHour, shift.rightOffset)
                                                                        const extendsToNextDay = shift.extendsToNextDay === true && shift.totalWidth !== undefined
                                                                        // For night shifts, use total width that spans both days
                                                                        const originalFullWidth = extendsToNextDay ? shift.totalWidth : (originalRight - originalLeft)

                                                                        // Apply resize adjustment if this shift is being resized
                                                                        const isBeingResized = resizingShift?.shift === shift
                                                                        const isBeingMoved = movingShift?.shift === shift && (movingShift?.hasMoved ?? false)
                                                                        const resizeAdj = getResizeAdjustedPositions(shift, originalLeft, originalLeft + originalFullWidth, dayWidthForNightShift)
                                                                        const moveLeft = getMoveAdjustedLeft(shift, originalLeft, originalFullWidth, dayWidthForNightShift)
                                                                        const left = isBeingResized ? resizeAdj.left : isBeingMoved ? moveLeft : originalLeft
                                                                        const fullWidth = isBeingResized ? resizeAdj.width : originalFullWidth
                                                                        const right = extendsToNextDay ? originalRight : left + fullWidth
                                                                        const canDrag = !isReadOnlyDay
                                                                        const canResize = !isReadOnlyDay && !extendsToNextDay

                                                                        // Collect all breaks (main + additional) and sort by start time
                                                                        const allBreaks: Array<{ startTime: Date; endTime: Date; startLeft: number; endLeft: number; breakKind: 'main' | 'additional'; breakIndex: number }> = []

                                                                        // Add main break if exists
                                                                        if (shift.breakStartTime && shift.breakEndTime) {
                                                                            const breakStartHour = getStartHourColumn(shift.breakStartTime)
                                                                            const breakEndHour = getStartHourColumn(shift.breakEndTime)
                                                                            const breakStartOffset = getLeftOffsetPercentage(shift.breakStartTime)
                                                                            const breakEndOffset = getRightOffsetPercentage(shift.breakEndTime)
                                                                            const breakStartLeft = getLeftPositionLocal(breakStartHour, breakStartOffset)
                                                                            const breakEndLeft = getRightPositionLocal(breakEndHour, breakEndOffset)
                                                                            // Apply live drag preview if this break is being dragged
                                                                            const adj = getBreakDragAdjusted(shift, 'main', -1, breakStartLeft, breakEndLeft)

                                                                            allBreaks.push({
                                                                                startTime: shift.breakStartTime,
                                                                                endTime: shift.breakEndTime,
                                                                                startLeft: adj.startLeft,
                                                                                endLeft: adj.endLeft,
                                                                                breakKind: 'main',
                                                                                breakIndex: -1
                                                                            })
                                                                        }

                                                                        // Add additional breaks if they exist
                                                                        if (shift.additionalBreaks && shift.additionalBreaks.length > 0) {
                                                                            shift.additionalBreaks.forEach((ab: { startTime: Date; endTime: Date }, abIndex: number) => {
                                                                                const breakStartHour = getStartHourColumn(ab.startTime)
                                                                                const breakEndHour = getStartHourColumn(ab.endTime)
                                                                                const breakStartOffset = getLeftOffsetPercentage(ab.startTime)
                                                                                const breakEndOffset = getRightOffsetPercentage(ab.endTime)
                                                                                const breakStartLeft = getLeftPositionLocal(breakStartHour, breakStartOffset)
                                                                                const breakEndLeft = getRightPositionLocal(breakEndHour, breakEndOffset)
                                                                                // Apply live drag preview if this break is being dragged
                                                                                const adj = getBreakDragAdjusted(shift, 'additional', abIndex, breakStartLeft, breakEndLeft)

                                                                                allBreaks.push({
                                                                                    startTime: ab.startTime,
                                                                                    endTime: ab.endTime,
                                                                                    startLeft: adj.startLeft,
                                                                                    endLeft: adj.endLeft,
                                                                                    breakKind: 'additional',
                                                                                    breakIndex: abIndex
                                                                                })
                                                                            })
                                                                        }

                                                                        // Sort breaks by start time
                                                                        allBreaks.sort((a, b) => a.startLeft - b.startLeft)

                                                                        // Check if shift is unpublished
                                                                        const isUnpublished = shift.published === false || shift.published === undefined

                                                                        // If there are breaks, render multiple segments with visual break indicators
                                                                        // For night shifts, use fullWidth instead of right for the end position
                                                                        const effectiveRight = extendsToNextDay ? left + fullWidth : right
                                                                        const validBreaks = allBreaks.filter(b => b.startLeft > left && b.endLeft < effectiveRight)

                                                                        if (validBreaks.length > 0) {
                                                                            // Build segments: [start, break1_start, break1_end, break2_start, break2_end, ..., end]
                                                                            const segments: Array<{ left: number; width: number; startTime: Date; endTime: Date }> = []
                                                                            let currentLeft = left
                                                                            let currentTime = shift.startTime

                                                                            // Create segments between breaks
                                                                            for (let i = 0; i < validBreaks.length; i++) {
                                                                                const br = validBreaks[i]
                                                                                // Segment before this break
                                                                                if (br.startLeft > currentLeft) {
                                                                                    segments.push({
                                                                                        left: currentLeft,
                                                                                        width: br.startLeft - currentLeft,
                                                                                        startTime: currentTime,
                                                                                        endTime: br.startTime
                                                                                    })
                                                                                }
                                                                                currentLeft = br.endLeft
                                                                                currentTime = br.endTime
                                                                            }

                                                                            // Final segment after last break
                                                                            if (effectiveRight > currentLeft) {
                                                                                segments.push({
                                                                                    left: currentLeft,
                                                                                    width: effectiveRight - currentLeft,
                                                                                    startTime: currentTime,
                                                                                    endTime: shift.endTime
                                                                                })
                                                                            }

                                                                            const cardStyle = {
                                                                                background: theme.palette.mode === 'dark'
                                                                                    ? theme.palette.grey[800]
                                                                                    : theme.palette.background.paper,
                                                                                color: theme.palette.text.primary,
                                                                                padding: "6px 8px",
                                                                                borderRadius: "8px",
                                                                                border: isUnpublished
                                                                                    ? `1.5px dashed ${theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[300]}`
                                                                                    : `1.5px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                                                                                overflow: "hidden",
                                                                                fontSize: "0.78rem",
                                                                                display: "flex",
                                                                                flexDirection: "column" as const,
                                                                                justifyContent: "center" as const,
                                                                                gap: "2px",
                                                                                opacity: isUnpublished ? 0.92 : 1,
                                                                                boxShadow: theme.palette.mode === 'dark'
                                                                                    ? "0 2px 8px rgba(0,0,0,0.3)"
                                                                                    : "0 2px 8px rgba(0,0,0,0.08)",
                                                                                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                                                                cursor: "pointer" as const,
                                                                                zIndex: 10,
                                                                                position: "relative" as const,
                                                                                "&:hover": {
                                                                                    transform: "translateY(-1px)",
                                                                                    boxShadow: theme.palette.mode === 'dark'
                                                                                        ? "0 4px 16px rgba(0,0,0,0.4)"
                                                                                        : "0 4px 16px rgba(0,0,0,0.12)",
                                                                                    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
                                                                                    zIndex: 20,
                                                                                    opacity: 1
                                                                                }
                                                                            }

                                                                            return (
                                                                                <React.Fragment key={`shift-${shift.id}-${shiftIndex}`}>
                                                                                    {/* Render work segments */}
                                                                                    {segments.map((segment, segIndex) => {
                                                                                        const isFirst = segIndex === 0
                                                                                        const isLast = segIndex === segments.length - 1
                                                                                        const hasLeftBreak = !isFirst
                                                                                        const hasRightBreak = !isLast
                                                                                        const hasEnoughSpace = segment.width > 40
                                                                                        // Hours worked within this individual segment (e.g. morning vs. afternoon when split by a break).
                                                                                        // Break boundaries and shift times can live on different epochs/days, so we diff by
                                                                                        // time-of-day minutes (matching how segments are positioned) and wrap past midnight.
                                                                                        const segmentMinuteOfDay = (d: Date) => d.getHours() * 60 + d.getMinutes()
                                                                                        let segmentMinutes = segmentMinuteOfDay(segment.endTime) - segmentMinuteOfDay(segment.startTime)
                                                                                        if (segmentMinutes < 0) segmentMinutes += 24 * 60
                                                                                        const segmentHours = Math.round((segmentMinutes / 60) * 10) / 10
                                                                                        // Too narrow to fit the hours inside the segment → show a floating badge below it instead.
                                                                                        const showHoursInline = segment.width >= HOURS_INLINE_MIN_WIDTH
                                                                                        const startTimeLabel = format(segment.startTime, "HH:mm", { locale: getLocale() })
                                                                                        const endTimeLabel = format(segment.endTime, "HH:mm", { locale: getLocale() })
                                                                                        const timeLabel = `${startTimeLabel} - ${endTimeLabel}`

                                                                                        const paperElement = (
                                                                                            <Paper
                                                                                                elevation={0}
                                                                                                sx={{
                                                                                                    ...cardStyle,
                                                                                                    position: "absolute",
                                                                                                    top: "6px",
                                                                                                    bottom: "6px",
                                                                                                    left: `${segment.left}px`,
                                                                                                    width: `${segment.width}px`,
                                                                                                    borderRadius: isFirst && isLast ? "8px" : isFirst ? "8px 0 0 8px" : isLast ? "0 8px 8px 0" : "0",
                                                                                                    borderRight: hasRightBreak ? "2px dashed" : "none",
                                                                                                    borderLeft: hasLeftBreak ? "2px dashed" : "none",
                                                                                                    borderRightColor: hasRightBreak ? (theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400]) : undefined,
                                                                                                    borderLeftColor: hasLeftBreak ? (theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400]) : undefined,
                                                                                                    overflow: extendsToNextDay && isLast ? "visible" : "hidden",
                                                                                                    transition: (isBeingResized || isBeingMoved) ? "none" : undefined,
                                                                                                    cursor: isBeingResized ? "ew-resize" : isBeingMoved ? "grabbing" : (canDrag ? "grab" : "pointer"),
                                                                                                }}
                                                                                                onMouseDown={(e) => {
                                                                                                    if (e.button === 0 && canDrag) {
                                                                                                        handleMoveStart(e, shift, day, employee, originalLeft, originalLeft + originalFullWidth)
                                                                                                    }
                                                                                                }}
                                                                                                onClick={() => { if (!isBeingResized && !isBeingMoved) handleShiftClick(shift, employee, day) }}
                                                                                            >
                                                                                                {isUnpublished && segIndex === 0 && (
                                                                                                    <Box
                                                                                                        sx={{
                                                                                                            position: "absolute",
                                                                                                            top: 3,
                                                                                                            right: 3,
                                                                                                            width: 5,
                                                                                                            height: 5,
                                                                                                            borderRadius: "50%",
                                                                                                            bgcolor: theme.palette.mode === 'dark'
                                                                                                                ? theme.palette.warning.main
                                                                                                                : theme.palette.warning.light,
                                                                                                            boxShadow: theme.palette.mode === 'dark'
                                                                                                                ? `0 0 4px ${theme.palette.warning.main}`
                                                                                                                : `0 0 4px ${theme.palette.warning.light}`,
                                                                                                            zIndex: 15
                                                                                                        }}
                                                                                                    />
                                                                                                )}
                                                                                                {segIndex === 0 && (
                                                                                                    <>
                                                                                                        <Typography
                                                                                                            variant="caption"
                                                                                                            fontWeight={600}
                                                                                                            noWrap
                                                                                                            sx={{
                                                                                                                fontSize: "0.75rem",
                                                                                                                color: theme.palette.text.primary,
                                                                                                                lineHeight: 1.2
                                                                                                            }}
                                                                                                        >
                                                                                                            {shift.shiftData.name}
                                                                                                        </Typography>
                                                                                                        <Typography
                                                                                                            variant="caption"
                                                                                                            noWrap
                                                                                                            sx={{
                                                                                                                fontSize: "0.7rem",
                                                                                                                color: theme.palette.text.secondary,
                                                                                                                lineHeight: 1.2,
                                                                                                                fontWeight: 500
                                                                                                            }}
                                                                                                        >
                                                                                                            {format(segment.startTime, "HH:mm", { locale: getLocale() })} - {format(segment.endTime, "HH:mm", { locale: getLocale() })}
                                                                                                        </Typography>
                                                                                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 0.25 }}>
                                                                                                            <Typography
                                                                                                                variant="caption"
                                                                                                                noWrap
                                                                                                                sx={{
                                                                                                                    fontSize: "0.65rem",
                                                                                                                    color: theme.palette.text.secondary,
                                                                                                                    lineHeight: 1.2
                                                                                                                }}
                                                                                                            >
                                                                                                                {shift.workPositionName}
                                                                                                            </Typography>
                                                                                                            {showHoursInline && (
                                                                                                                <Typography
                                                                                                                    variant="caption"
                                                                                                                    noWrap
                                                                                                                    sx={{
                                                                                                                        fontSize: "0.65rem",
                                                                                                                        color: theme.palette.primary.main,
                                                                                                                        fontWeight: 600,
                                                                                                                        lineHeight: 1.2
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {segmentHours}h
                                                                                                                </Typography>
                                                                                                            )}
                                                                                                        </Box>
                                                                                                    </>
                                                                                                )}
                                                                                                {segIndex > 0 && hasEnoughSpace && (
                                                                                                    <Box sx={{
                                                                                                        display: "flex",
                                                                                                        flexDirection: "column",
                                                                                                        alignItems: "center",
                                                                                                        justifyContent: "center",
                                                                                                        width: "100%",
                                                                                                        px: segment.width < 50 ? 0.25 : 0.5,
                                                                                                        py: 0
                                                                                                    }}>
                                                                                                        <Typography
                                                                                                            variant="caption"
                                                                                                            noWrap
                                                                                                            sx={{
                                                                                                                fontSize: segment.width < 45 ? "0.65rem" : "0.7rem",
                                                                                                                color: theme.palette.text.secondary,
                                                                                                                lineHeight: 1.1,
                                                                                                                fontWeight: 500,
                                                                                                                textAlign: "center",
                                                                                                                width: "100%"
                                                                                                            }}
                                                                                                        >
                                                                                                            {startTimeLabel}
                                                                                                        </Typography>
                                                                                                        <Typography
                                                                                                            variant="caption"
                                                                                                            noWrap
                                                                                                            sx={{
                                                                                                                fontSize: segment.width < 45 ? "0.65rem" : "0.7rem",
                                                                                                                color: theme.palette.text.secondary,
                                                                                                                lineHeight: 1.1,
                                                                                                                fontWeight: 500,
                                                                                                                textAlign: "center",
                                                                                                                width: "100%"
                                                                                                            }}
                                                                                                        >
                                                                                                            {endTimeLabel}
                                                                                                        </Typography>
                                                                                                        {showHoursInline && (
                                                                                                            <Typography
                                                                                                                variant="caption"
                                                                                                                noWrap
                                                                                                                sx={{
                                                                                                                    fontSize: "0.65rem",
                                                                                                                    color: theme.palette.primary.main,
                                                                                                                    fontWeight: 600,
                                                                                                                    lineHeight: 1.2,
                                                                                                                    textAlign: "center",
                                                                                                                    width: "100%"
                                                                                                                }}
                                                                                                            >
                                                                                                                {segmentHours}h
                                                                                                            </Typography>
                                                                                                        )}
                                                                                                    </Box>
                                                                                                )}
                                                                                            </Paper>
                                                                                        )

                                                                                        // Wrap in tooltip if segment is too small to display text
                                                                                        const segmentCard = segIndex > 0 && !hasEnoughSpace ? (
                                                                                            <Tooltip title={timeLabel} arrow>
                                                                                                {paperElement}
                                                                                            </Tooltip>
                                                                                        ) : paperElement

                                                                                        return (
                                                                                            <React.Fragment key={`segment-${segIndex}`}>
                                                                                                {segmentCard}
                                                                                                {/* Floating hours badge for segments too narrow to show it inline */}
                                                                                                {!showHoursInline && (
                                                                                                    <Box
                                                                                                        sx={{
                                                                                                            position: "absolute",
                                                                                                            left: `${segment.left + segment.width / 2}px`,
                                                                                                            transform: "translateX(-50%)",
                                                                                                            bottom: "-8px",
                                                                                                            px: 0.5,
                                                                                                            py: "1px",
                                                                                                            borderRadius: "6px",
                                                                                                            backgroundColor: theme.palette.background.paper,
                                                                                                            border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[300]}`,
                                                                                                            boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                                                                                                            pointerEvents: "none",
                                                                                                            whiteSpace: "nowrap",
                                                                                                            zIndex: 26,
                                                                                                        }}
                                                                                                    >
                                                                                                        <Typography
                                                                                                            variant="caption"
                                                                                                            sx={{
                                                                                                                fontSize: "0.6rem",
                                                                                                                fontWeight: 700,
                                                                                                                color: theme.palette.primary.main,
                                                                                                                lineHeight: 1,
                                                                                                            }}
                                                                                                        >
                                                                                                            {segmentHours}h
                                                                                                        </Typography>
                                                                                                    </Box>
                                                                                                )}
                                                                                            </React.Fragment>
                                                                                        )
                                                                                    })}

                                                                                    {/* Render break gap indicators */}
                                                                                    {validBreaks.map((br, breakIndex) => {
                                                                                        const breakGapWidth = br.endLeft - br.startLeft
                                                                                        const isThisBreakDragged = !!draggingBreak
                                                                                            && draggingBreak.shift === shift
                                                                                            && draggingBreak.breakKind === br.breakKind
                                                                                            && draggingBreak.breakIndex === br.breakIndex
                                                                                            && draggingBreak.hasMoved
                                                                                        const breakEdgeColor = isThisBreakDragged
                                                                                            ? theme.palette.primary.main
                                                                                            : (theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400])
                                                                                        return (
                                                                                            <Box
                                                                                                key={`break-${breakIndex}`}
                                                                                                sx={{
                                                                                                    position: "absolute",
                                                                                                    top: "6px",
                                                                                                    bottom: "6px",
                                                                                                    left: `${br.startLeft}px`,
                                                                                                    width: `${breakGapWidth}px`,
                                                                                                    display: "flex",
                                                                                                    flexDirection: "column",
                                                                                                    alignItems: "center",
                                                                                                    justifyContent: "center",
                                                                                                    zIndex: 11,
                                                                                                    pointerEvents: "none"
                                                                                                }}
                                                                                            >
                                                                                                {/* Vertical dashed line indicators */}
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        position: "absolute",
                                                                                                        left: 0,
                                                                                                        top: 0,
                                                                                                        bottom: 0,
                                                                                                        width: "2px",
                                                                                                        borderLeft: "2px dashed",
                                                                                                        borderColor: breakEdgeColor
                                                                                                    }}
                                                                                                />
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        position: "absolute",
                                                                                                        right: 0,
                                                                                                        top: 0,
                                                                                                        bottom: 0,
                                                                                                        width: "2px",
                                                                                                        borderRight: "2px dashed",
                                                                                                        borderColor: breakEdgeColor
                                                                                                    }}
                                                                                                />
                                                                                                {/* Drag-to-edit break interaction zones (move + resize each edge) */}
                                                                                                {canResize && (
                                                                                                    <>
                                                                                                        {/* Move the whole break */}
                                                                                                        <Box
                                                                                                            onMouseDown={(e) => {
                                                                                                                if (e.button !== 0) return
                                                                                                                handleBreakDragStart(e, shift, day, employee, br.breakKind, br.breakIndex, 'move', br.startTime, br.endTime, br.startLeft, br.endLeft)
                                                                                                            }}
                                                                                                            sx={{
                                                                                                                position: "absolute",
                                                                                                                top: 0,
                                                                                                                bottom: 0,
                                                                                                                left: 0,
                                                                                                                right: 0,
                                                                                                                cursor: isThisBreakDragged && draggingBreak?.mode === 'move' ? "grabbing" : "grab",
                                                                                                                pointerEvents: "auto",
                                                                                                                zIndex: 13,
                                                                                                                "&:hover": {
                                                                                                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                                                                                                }
                                                                                                            }}
                                                                                                        />
                                                                                                        {/* Resize the break's left edge */}
                                                                                                        <Box
                                                                                                            onMouseDown={(e) => {
                                                                                                                if (e.button !== 0) return
                                                                                                                handleBreakDragStart(e, shift, day, employee, br.breakKind, br.breakIndex, 'resize-left', br.startTime, br.endTime, br.startLeft, br.endLeft)
                                                                                                            }}
                                                                                                            sx={{
                                                                                                                position: "absolute",
                                                                                                                top: 0,
                                                                                                                bottom: 0,
                                                                                                                left: "-3px",
                                                                                                                width: "6px",
                                                                                                                cursor: "ew-resize",
                                                                                                                pointerEvents: "auto",
                                                                                                                zIndex: 14,
                                                                                                                "&:hover": {
                                                                                                                    bgcolor: alpha(theme.palette.primary.main, 0.4),
                                                                                                                }
                                                                                                            }}
                                                                                                        />
                                                                                                        {/* Resize the break's right edge */}
                                                                                                        <Box
                                                                                                            onMouseDown={(e) => {
                                                                                                                if (e.button !== 0) return
                                                                                                                handleBreakDragStart(e, shift, day, employee, br.breakKind, br.breakIndex, 'resize-right', br.startTime, br.endTime, br.startLeft, br.endLeft)
                                                                                                            }}
                                                                                                            sx={{
                                                                                                                position: "absolute",
                                                                                                                top: 0,
                                                                                                                bottom: 0,
                                                                                                                right: "-3px",
                                                                                                                width: "6px",
                                                                                                                cursor: "ew-resize",
                                                                                                                pointerEvents: "auto",
                                                                                                                zIndex: 14,
                                                                                                                "&:hover": {
                                                                                                                    bgcolor: alpha(theme.palette.primary.main, 0.4),
                                                                                                                }
                                                                                                            }}
                                                                                                        />
                                                                                                    </>
                                                                                                )}
                                                                                                {/* Break time badge */}
                                                                                                {breakGapWidth > 40 && (
                                                                                                    <Box
                                                                                                        sx={{
                                                                                                            position: "absolute",
                                                                                                            top: "50%",
                                                                                                            left: "50%",
                                                                                                            transform: "translate(-50%, -50%)",
                                                                                                            bgcolor: theme.palette.mode === 'dark'
                                                                                                                ? theme.palette.grey[700]
                                                                                                                : theme.palette.grey[100],
                                                                                                            borderRadius: "4px",
                                                                                                            px: 0.5,
                                                                                                            py: 0.25,
                                                                                                            border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[300]}`,
                                                                                                            zIndex: 12
                                                                                                        }}
                                                                                                    >
                                                                                                        <Typography
                                                                                                            variant="caption"
                                                                                                            sx={{
                                                                                                                fontSize: "0.6rem",
                                                                                                                color: theme.palette.text.secondary,
                                                                                                                fontWeight: 500,
                                                                                                                whiteSpace: "nowrap"
                                                                                                            }}
                                                                                                        >
                                                                                                            {format(br.startTime, "HH:mm", { locale: getLocale() })} - {format(br.endTime, "HH:mm", { locale: getLocale() })}
                                                                                                        </Typography>
                                                                                                    </Box>
                                                                                                )}
                                                                                            </Box>
                                                                                        )
                                                                                    })}

                                                                                    {/* Resize handles for segmented shifts */}
                                                                                    {canResize && (
                                                                                        <>
                                                                                            {/* Left resize handle */}
                                                                                            <Box
                                                                                                onMouseDown={(e) => handleResizeStart(e, shift, 'left', day, employee, left, left + fullWidth)}
                                                                                                sx={{
                                                                                                    position: "absolute",
                                                                                                    top: "6px",
                                                                                                    bottom: "6px",
                                                                                                    left: `${left - 3}px`,
                                                                                                    width: "6px",
                                                                                                    cursor: "ew-resize",
                                                                                                    zIndex: 30,
                                                                                                    borderRadius: "3px 0 0 3px",
                                                                                                    transition: "background-color 0.15s",
                                                                                                    "&:hover": {
                                                                                                        bgcolor: alpha(theme.palette.primary.main, 0.3),
                                                                                                    },
                                                                                                    ...(isBeingResized && resizingShift?.edge === 'left' ? {
                                                                                                        bgcolor: alpha(theme.palette.primary.main, 0.5),
                                                                                                    } : {})
                                                                                                }}
                                                                                            />
                                                                                            {/* Right resize handle */}
                                                                                            <Box
                                                                                                onMouseDown={(e) => handleResizeStart(e, shift, 'right', day, employee, left, left + fullWidth)}
                                                                                                sx={{
                                                                                                    position: "absolute",
                                                                                                    top: "6px",
                                                                                                    bottom: "6px",
                                                                                                    left: `${left + fullWidth - 3}px`,
                                                                                                    width: "6px",
                                                                                                    cursor: "ew-resize",
                                                                                                    zIndex: 30,
                                                                                                    borderRadius: "0 3px 3px 0",
                                                                                                    transition: "background-color 0.15s",
                                                                                                    "&:hover": {
                                                                                                        bgcolor: alpha(theme.palette.primary.main, 0.3),
                                                                                                    },
                                                                                                    ...(isBeingResized && resizingShift?.edge === 'right' ? {
                                                                                                        bgcolor: alpha(theme.palette.primary.main, 0.5),
                                                                                                    } : {})
                                                                                                }}
                                                                                            />
                                                                                        </>
                                                                                    )}
                                                                                </React.Fragment>
                                                                            )
                                                                        }

                                                                        // No break - render as single card
                                                                        // Too narrow to fit the hours inside the card → show a floating badge below it instead.
                                                                        const showHoursInline = fullWidth >= HOURS_INLINE_MIN_WIDTH
                                                                        return (
                                                                            <React.Fragment key={`shift-${shift.id}-${shiftIndex}`}>
                                                                            <Paper
                                                                                elevation={0}
                                                                                sx={{
                                                                                    position: "absolute",
                                                                                    top: "6px",
                                                                                    bottom: "6px",
                                                                                    left: `${left}px`,
                                                                                    width: `${fullWidth}px`,
                                                                                    background: theme.palette.mode === 'dark'
                                                                                        ? theme.palette.grey[800]
                                                                                        : theme.palette.background.paper,
                                                                                    color: theme.palette.text.primary,
                                                                                    padding: "6px 8px",
                                                                                    borderRadius: "8px",
                                                                                    border: isUnpublished
                                                                                        ? `1.5px dashed ${theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[300]}`
                                                                                        : `1.5px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                                                                                    // For night shifts extending to next day, allow overflow so card can span visually
                                                                                    overflow: extendsToNextDay ? "visible" : "hidden",
                                                                                    fontSize: "0.78rem",
                                                                                    display: "flex",
                                                                                    flexDirection: "column",
                                                                                    justifyContent: "center",
                                                                                    gap: "2px",
                                                                                    opacity: isUnpublished ? 0.92 : 1,
                                                                                    boxShadow: theme.palette.mode === 'dark'
                                                                                        ? "0 2px 8px rgba(0,0,0,0.3)"
                                                                                        : "0 2px 8px rgba(0,0,0,0.08)",
                                                                                    transition: (isBeingResized || isBeingMoved) ? "none" : "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                                                                    cursor: isBeingResized ? "ew-resize" : isBeingMoved ? "grabbing" : (canDrag ? "grab" : "pointer"),
                                                                                    zIndex: (isBeingResized || isBeingMoved) ? 25 : 10,
                                                                                    "&:hover": {
                                                                                        transform: (isBeingResized || isBeingMoved) ? "none" : "translateY(-1px)",
                                                                                        boxShadow: theme.palette.mode === 'dark'
                                                                                            ? "0 4px 16px rgba(0,0,0,0.4)"
                                                                                            : "0 4px 16px rgba(0,0,0,0.12)",
                                                                                        borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
                                                                                        zIndex: 20,
                                                                                        opacity: 1
                                                                                    }
                                                                                }}
                                                                                onMouseDown={(e) => {
                                                                                    if (e.button === 0 && canDrag) {
                                                                                        handleMoveStart(e, shift, day, employee, originalLeft, originalLeft + originalFullWidth)
                                                                                    }
                                                                                }}
                                                                                onClick={() => { if (!isBeingResized && !isBeingMoved) handleShiftClick(shift, employee, day) }}
                                                                            >
                                                                                {isUnpublished && (
                                                                                    <Box
                                                                                        sx={{
                                                                                            position: "absolute",
                                                                                            top: 3,
                                                                                            right: 3,
                                                                                            width: 5,
                                                                                            height: 5,
                                                                                            borderRadius: "50%",
                                                                                            bgcolor: theme.palette.mode === 'dark'
                                                                                                ? theme.palette.warning.main
                                                                                                : theme.palette.warning.light,
                                                                                            boxShadow: theme.palette.mode === 'dark'
                                                                                                ? `0 0 4px ${theme.palette.warning.main}`
                                                                                                : `0 0 4px ${theme.palette.warning.light}`,
                                                                                            zIndex: 15
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    fontWeight={600}
                                                                                    noWrap
                                                                                    sx={{
                                                                                        fontSize: "0.75rem",
                                                                                        color: theme.palette.text.primary,
                                                                                        lineHeight: 1.2
                                                                                    }}
                                                                                >
                                                                                    {shift.shiftData.name}
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    noWrap
                                                                                    sx={{
                                                                                        fontSize: "0.7rem",
                                                                                        color: theme.palette.text.secondary,
                                                                                        lineHeight: 1.2,
                                                                                        fontWeight: 500
                                                                                    }}
                                                                                >
                                                                                    {format(shift.startTime, "HH:mm", { locale: getLocale() })} - {format(shift.endTime, "HH:mm", { locale: getLocale() })}
                                                                                </Typography>
                                                                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 0.25 }}>
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        noWrap
                                                                                        sx={{
                                                                                            fontSize: "0.65rem",
                                                                                            color: theme.palette.text.secondary,
                                                                                            lineHeight: 1.2
                                                                                        }}
                                                                                    >
                                                                                        {shift.workPositionName}
                                                                                    </Typography>
                                                                                    {showHoursInline && (
                                                                                        <Typography
                                                                                            variant="caption"
                                                                                            noWrap
                                                                                            sx={{
                                                                                                fontSize: "0.65rem",
                                                                                                color: theme.palette.primary.main,
                                                                                                fontWeight: 600,
                                                                                                lineHeight: 1.2
                                                                                            }}
                                                                                        >
                                                                                            {shift.totalHours}h
                                                                                        </Typography>
                                                                                    )}
                                                                                </Box>
                                                                            </Paper>
                                                                            {/* Floating hours badge when the card is too narrow to show it inline */}
                                                                            {!showHoursInline && (
                                                                                <Box
                                                                                    sx={{
                                                                                        position: "absolute",
                                                                                        left: `${left + fullWidth / 2}px`,
                                                                                        transform: "translateX(-50%)",
                                                                                        bottom: "-8px",
                                                                                        px: 0.5,
                                                                                        py: "1px",
                                                                                        borderRadius: "6px",
                                                                                        backgroundColor: theme.palette.background.paper,
                                                                                        border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[300]}`,
                                                                                        boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                                                                                        pointerEvents: "none",
                                                                                        whiteSpace: "nowrap",
                                                                                        zIndex: 26,
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        sx={{
                                                                                            fontSize: "0.6rem",
                                                                                            fontWeight: 700,
                                                                                            color: theme.palette.primary.main,
                                                                                            lineHeight: 1,
                                                                                        }}
                                                                                    >
                                                                                        {shift.totalHours}h
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                            {/* Resize handles for single card */}
                                                                            {canResize && (
                                                                                <>
                                                                                    {/* Left resize handle */}
                                                                                    <Box
                                                                                        onMouseDown={(e) => handleResizeStart(e, shift, 'left', day, employee, left, left + fullWidth)}
                                                                                        sx={{
                                                                                            position: "absolute",
                                                                                            top: "6px",
                                                                                            bottom: "6px",
                                                                                            left: `${left - 3}px`,
                                                                                            width: "6px",
                                                                                            cursor: "ew-resize",
                                                                                            zIndex: 30,
                                                                                            borderRadius: "3px 0 0 3px",
                                                                                            transition: "background-color 0.15s",
                                                                                            "&:hover": {
                                                                                                bgcolor: alpha(theme.palette.primary.main, 0.3),
                                                                                            },
                                                                                            ...(isBeingResized && resizingShift?.edge === 'left' ? {
                                                                                                bgcolor: alpha(theme.palette.primary.main, 0.5),
                                                                                            } : {})
                                                                                        }}
                                                                                    />
                                                                                    {/* Right resize handle */}
                                                                                    <Box
                                                                                        onMouseDown={(e) => handleResizeStart(e, shift, 'right', day, employee, left, left + fullWidth)}
                                                                                        sx={{
                                                                                            position: "absolute",
                                                                                            top: "6px",
                                                                                            bottom: "6px",
                                                                                            left: `${left + fullWidth - 3}px`,
                                                                                            width: "6px",
                                                                                            cursor: "ew-resize",
                                                                                            zIndex: 30,
                                                                                            borderRadius: "0 3px 3px 0",
                                                                                            transition: "background-color 0.15s",
                                                                                            "&:hover": {
                                                                                                bgcolor: alpha(theme.palette.primary.main, 0.3),
                                                                                            },
                                                                                            ...(isBeingResized && resizingShift?.edge === 'right' ? {
                                                                                                bgcolor: alpha(theme.palette.primary.main, 0.5),
                                                                                            } : {})
                                                                                        }}
                                                                                    />
                                                                                </>
                                                                            )}
                                                                            </React.Fragment>
                                                                        )
                                                                    })}

                                                                    {/* Render calendar entries (e.g., day off, vacation, etc.) */}
                                                                    {calendarEntriesForDay.map((calendarEntry, entryIndex) => {
                                                                        const entryStartDate = new Date(calendarEntry.entry.startedOn)
                                                                        const entryEndDate = new Date(calendarEntry.entry.endedOn)
                                                                        const entryStartDateOnly = startOfDay(entryStartDate)
                                                                        const entryEndDateOnly = startOfDay(entryEndDate)
                                                                        const isMultiDay = !isSameDay(entryStartDateOnly, entryEndDateOnly)

                                                                        // FIX 2: DETECT ALL DAY VS TIME SPECIFIC
                                                                        const isAllDay = (calendarEntry.entry.startTime === null && calendarEntry.entry.endTime === null) ||
                                                                            (calendarEntry.startHour === 0 && calendarEntry.endHour === 23)

                                                                        let left = "0px"
                                                                        let width = "100%" // Default to full width for all-day

                                                                        if (!isAllDay) {
                                                                            // Only calculate pixels if it implies a specific time
                                                                            const leftPx = getLeftPosition(day, calendarEntry.startHour, calendarEntry.leftOffset)
                                                                            const rightPx = getRightPosition(day, calendarEntry.endHour, calendarEntry.rightOffset)
                                                                            left = `${leftPx}px`
                                                                            width = `${rightPx - leftPx}px`
                                                                        } else if (isMultiDay) {
                                                                            // If it's multi-day all-day, we might want to calculate pixel width to span days,
                                                                            // OR just let it fill this specific day cell (simplest for collapsed views).
                                                                            // Given the complexity of collapsed columns, rendering per-cell 100% is safer visually.
                                                                            // If you want it to span, you need complex pixel math across multiple days' different layouts.
                                                                            // Let's stick to filling the current cell:
                                                                            left = "0px"
                                                                            width = "100%"
                                                                        }

                                                                        return (
                                                                            <Paper
                                                                                key={`calendar-entry-${calendarEntry.entry.id}-${entryIndex}`}
                                                                                elevation={2}
                                                                                sx={{
                                                                                    position: "absolute",
                                                                                    top: "4px",
                                                                                    bottom: "4px",
                                                                                    left,   // Use string (0px or calculated)
                                                                                    width, // Use string (100% or calculated)
                                                                                    background: calendarEntry.color,
                                                                                    color: "white",
                                                                                    padding: "2px 6px",
                                                                                    borderRadius: "4px",
                                                                                    fontSize: "0.65rem",
                                                                                    display: "flex",
                                                                                    flexDirection: "column",
                                                                                    justifyContent: "center",
                                                                                    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                                                                                    transition: "all 0.3s ease",
                                                                                    zIndex: 8,
                                                                                    opacity: 0.85,
                                                                                    overflow: "hidden",
                                                                                    cursor: "pointer",
                                                                                    "&:hover": {
                                                                                        opacity: 1,
                                                                                        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                                                                                        zIndex: 18,
                                                                                        transform: "translateY(-1px)"
                                                                                    }
                                                                                }}
                                                                                onClick={() => {
                                                                                    setSelectedCalendarEntry({
                                                                                        entry: calendarEntry.entry,
                                                                                        employee,
                                                                                        date: day,
                                                                                        startTime: calendarEntry.startTime,
                                                                                        endTime: calendarEntry.endTime,
                                                                                        color: calendarEntry.color,
                                                                                        type: calendarEntry.type
                                                                                    })
                                                                                    setOpenCalendarEntryModal(true)
                                                                                }}
                                                                            >
                                                                                <Tooltip
                                                                                    title={
                                                                                        <Box>
                                                                                            <Typography variant="caption" fontWeight="bold">
                                                                                                {calendarEntryLabel(calendarEntry.entry)}
                                                                                            </Typography>
                                                                                            {!isAllDay && (
                                                                                                <Typography variant="caption" display="block">
                                                                                                    {format(calendarEntry.startTime, "HH:mm")} - {format(calendarEntry.endTime, "HH:mm")}
                                                                                                </Typography>
                                                                                            )}
                                                                                            <Typography variant="caption" display="block" sx={{ mt: 0.5, fontSize: "0.7rem" }}>
                                                                                                {t("shift.clickToView") || "Click to view details"}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    }
                                                                                >
                                                                                    <Typography variant="caption" fontWeight="bold" noWrap sx={{ fontSize: "0.6rem" }}>
                                                                                        {calendarEntryLabel(calendarEntry.entry)}
                                                                                    </Typography>
                                                                                </Tooltip>
                                                                            </Paper>
                                                                        )
                                                                    })}

                                                                    {/* Employee day notes - rendered as an all-day strip (a note has no
                                                                        shift and no time, so it spans the whole day like an all-day event).
                                                                        Pinned to the bottom lane so shifts/events stay visible above it. */}
                                                                    {canManageShiftNotes && (() => {
                                                                        const dayNotes = getUnresolvedEmployeeDayNotes(employee.id, day)
                                                                        if (dayNotes.length === 0) return null
                                                                        const notesLabel = dayNotes.map(employeeDayNoteText).join(' • ')
                                                                        return (
                                                                            <Tooltip title={employeeDayNotesTooltip(dayNotes)} arrow placement="top">
                                                                                <Box
                                                                                    onClick={() => handleAddShift(employee, day)}
                                                                                    sx={{
                                                                                        position: 'absolute',
                                                                                        left: '2px',
                                                                                        right: '2px',
                                                                                        bottom: '2px',
                                                                                        height: '24px',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 0.5,
                                                                                        pl: 0.75,
                                                                                        pr: '14px',
                                                                                        bgcolor: noteStrip.bg,
                                                                                        color: noteStrip.text,
                                                                                        border: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
                                                                                        borderRadius: '4px',
                                                                                        overflow: 'hidden',
                                                                                        cursor: 'pointer',
                                                                                        zIndex: 12,
                                                                                        // Sticky-note look: cut the top-right corner and
                                                                                        // shade the folded-over flap behind it.
                                                                                        clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 0 100%)',
                                                                                        '&::after': {
                                                                                            content: '""',
                                                                                            position: 'absolute',
                                                                                            top: 0,
                                                                                            right: 0,
                                                                                            width: '10px',
                                                                                            height: '10px',
                                                                                            background: 'linear-gradient(225deg, transparent 50%, rgba(0, 0, 0, 0.22) 50%)'
                                                                                        },
                                                                                        '&:hover': {
                                                                                            bgcolor: noteStrip.bgHover,
                                                                                            zIndex: 18
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <EditNote sx={{ fontSize: '0.95rem', flexShrink: 0 }} />
                                                                                    <Typography variant="caption" noWrap sx={{ fontSize: '0.68rem', fontWeight: 600, lineHeight: 1 }}>
                                                                                        {notesLabel}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Tooltip>
                                                                        )
                                                                    })()}
                                                                </>
                                                            )
                                                        })()}
                                                    </Grid>
                                                )
                                            })}
                                        </Grid>
                                    )
                                })}
                            </Grid>
                        </Box>
                    )
                ) : (
                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="60vh" sx={{ gap: 5 }}>
                        <img src={errorImage} alt="Loading" style={{ height: "150px" }} />
                        <Typography variant="h4" align="center" sx={{ fontWeight: "bold" }}>
                            {t("shift.noEmployees")}
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                navigate("/dashboard/employees")
                            }}
                            sx={{ fontSize: "larger" }}
                        >
                            {t("shift.goToEmployees")}
                        </Button>
                    </Box>
                )}
            </Card>
            <ShiftReportModal open={openReportModal} onClose={() => { setOpenReportModal(false) }} currentDepartment={selectedDepartment} />
            <ComplianceOverviewModal open={openComplianceModal} onClose={() => { setOpenComplianceModal(false) }} />
            <CopyShiftPlanModal
                open={openCopyModal}
                onClose={() => { setOpenCopyModal(false) }}
                departmentId={selectedDepartment?.id ?? 0}
                seasonId={seasonId}
                isDepchief={isDepchief}
                employeeIds={employees.map(emp => emp.id)}
                fetchData={fetchData}
            />

            <Modal
                open={openAddShiftModal}
                onClose={() => {
                    setOpenAddShiftModal(false)
                    // Clear template data if not in day edit mode
                    if (!selectedDayForEdit) {
                        setDayTemplateData(null)
                        setDayShiftsData([])
                    }
                }}
                aria-labelledby="add-shift-modal"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Paper
                    elevation={24}
                    sx={{
                        width: '90%',
                        maxWidth: 900,
                        maxHeight: '90vh',
                        overflow: 'auto',
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                        border: `1px solid ${theme.palette.divider}`,
                        position: 'relative'
                    }}
                >
                    <Box p={2}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Typography variant="h5" fontWeight="bold" color="text.primary">
                                    {t("shift.assignShift")}
                                </Typography>
                                <BetaChip label="BETA" size="small" />
                            </Box>
                            <Button
                                onClick={() => setOpenAddShiftModal(false)}
                                sx={{ minWidth: 'auto', p: 1 }}
                            >
                                ✕
                            </Button>
                        </Box>

                        {selectedEmployee && selectedDate && (
                            <Box mb={3}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Avatar
                                            src={getEmployeeImageUrl(selectedEmployee.filePath)}
                                            sx={{ width: 48, height: 48 }}
                                        >
                                            {!selectedEmployee.filePath && selectedEmployee.firstName?.[0]}{!selectedEmployee.filePath && selectedEmployee.lastName?.[0]}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6" fontWeight="bold">
                                                {`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {format(selectedDate, "EEEE, dd MMMM yyyy", { locale: getLocale() })} · {weekLabel(selectedDate)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {!isViewerOnly && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<Add />}
                                        disabled={selectedDate ? isDateReadOnly(selectedDate) : false}
                                        onClick={() => {
                                            if (selectedDate && isDateReadOnly(selectedDate)) {
                                                toast.error(t("shift.cannotModifyPastDays") || "Cannot modify past days")
                                                return
                                            }
                                            setOpenAddEventModal(true)
                                        }}
                                        sx={{ ml: 'auto' }}
                                    >
                                        {t("shift.addCalendarEvent")}
                                    </Button>
                                    )}
                                </Box>

                                {/* Quick Actions for Calendar Events */}
                                {!isViewerOnly && (
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" mb={1.5}>
                                        {t("shift.quickActions") || "Quick Actions"}
                                    </Typography>
                                    <Box display="flex" gap={1.5} flexWrap="wrap">
                                        <Button
                                            variant="contained"
                                            size="medium"
                                            onClick={async () => await handleQuickCalendarEvent(CalendarEntryEntityTypeEnum.Vacation)}
                                            disabled={selectedDate ? isDateReadOnly(selectedDate) : false}
                                            sx={{
                                                backgroundColor: '#4caf50',
                                                '&:hover': {
                                                    backgroundColor: '#45a049',
                                                },
                                                minWidth: 140,
                                                textTransform: 'none',
                                                fontWeight: 600
                                            }}
                                        >
                                            {t("employees.calendar.types.vacation") || "Ferie"}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            size="medium"
                                            onClick={async () => await handleQuickCalendarEvent(CalendarEntryEntityTypeEnum.DayOff)}
                                            disabled={selectedDate ? isDateReadOnly(selectedDate) : false}
                                            sx={{
                                                backgroundColor: '#ff9800',
                                                '&:hover': {
                                                    backgroundColor: '#f57c00',
                                                },
                                                minWidth: 140,
                                                textTransform: 'none',
                                                fontWeight: 600
                                            }}
                                        >
                                            {t("employees.calendar.types.day_off") || "Giorno Libero"}
                                        </Button>
                                    </Box>
                                </Box>
                                )}
                            </Box>
                        )}

                        {/* Manager-only notes about this employee for this day */}
                        {selectedEmployee && selectedDate && canManageShiftNotes && (
                            <EmployeeDayNotesSection
                                notes={getEmployeeDayNotes(selectedEmployee.id, selectedDate)}
                                employeeId={selectedEmployee.id}
                                date={selectedDate}
                                fetchData={fetchEmployeeDayNotes}
                                departmentId={selectedDepartment?.id}
                                seasonId={seasonId}
                            />
                        )}

                        {isLoadingShifts ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                                <Box textAlign="center">
                                    <CircularProgress size={48} sx={{ mb: 2 }} />
                                    <Typography variant="body1" color="text.secondary">
                                        {t("shift.loadingAvailableShifts")}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : availableShifts.length > 0 ? (
                            <Box>
                                {/* Step 1: Select Shift */}
                                {!selectedShiftId && (
                                    <Box>
                                        <Typography variant="h6" fontWeight="bold" mb={2} color="text.primary">
                                            {t("shift.selectShift")}
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {getAvailableShiftsGrouped().map((shiftGroup) => (
                                                <Grid item xs={12} sm={6} key={shiftGroup.shiftId}>
                                                    <Paper
                                                        elevation={2}
                                                        sx={{
                                                            p: 2,
                                                            borderRadius: 2,
                                                            border: `2px solid ${theme.palette.divider}`,
                                                            transition: "all 0.3s ease",
                                                            cursor: "pointer",
                                                            "&:hover": {
                                                                transform: "translateY(-2px)",
                                                                boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                                                                borderColor: theme.palette.primary.main
                                                            }
                                                        }}
                                                        onClick={() => setSelectedShiftId(shiftGroup.shiftId)}
                                                    >
                                                        <Typography variant="h6" fontWeight="bold" color="text.primary">
                                                            {shiftGroup.shiftName}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                                                            {shiftGroup.unassignedCount > 0 ? shiftGroup.unassignedCount : 0} {t("shift.positionsToAssign")}
                                                            {shiftGroup.assignedCount > 0 && (
                                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontStyle: 'italic' }}>
                                                                    ({shiftGroup.assignedCount} {t("shift.alreadyAssigned")})
                                                                </Typography>
                                                            )}
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                )}

                                {/* Step 2: Select Work Position */}
                                {selectedShiftId && !selectedWorkPositionId && (
                                    <Box>
                                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                                            <Button
                                                size="small"
                                                startIcon={<ArrowBackIosIcon />}
                                                onClick={() => setSelectedShiftId(null)}
                                            >
                                                {t("common.back")}
                                            </Button>
                                            <Typography variant="h6" fontWeight="bold" color="text.primary">
                                                {t("shift.selectWorkPosition")}
                                            </Typography>
                                        </Box>
                                        <Grid container spacing={2}>
                                            {getWorkPositionsForShift(selectedShiftId).map((workPos) => (
                                                <Grid item xs={12} sm={6} key={workPos.workPositionId}>
                                                    <Paper
                                                        elevation={2}
                                                        sx={{
                                                            p: 2,
                                                            borderRadius: 2,
                                                            border: `2px solid ${theme.palette.divider}`,
                                                            transition: "all 0.3s ease",
                                                            cursor: "pointer",
                                                            "&:hover": {
                                                                transform: "translateY(-2px)",
                                                                boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                                                                borderColor: theme.palette.primary.main
                                                            }
                                                        }}
                                                        onClick={() => setSelectedWorkPositionId(workPos.workPositionId)}
                                                    >
                                                        <Typography variant="h6" fontWeight="bold" color="text.primary">
                                                            {workPos.workPosition?.name || t("shift.noPosition")}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                                                            {workPos.unassignedCount} {t("shift.positionsToAssign")}
                                                            {workPos.assignedCount > 0 && (
                                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontStyle: 'italic' }}>
                                                                    ({workPos.assignedCount} {t("shift.alreadyAssigned")})
                                                                </Typography>
                                                            )}
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                )}

                                {/* Step 3: Select Shift Detail or Auto-assign */}
                                {selectedShiftId && selectedWorkPositionId && (
                                    <Box>
                                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                                            <Button
                                                size="small"
                                                startIcon={<ArrowBackIosIcon />}
                                                onClick={() => setSelectedWorkPositionId(null)}
                                            >
                                                {t("common.back")}
                                            </Button>
                                            <Typography variant="h6" fontWeight="bold" color="text.primary">
                                                {t("shift.selectPosition")}
                                            </Typography>
                                        </Box>
                                        {(() => {
                                            const uniqueDetails = getUniqueShiftDetailsForWorkPosition(selectedShiftId, selectedWorkPositionId)
                                            const workPosition = getWorkPositionsForShift(selectedShiftId).find(wp => wp.workPositionId === selectedWorkPositionId)?.workPosition

                                            // If all shift details have the same times, auto-assign to first available
                                            if (uniqueDetails.length === 1 && uniqueDetails[0].allDetails.length > 1) {
                                                return (
                                                    <Box>
                                                        <Alert severity="info" sx={{ mb: 2 }}>
                                                            {t("shift.allPositionsSame")}
                                                        </Alert>
                                                        <Paper
                                                            elevation={2}
                                                            sx={{
                                                                p: 2,
                                                                borderRadius: 2,
                                                                border: `2px solid ${theme.palette.primary.main}`,
                                                                mb: 2
                                                            }}
                                                        >
                                                            <Typography variant="h6" fontWeight="bold" mb={1}>
                                                                {getShiftNameById(selectedShiftId)} - {workPosition?.name}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" mb={1}>
                                                                {uniqueDetails[0].firstDetail.startTime
                                                                    ? new Date(uniqueDetails[0].firstDetail.startTime).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })
                                                                    : shifts.find(s => s.id === selectedShiftId)?.startHour || t("shift.noStartTime")} - {uniqueDetails[0].firstDetail.endTime
                                                                        ? new Date(uniqueDetails[0].firstDetail.endTime).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })
                                                                        : shifts.find(s => s.id === selectedShiftId)?.endHour || t("shift.noEndTime")}
                                                            </Typography>
                                                            {uniqueDetails[0].firstDetail.breakStart && uniqueDetails[0].firstDetail.breakEnd && typeof uniqueDetails[0].firstDetail.breakStart === 'string' && typeof uniqueDetails[0].firstDetail.breakEnd === 'string' && uniqueDetails[0].firstDetail.breakStart.trim() !== '' && uniqueDetails[0].firstDetail.breakEnd.trim() !== '' && (
                                                                <Typography variant="body2" color="text.secondary" mb={2}>
                                                                    {t("shift.break")}: {new Date(uniqueDetails[0].firstDetail.breakStart).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })} - {new Date(uniqueDetails[0].firstDetail.breakEnd).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })}
                                                                </Typography>
                                                            )}
                                                            {!isViewerOnly && (
                                                            <Button
                                                                variant="contained"
                                                                fullWidth
                                                                onClick={async () => handleAssignShift(uniqueDetails[0].allDetails[0].id)}
                                                            >
                                                                {t("shift.assign")}
                                                            </Button>
                                                            )}
                                                        </Paper>
                                                    </Box>
                                                )
                                            }

                                            // Show unique shift details
                                            return (
                                                <Grid container spacing={2}>
                                                    {uniqueDetails.map((detailGroup, index) => (
                                                        <Grid item xs={12} key={index}>
                                                            <Paper
                                                                elevation={2}
                                                                sx={{
                                                                    p: 2,
                                                                    borderRadius: 2,
                                                                    border: `2px solid ${theme.palette.divider}`,
                                                                    transition: "all 0.3s ease",
                                                                    cursor: "pointer",
                                                                    "&:hover": {
                                                                        transform: "translateY(-2px)",
                                                                        boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                                                                        borderColor: theme.palette.primary.main
                                                                    }
                                                                }}
                                                                onClick={isViewerOnly ? undefined : async () => handleAssignShift(detailGroup.firstDetail.id)}
                                                            >
                                                                <Typography variant="h6" fontWeight="bold" color="text.primary" mb={1}>
                                                                    {getShiftNameById(selectedShiftId)} - {workPosition?.name}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary" mb={0.5}>
                                                                    {detailGroup.firstDetail.startTime
                                                                        ? new Date(detailGroup.firstDetail.startTime).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })
                                                                        : shifts.find(s => s.id === selectedShiftId)?.startHour || t("shift.noStartTime")} - {detailGroup.firstDetail.endTime
                                                                            ? new Date(detailGroup.firstDetail.endTime).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })
                                                                            : shifts.find(s => s.id === selectedShiftId)?.endHour || t("shift.noEndTime")}
                                                                </Typography>
                                                                {detailGroup.firstDetail.breakStart && detailGroup.firstDetail.breakEnd && typeof detailGroup.firstDetail.breakStart === 'string' && typeof detailGroup.firstDetail.breakEnd === 'string' && detailGroup.firstDetail.breakStart.trim() !== '' && detailGroup.firstDetail.breakEnd.trim() !== '' && (
                                                                    <Typography variant="body2" color="text.secondary" mb={1}>
                                                                        {t("shift.break")}: {new Date(detailGroup.firstDetail.breakStart).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })} - {new Date(detailGroup.firstDetail.breakEnd).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })}
                                                                    </Typography>
                                                                )}
                                                                {detailGroup.allDetails.length > 1 && (
                                                                    <Chip
                                                                        label={`${detailGroup.allDetails.length} ${t("shift.positionsToAssign")}`}
                                                                        size="small"
                                                                        color="secondary"
                                                                        sx={{ mt: 1 }}
                                                                    />
                                                                )}
                                                            </Paper>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            )
                                        })()}
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Box textAlign="center" py={4}>
                                <Typography variant="h6" color="text.secondary" mb={1}>
                                    {t("shift.noAvailableShifts")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("shift.noAvailableShiftsDescription")}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Modal>

            <AddEventModal
                open={openAddEventModal}
                onClose={() => {
                    setOpenAddEventModal(false)
                    skipOverlapCheckRef.current = false
                }}
                skipOverlapCheckRef={skipOverlapCheckRef}
                data={selectedDate ? {
                    startedOn: selectedDate,
                    endedOn: selectedDate,
                    allDay: true
                } : undefined}
                onSubmit={async (values: AddWorkFormData) => {
                    // handleCreateCalendarEvent now handles overlap checking internally
                    await handleCreateCalendarEvent(values)
                }}
            />

            <Modal
                open={openShiftDetailsModal}
                onClose={() => setOpenShiftDetailsModal(false)}
                aria-labelledby="shift-details-modal"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Paper
                    elevation={24}
                    sx={{
                        width: '90%',
                        maxWidth: 700,
                        maxHeight: '90vh',
                        overflow: 'auto',
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                        border: `1px solid ${theme.palette.divider}`,
                        position: 'relative'
                    }}
                >
                    <Box p={3}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Typography variant="h5" fontWeight="bold" color="text.primary">
                                    {t("shift.shiftDetails")}
                                </Typography>
                                <BetaChip label="BETA" size="small" />
                            </Box>
                            <Button
                                onClick={() => setOpenShiftDetailsModal(false)}
                                sx={{ minWidth: 'auto', p: 1 }}
                            >
                                ✕
                            </Button>
                        </Box>

                        {selectedShiftDetails && (
                            <Box>
                                {/* Shift Details - Main Focus */}
                                <Paper
                                    elevation={2}
                                    sx={{
                                        p: 2.5,
                                        borderRadius: 2,
                                        mb: 2,
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.background.paper} 100%)`,
                                        border: `1.5px solid ${theme.palette.primary.main}30`
                                    }}
                                >
                                    <Typography variant="h6" fontWeight="bold" mb={0.5} color="text.primary">
                                        {t("shift.shiftInformation")}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" mb={2}>
                                        {format(selectedShiftDetails.date, "EEEE, dd MMMM yyyy", { locale: getLocale() })} · {weekLabel(selectedShiftDetails.date)}
                                    </Typography>
                                    <Box display="flex" flexDirection="column" gap={2}>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1.5,
                                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                                                border: `1px solid ${theme.palette.divider}`
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                                                {t("shift.shiftName")}
                                            </Typography>
                                            <Typography variant="subtitle1" fontWeight="bold" mt={0.5}>
                                                {selectedShiftDetails.shiftData.name}
                                            </Typography>
                                        </Box>

                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: 1.5,
                                                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                                                        border: `1px solid ${theme.palette.divider}`
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                                                        {t("shift.startTime")}
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight="bold" mt={0.5}>
                                                        {selectedShiftDetails.shift.start
                                                            ? format(selectedShiftDetails.shift.start, "HH:mm", { locale: getLocale() })
                                                            : selectedShiftDetails.shiftData.startHour}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: 1.5,
                                                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                                                        border: `1px solid ${theme.palette.divider}`
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                                                        {t("shift.endTime")}
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight="bold" mt={0.5}>
                                                        {selectedShiftDetails.shift.end
                                                            ? format(selectedShiftDetails.shift.end, "HH:mm", { locale: getLocale() })
                                                            : selectedShiftDetails.shiftData.endHour}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        {/* Main Break */}
                                        {selectedShiftDetails.shift.breakStartTime && selectedShiftDetails.shift.breakEndTime && (
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                                                    border: `1px solid ${theme.palette.divider}`
                                                }}
                                            >
                                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
                                                    {t("shift.break")}
                                                </Typography>
                                                <Typography variant="body1" fontWeight="bold" mt={0.5}>
                                                    {format(selectedShiftDetails.shift.breakStartTime, "HH:mm", { locale: getLocale() })} - {format(selectedShiftDetails.shift.breakEndTime, "HH:mm", { locale: getLocale() })}
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Additional Breaks */}
                                        {selectedShiftDetails.shift.additionalBreaks && selectedShiftDetails.shift.additionalBreaks.length > 0 && (
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                                                    border: `1px solid ${theme.palette.divider}`
                                                }}
                                            >
                                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem', mb: 1, display: 'block' }}>
                                                    {t("shift.additionalBreaks")} ({selectedShiftDetails.shift.additionalBreaks.length})
                                                </Typography>
                                                <Box display="flex" flexDirection="column" gap={1}>
                                                    {selectedShiftDetails.shift.additionalBreaks.map((ab, index) => (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                pl: 1.5,
                                                                py: 0.75,
                                                                backgroundColor: theme.palette.mode === 'dark'
                                                                    ? 'rgba(33, 150, 243, 0.1)'
                                                                    : 'rgba(33, 150, 243, 0.05)',
                                                                borderRadius: 1
                                                            }}
                                                        >
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                {t("shift.additionalBreak")} #{index + 1}
                                                            </Typography>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {format(ab.startTime, "HH:mm", { locale: getLocale() })} - {format(ab.endTime, "HH:mm", { locale: getLocale() })}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Paper>

                                {/* Employee Information - Less Prominent */}
                                <Paper
                                    elevation={1}
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
                                        border: `1px solid ${theme.palette.divider}`
                                    }}
                                >
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Avatar
                                            src={getEmployeeImageUrl(selectedShiftDetails.employee.filePath)}
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                border: `2px solid ${theme.palette.divider}`
                                            }}
                                        >
                                            {!selectedShiftDetails.employee.filePath && selectedShiftDetails.employee.firstName?.[0]}{!selectedShiftDetails.employee.filePath && selectedShiftDetails.employee.lastName?.[0]}
                                        </Avatar>
                                        <Box flex={1}>
                                            <Typography variant="subtitle2" fontWeight="medium" color="text.primary">
                                                {`${selectedShiftDetails.employee.firstName} ${selectedShiftDetails.employee.lastName}`}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                                {selectedShiftDetails.employee.employeesToDepartments?.[0]?.workPosition?.name || t("shift.noPosition")}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                {t("shift.contractType")}
                                            </Typography>
                                            <Typography variant="caption" fontWeight="medium" sx={{ display: 'block' }}>
                                                {selectedShiftDetails.employee.employeesToSeasons?.[0]?.contract?.isPartTime ? t("shift.partTime") : t("shift.fullTime")}
                                            </Typography>
                                            {selectedShiftDetails.employee.employeesToSeasons?.[0]?.contract?.workPercentage && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}>
                                                    {selectedShiftDetails.employee.employeesToSeasons[0].contract.workPercentage}%
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>

                                <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setOpenShiftDetailsModal(false)}
                                    >
                                        {t("common.forms.button.close")}
                                    </Button>
                                    {!isViewerOnly && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<DeleteForever />}
                                        disabled={isDateReadOnly(selectedShiftDetails.date)}
                                        onClick={async () => {
                                            if (!selectedShiftDetails?.shift?.assignmentId || !selectedDepartment) {
                                                toast.error(t("shift.errorRemovingShift"))
                                                return
                                            }

                                            // Double-check read-only status
                                            if (isDateReadOnly(selectedShiftDetails.date)) {
                                                toast.error(t("shift.cannotModifyPastDays") || "Cannot modify past days")
                                                return
                                            }

                                            try {
                                                await shiftApi.removeEmployeeAssingment(
                                                    selectedShiftDetails.shift.assignmentId,
                                                    selectedDepartment.id,
                                                    seasonId,
                                                    selectedShiftDetails.employee.id
                                                )
                                                toast.success(t("shift.shiftRemovedSuccessfully"))
                                                setOpenShiftDetailsModal(false)
                                                setSelectedShiftDetails(null)

                                                // Refresh the data with a small delay to ensure backend has processed
                                                fetchData()
                                                // Refresh calendar entries if we have a date
                                                if (selectedShiftDetails.date) {
                                                    await fetchCalendarEntries()
                                                }

                                                // Refresh worked hours and update score after a delay to ensure backend has processed
                                                setTimeout(async () => {
                                                    await refreshHoursAndScore()
                                                }, 500)
                                            } catch (error) {
                                                console.error("Error removing shift assignment:", error)
                                                toast.error(t("shift.errorRemovingShift"))
                                            }
                                        }}
                                    >
                                        {t("shift.removeShift")}
                                    </Button>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Modal>

            {/* Template Assignment Modal */}
            <Modal
                open={openAssignTemplateModal}
                onClose={() => {
                    setOpenAssignTemplateModal(false)
                    setSelectedDayForTemplate(null)
                }}
                aria-labelledby="assign-template-modal-title"
            >
                <Paper
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", sm: 500 },
                        maxWidth: 500,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2
                    }}
                >
                    <Typography id="assign-template-modal-title" variant="h6" component="h2" mb={2}>
                        {t("shift.assignTemplate") || "Assign Template"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                        {selectedDayForTemplate && (
                            <>
                                {t("shift.selectTemplateForDay") || "Select a template to assign to"} {format(selectedDayForTemplate, "dd.MM.yyyy", { locale: getLocale() })} · {weekLabel(selectedDayForTemplate)}
                            </>
                        )}
                    </Typography>
                    <Autocomplete
                        options={availableTemplates.map((t) => ({ label: t.name, id: t.id }))}
                        onChange={(event, value) => {
                            if (value) {
                                handleTemplateAssignSubmit(value.id)
                            }
                        }}
                        renderInput={(params) => (
                            <AppTextField
                                {...params}
                                placeholder={t("shiftTemplate.templateSelector.select") || "Select template"}
                                label={t("shiftTemplate.itemName") || "Template"}
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        fullWidth
                    />
                    <Box display="flex" justifyContent="flex-end" gap={1} mt={3}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setOpenAssignTemplateModal(false)
                                setSelectedDayForTemplate(null)
                            }}
                        >
                            {t("common.forms.button.cancel") || "Cancel"}
                        </Button>
                    </Box>
                </Paper>
            </Modal>

            {/* Overlap Confirmation Modal */}
            <Modal
                open={openOverlapModal}
                onClose={() => {
                    setOpenOverlapModal(false)
                    setOverlapData(null)
                }}
                aria-labelledby="overlap-modal-title"
            >
                <Paper
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", sm: 600 },
                        maxWidth: 600,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 3,
                        border: `2px solid ${theme.palette.warning.main}`
                    }}
                >
                    <Box display="flex" alignItems="center" gap={2} mb={3}>
                        <Warning sx={{ fontSize: 40, color: theme.palette.warning.main }} />
                        <Typography id="overlap-modal-title" variant="h5" component="h2" fontWeight="bold" color="warning.main">
                            {t("shift.overlapDetected") || "Overlap Detected"}
                        </Typography>
                    </Box>

                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="body1" fontWeight="medium">
                            {t("shift.overlapWarning") || "This shift assignment would overlap with an existing item. Please review the details below."}
                        </Typography>
                    </Alert>

                    {overlapData && (() => {
                        // Calculate time range for visualization (24 hours)
                        const dayStart = new Date(overlapData.newShift.startTime)
                        dayStart.setHours(0, 0, 0, 0)
                        const dayEnd = new Date(dayStart)
                        dayEnd.setHours(23, 59, 59, 999)

                        // Calculate positions as percentages of the day
                        const getPositionPercent = (time: Date) => {
                            const hours = time.getHours()
                            const minutes = time.getMinutes()
                            return ((hours * 60 + minutes) / (24 * 60)) * 100
                        }

                        const getWidthPercent = (start: Date, end: Date) => {
                            const startPos = getPositionPercent(start)
                            const endPos = getPositionPercent(end)
                            return Math.max(2, endPos - startPos) // Minimum 2% width for visibility
                        }

                        const existingStartPercent = getPositionPercent(overlapData.existingItem.startTime)
                        const existingWidthPercent = getWidthPercent(overlapData.existingItem.startTime, overlapData.existingItem.endTime)
                        const newStartPercent = getPositionPercent(overlapData.newShift.startTime)
                        const newWidthPercent = getWidthPercent(overlapData.newShift.startTime, overlapData.newShift.endTime)

                        // Calculate overlap region
                        const overlapStart = Math.max(existingStartPercent, newStartPercent)
                        const overlapEnd = Math.min(
                            existingStartPercent + existingWidthPercent,
                            newStartPercent + newWidthPercent
                        )
                        const overlapWidth = Math.max(0, overlapEnd - overlapStart)

                        return (
                            <Box>
                                {/* Combined Timeline Visualization */}
                                <Box mb={3}>
                                    <Typography variant="h6" fontWeight="bold" color="text.primary" mb={2}>
                                        {t("shift.overlapTimeline") || "Overlap Timeline"}
                                    </Typography>

                                    {/* Shared timeline container with both bars */}
                                    <Box
                                        sx={{
                                            position: "relative",
                                            width: "100%",
                                            bgcolor: theme.palette.grey[100],
                                            borderRadius: 2,
                                            overflow: "hidden",
                                            border: `2px solid ${theme.palette.divider}`,
                                            p: 2
                                        }}
                                    >
                                        {/* Time markers - shared across both rows */}
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                left: 0,
                                                right: 0,
                                                top: 0,
                                                bottom: 0,
                                                pointerEvents: "none"
                                            }}
                                        >
                                            {[0, 6, 12, 18, 24].map((hour) => (
                                                <Box
                                                    key={hour}
                                                    sx={{
                                                        position: "absolute",
                                                        left: `${(hour / 24) * 100}%`,
                                                        top: 0,
                                                        bottom: 0,
                                                        width: "1px",
                                                        bgcolor: theme.palette.grey[300],
                                                        zIndex: 1
                                                    }}
                                                />
                                            ))}
                                            {/* Hour labels */}
                                            {[0, 6, 12, 18, 24].map((hour) => (
                                                <Box
                                                    key={`label-${hour}`}
                                                    sx={{
                                                        position: "absolute",
                                                        left: `${(hour / 24) * 100}%`,
                                                        top: -20,
                                                        transform: "translateX(-50%)",
                                                        zIndex: 2
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                                                        {String(hour).padStart(2, "0")}:00
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>

                                        {/* Row 1: Existing Item */}
                                        <Box mb={2}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Chip
                                                        label={overlapData.existingItem.type === 'shift' ? t("shift.itemName") : t("employees.calendar.entry.itemName")}
                                                        size="small"
                                                        color={overlapData.existingItem.type === 'shift' ? 'primary' : 'secondary'}
                                                    />
                                                    <Typography variant="body2" fontWeight="bold" color="text.primary">
                                                        {overlapData.existingItem.name}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    width: "100%",
                                                    height: 50,
                                                    bgcolor: theme.palette.grey[200],
                                                    borderRadius: 1,
                                                    overflow: "hidden",
                                                    border: `2px solid ${theme.palette.error.main}`
                                                }}
                                            >
                                                {/* Existing item bar */}
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        left: `${existingStartPercent}%`,
                                                        top: "5px",
                                                        height: "40px",
                                                        width: `${existingWidthPercent}%`,
                                                        bgcolor: theme.palette.error.main,
                                                        borderRadius: 1,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        zIndex: 2,
                                                        boxShadow: 2
                                                    }}
                                                >
                                                    <Typography variant="caption" fontWeight="bold" color="white" sx={{ fontSize: "0.75rem" }}>
                                                        {format(overlapData.existingItem.startTime, "HH:mm")} - {format(overlapData.existingItem.endTime, "HH:mm")}
                                                    </Typography>
                                                </Box>
                                                {/* Break indicator for existing item */}
                                                {overlapData.existingItem.breakStart && overlapData.existingItem.breakEnd && (
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            left: `${getPositionPercent(overlapData.existingItem.breakStart)}%`,
                                                            top: "30px",
                                                            height: "3px",
                                                            width: `${getWidthPercent(overlapData.existingItem.breakStart, overlapData.existingItem.breakEnd)}%`,
                                                            bgcolor: theme.palette.error.dark,
                                                            zIndex: 3,
                                                            borderRadius: 1
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Row 2: New Shift/Calendar Event */}
                                        <Box>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Chip
                                                        label={overlapData.calendarEventData ? (t("shift.newCalendarEvent") || "New Calendar Event") : t("shift.new")}
                                                        size="small"
                                                        color="info"
                                                    />
                                                    <Typography variant="body2" fontWeight="bold" color="text.primary">
                                                        {overlapData.newShift.name}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    width: "100%",
                                                    height: 50,
                                                    bgcolor: theme.palette.grey[200],
                                                    borderRadius: 1,
                                                    overflow: "hidden",
                                                    border: `2px solid ${theme.palette.info.main}`
                                                }}
                                            >
                                                {/* New shift bar */}
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        left: `${newStartPercent}%`,
                                                        top: "5px",
                                                        height: "40px",
                                                        width: `${newWidthPercent}%`,
                                                        bgcolor: theme.palette.info.main,
                                                        borderRadius: 1,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        zIndex: 2,
                                                        boxShadow: 2
                                                    }}
                                                >
                                                    <Typography variant="caption" fontWeight="bold" color="white" sx={{ fontSize: "0.75rem" }}>
                                                        {format(overlapData.newShift.startTime, "HH:mm")} - {format(overlapData.newShift.endTime, "HH:mm")}
                                                    </Typography>
                                                </Box>
                                                {/* Break indicator for new shift */}
                                                {overlapData.newShift.breakStart && overlapData.newShift.breakEnd && (
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            left: `${getPositionPercent(overlapData.newShift.breakStart)}%`,
                                                            top: "30px",
                                                            height: "3px",
                                                            width: `${getWidthPercent(overlapData.newShift.breakStart, overlapData.newShift.breakEnd)}%`,
                                                            bgcolor: theme.palette.info.dark,
                                                            zIndex: 3,
                                                            borderRadius: 1
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Overlap highlight - spans both rows */}
                                        {overlapWidth > 0 && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    left: `${overlapStart}%`,
                                                    top: 0,
                                                    bottom: 0,
                                                    width: `${overlapWidth}%`,
                                                    bgcolor: alpha(theme.palette.warning.main, 0.2),
                                                    borderLeft: `3px dashed ${theme.palette.warning.main}`,
                                                    borderRight: `3px dashed ${theme.palette.warning.main}`,
                                                    zIndex: 1,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    pointerEvents: "none"
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        bgcolor: theme.palette.warning.main,
                                                        color: "white",
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: 1,
                                                        boxShadow: 2
                                                    }}
                                                >
                                                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: "0.7rem" }}>
                                                        {t("shift.overlap") || "OVERLAP"}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>

                                {/* Check if this is a calendar event overlap (not allowed) */}
                                {overlapData?.calendarEventData && overlapData.existingItem.type === 'calendar' ? (
                                    <Alert severity="error" sx={{ mb: 3 }}>
                                        <Typography variant="body2" fontWeight="bold" mb={1}>
                                            {t("shift.calendarEventOverlapNotAllowed") || "Calendar Events Cannot Overlap"}
                                        </Typography>
                                        <Typography variant="body2">
                                            {t("shift.calendarEventOverlapExplanation") || "It is not possible to create a calendar event that overlaps with an existing calendar event. Please choose a different date or time, or remove the existing event first."}
                                        </Typography>
                                    </Alert>
                                ) : (
                                    <Alert severity="error" sx={{ mb: 3 }}>
                                        <Typography variant="body2">
                                            {t("shift.overlapConfirmation") || "Are you absolutely sure you want to proceed with this assignment? This will create a scheduling conflict."}
                                        </Typography>
                                    </Alert>
                                )}
                            </Box>
                        )
                    })()}

                    <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setOpenOverlapModal(false)
                                setOverlapData(null)
                            }}
                        >
                            {t("common.forms.button.cancel") || "Cancel"}
                        </Button>
                        {/* Only show "Proceed Anyway" button if it's NOT a calendar event overlap */}
                        {!(overlapData?.calendarEventData && overlapData.existingItem.type === 'calendar') && (
                            <Button
                                variant="contained"
                                color="error"
                                onClick={async () => {
                                    if (overlapData) {
                                        if (overlapData.shiftDetailId !== undefined) {
                                            // Handle shift assignment
                                            await handleAssignShift(overlapData.shiftDetailId, true)
                                        } else if (overlapData.calendarEventData) {
                                            // Handle calendar event creation (only for shift overlaps, not calendar overlaps)
                                            const eventData = overlapData.calendarEventData
                                            const values: AddWorkFormData = {
                                                ...eventData.rest,
                                                startedOn: eventData.startedOn,
                                                endedOn: eventData.endedOn,
                                                allDay: eventData.allDay,
                                                startTime: eventData.startTime,
                                                endTime: eventData.endTime,
                                                type: eventData.type,
                                                customTypeId: eventData.customTypeId
                                            }

                                            // Skip overlap check and create the calendar event
                                            await handleCreateCalendarEvent(values, true)
                                            setOpenOverlapModal(false)
                                            setOverlapData(null)
                                        }
                                    }
                                }}
                                sx={{
                                    fontWeight: "bold"
                                }}
                            >
                                {t("shift.proceedAnyway") || "Proceed Anyway"}
                            </Button>
                        )}
                    </Box>
                </Paper>
            </Modal>

            {/* Calendar Entry Details/Delete Modal */}
            <Modal
                open={openCalendarEntryModal}
                onClose={() => {
                    setOpenCalendarEntryModal(false)
                    setSelectedCalendarEntry(null)
                }}
                aria-labelledby="calendar-entry-modal-title"
            >
                <Paper
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", sm: 500 },
                        maxWidth: 500,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 3
                    }}
                >
                    {selectedCalendarEntry && (
                        <>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <Typography id="calendar-entry-modal-title" variant="h5" component="h2" fontWeight="bold">
                                    {t("employees.calendar.entry.title") || "Calendar Entry"}
                                </Typography>
                                <IconButtonMui
                                    onClick={() => {
                                        setOpenCalendarEntryModal(false)
                                        setSelectedCalendarEntry(null)
                                    }}
                                    sx={{ p: 0.5 }}
                                >
                                    <CloseIcon />
                                </IconButtonMui>
                            </Box>

                            <Box mb={3}>
                                <Box display="flex" alignItems="center" gap={2} mb={2}>
                                    <Avatar
                                        src={getEmployeeImageUrl(selectedCalendarEntry.employee.filePath)}
                                        sx={{ width: 48, height: 48 }}
                                    >
                                        {!selectedCalendarEntry.employee.filePath && selectedCalendarEntry.employee.firstName?.[0]}{!selectedCalendarEntry.employee.filePath && selectedCalendarEntry.employee.lastName?.[0]}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight="bold">
                                            {`${selectedCalendarEntry.employee.firstName} ${selectedCalendarEntry.employee.lastName}`}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {format(selectedCalendarEntry.date, "EEEE, dd MMMM yyyy", { locale: getLocale() })} · {weekLabel(selectedCalendarEntry.date)}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                bgcolor: selectedCalendarEntry.color,
                                                color: "white"
                                            }}
                                        >
                                            <Typography variant="h6" fontWeight="bold" mb={1}>
                                                {t(`employees.calendar.types.${selectedCalendarEntry.type.toLowerCase()}`)}
                                            </Typography>
                                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                <AccessTime sx={{ fontSize: 18 }} />
                                                <Typography variant="body2">
                                                    {(() => {
                                                        const entryStartDate = new Date(selectedCalendarEntry.entry.startedOn)
                                                        const entryEndDate = new Date(selectedCalendarEntry.entry.endedOn)
                                                        const entryStartDateOnly = startOfDay(entryStartDate)
                                                        const entryEndDateOnly = startOfDay(entryEndDate)
                                                        const isMultiDay = !isSameDay(entryStartDateOnly, entryEndDateOnly)
                                                        const isAllDay = selectedCalendarEntry.entry.startTime === null && selectedCalendarEntry.entry.endTime === null

                                                        if (isMultiDay) {
                                                            if (isAllDay) {
                                                                return `${format(entryStartDate, "dd MMM yyyy", { locale: getLocale() })} - ${format(entryEndDate, "dd MMM yyyy", { locale: getLocale() })} (${t("employees.calendar.entry.allDay") || "All day event"})`
                                                            } else {
                                                                const startTimeStr = selectedCalendarEntry.entry.startTime
                                                                    ? format(new Date(`2000-01-01T${selectedCalendarEntry.entry.startTime}`), "HH:mm", { locale: getLocale() })
                                                                    : format(entryStartDate, "HH:mm", { locale: getLocale() })
                                                                const endTimeStr = selectedCalendarEntry.entry.endTime
                                                                    ? format(new Date(`2000-01-01T${selectedCalendarEntry.entry.endTime}`), "HH:mm", { locale: getLocale() })
                                                                    : format(entryEndDate, "HH:mm", { locale: getLocale() })
                                                                return `${format(entryStartDate, "dd MMM yyyy", { locale: getLocale() })} ${startTimeStr} - ${format(entryEndDate, "dd MMM yyyy", { locale: getLocale() })} ${endTimeStr}`
                                                            }
                                                        } else {
                                                            if (isAllDay) {
                                                                return `${format(entryStartDate, "dd MMM yyyy", { locale: getLocale() })} (${t("employees.calendar.entry.allDay") || "All day event"})`
                                                            } else {
                                                                return `${format(selectedCalendarEntry.startTime, "HH:mm", { locale: getLocale() })} - ${format(selectedCalendarEntry.endTime, "HH:mm", { locale: getLocale() })}`
                                                            }
                                                        }
                                                    })()}
                                                </Typography>
                                            </Box>
                                            {selectedCalendarEntry.entry.publicNotes && (
                                                <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                                                    {selectedCalendarEntry.entry.publicNotes}
                                                </Typography>
                                            )}
                                        </Paper>
                                    </Grid>
                                    {selectedCalendarEntry.entry.managerNotes && (
                                        <Grid item xs={12}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: theme.palette.grey[50] }}>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1} color="text.secondary">
                                                    {t("employees.calendar.entry.managerNotes") || "Manager Notes"}
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {selectedCalendarEntry.entry.managerNotes}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>

                            <Box display="flex" justifyContent="flex-end" gap={2}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setOpenCalendarEntryModal(false)
                                        setSelectedCalendarEntry(null)
                                    }}
                                >
                                    {t("common.forms.button.close") || "Close"}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<DeleteForever />}
                                    disabled={selectedCalendarEntry ? isDateReadOnly(selectedCalendarEntry.date) : false}
                                    onClick={async () => {
                                        if (!selectedCalendarEntry) return

                                        // Double-check read-only status
                                        if (isDateReadOnly(selectedCalendarEntry.date)) {
                                            toast.error(t("shift.cannotModifyPastDays") || "Cannot modify past days")
                                            return
                                        }

                                        try {
                                            await calendarEntriesApi.remove(
                                                String(selectedCalendarEntry.entry.id),
                                                seasonId,
                                                selectedCalendarEntry.employee.id
                                            )
                                            toast.success(t("employees.calendar.entry.deletedSuccessfully") || "Calendar entry deleted successfully")
                                            setOpenCalendarEntryModal(false)
                                            setSelectedCalendarEntry(null)

                                            // Refresh calendar entries
                                            await fetchCalendarEntries()

                                            // Refresh worked hours and update score
                                            await refreshHoursAndScore()
                                        } catch (error: any) {
                                            console.error("Error deleting calendar entry:", error)
                                            if (error?.response?.status === 403 && error?.response?.data?.message?.includes('DEPCHIEF users cannot')) {
                                                toast.error(t('employees.calendar.entry.cannotModifyPast'))
                                            } else {
                                                toast.error(t("employees.calendar.entry.errorDeleting") || "Error deleting calendar entry")
                                            }
                                        }
                                    }}
                                >
                                    {t("employees.calendar.entry.deleteEntry") || "Delete Event"}
                                </Button>
                            </Box>
                        </>
                    )}
                </Paper>
            </Modal>

            {/* Assignment Score Modal */}
            <Modal
                open={openAssignmentScoreModal}
                onClose={() => {
                    setOpenAssignmentScoreModal(false)
                    setAssignmentScore(null)
                }}
                aria-labelledby="assignment-score-modal-title"
            >
                <Paper
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "95%", sm: 1200 },
                        maxWidth: 1200,
                        maxHeight: "95vh",
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 3,
                        borderRadius: 3,
                        overflow: "auto"
                    }}
                >
                    {assignmentScore && (() => {
                        const getScoreColor = (score: number) => {
                            if (score >= 90) return theme.palette.success.main
                            if (score >= 70) return theme.palette.warning.main
                            return theme.palette.error.main
                        }

                        // Get allocation color with intensity: red for under-allocation, violet for over-allocation
                        const getAllocationColor = (empScore: { requiredHours: number; assignedHours: number; overAllocationHours: number; percentage: number }) => {
                            if (empScore.overAllocationHours > 0) {
                                // Over-allocation: use violet/purple, darker/more intense for higher over-allocation
                                const overAllocationRatio = Math.min(empScore.overAllocationHours / empScore.requiredHours, 1.0) // Cap at 100% over
                                // Intensity factor: 0.0 (light violet) to 1.0 (dark violet)
                                const intensityFactor = Math.min(1.0, overAllocationRatio * 2) // Full intensity at 50% over

                                // Interpolate between light violet (#BA68C8) and dark violet (#6A1B9A)
                                const lightViolet = { r: 186, g: 104, b: 200 } // #BA68C8
                                const darkViolet = { r: 106, g: 27, b: 154 } // #6A1B9A
                                const r = Math.round(lightViolet.r + (darkViolet.r - lightViolet.r) * intensityFactor)
                                const g = Math.round(lightViolet.g + (darkViolet.g - lightViolet.g) * intensityFactor)
                                const b = Math.round(lightViolet.b + (darkViolet.b - lightViolet.b) * intensityFactor)
                                return `rgb(${r}, ${g}, ${b})`
                            } else if (empScore.percentage < 100) {
                                // Under-allocation: use red, darker/more intense for higher under-allocation
                                const underAllocationRatio = (100 - empScore.percentage) / 100
                                // Intensity factor: 0.0 (light red) to 1.0 (dark red)
                                const intensityFactor = underAllocationRatio

                                // Interpolate between light red (#EF5350) and dark red (#C62828)
                                const lightRed = { r: 239, g: 83, b: 80 } // #EF5350
                                const darkRed = { r: 198, g: 40, b: 40 } // #C62828
                                const r = Math.round(lightRed.r + (darkRed.r - lightRed.r) * intensityFactor)
                                const g = Math.round(lightRed.g + (darkRed.g - lightRed.g) * intensityFactor)
                                const b = Math.round(lightRed.b + (darkRed.b - lightRed.b) * intensityFactor)
                                return `rgb(${r}, ${g}, ${b})`
                            } else {
                                // Perfect allocation: use success color
                                return theme.palette.success.main
                            }
                        }

                        const getScoreLabel = (score: number) => {
                            if (score >= 95) return t("shift.score.excellent") || "Excellent"
                            if (score >= 85) return t("shift.score.good") || "Good"
                            if (score >= 70) return t("shift.score.fair") || "Fair"
                            if (score >= 50) return t("shift.score.needsImprovement") || "Needs Improvement"
                            return t("shift.score.poor") || "Poor"
                        }
                        const getBadge = (score: number) => {
                            if (score >= 95) return { icon: <EmojiEvents />, label: t("shift.badge.perfect") || "Perfect Assignment", color: theme.palette.success.main }
                            if (score >= 85) return { icon: <TrendingUp />, label: t("shift.badge.great") || "Great Job", color: theme.palette.success.light }
                            if (score >= 70) return { icon: <CheckCircle />, label: t("shift.badge.good") || "Good Progress", color: theme.palette.warning.main }
                            return { icon: <Warning />, label: t("shift.badge.needsWork") || "Needs Work", color: theme.palette.error.main }
                        }
                        const badge = getBadge(assignmentScore.score)

                        return (
                            <>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography id="assignment-score-modal-title" variant="h5" component="h2" fontWeight="bold">
                                        {t("shift.assignmentScore.title") || "Assignment Score"}
                                    </Typography>
                                    <IconButtonMui
                                        onClick={() => {
                                            setOpenAssignmentScoreModal(false)
                                            setAssignmentScore(null)
                                        }}
                                        sx={{ p: 0.5 }}
                                    >
                                        <CloseIcon />
                                    </IconButtonMui>
                                </Box>

                                {/* Overall Score Card */}
                                <Paper
                                    elevation={3}
                                    sx={{
                                        p: 2,
                                        mb: 2,
                                        borderRadius: 2,
                                        background: `linear-gradient(135deg, ${alpha(getScoreColor(assignmentScore.score), 0.1)} 0%, ${alpha(getScoreColor(assignmentScore.score), 0.05)} 100%)`,
                                        border: `2px solid ${getScoreColor(assignmentScore.score)}`
                                    }}
                                >
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Box sx={{ position: "relative", display: "inline-flex" }}>
                                            <CircularProgress
                                                variant="determinate"
                                                value={assignmentScore.score}
                                                size={80}
                                                thickness={5}
                                                sx={{
                                                    color: getScoreColor(assignmentScore.score),
                                                    transform: "rotate(-90deg)"
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    top: 0,
                                                    left: 0,
                                                    bottom: 0,
                                                    right: 0,
                                                    position: "absolute",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                }}
                                            >
                                                <Typography variant="h4" component="div" fontWeight="bold" color={getScoreColor(assignmentScore.score)}>
                                                    {assignmentScore.score}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
                                                    / 100
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box flex={1}>
                                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                                {badge.icon}
                                                <Typography variant="h6" fontWeight="bold" color={badge.color}>
                                                    {badge.label}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body1" color="text.secondary" mb={1.5}>
                                                {getScoreLabel(assignmentScore.score)}
                                            </Typography>
                                            <Grid container spacing={1.5}>
                                                <Grid item xs={6}>
                                                    <Paper elevation={1} sx={{ p: 1, borderRadius: 1.5, bgcolor: theme.palette.background.paper }}>
                                                        <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
                                                            {t("shift.assignmentScore.totalRequired") || "Total Required"}
                                                        </Typography>
                                                        <Typography variant="body1" fontWeight="bold" color="primary">
                                                            {assignmentScore.totalRequiredHours}h
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Paper elevation={1} sx={{ p: 1, borderRadius: 1.5, bgcolor: theme.palette.background.paper }}>
                                                        <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
                                                            {t("shift.assignmentScore.totalAssigned") || "Total Assigned"}
                                                        </Typography>
                                                        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                                            <Typography variant="body1" fontWeight="bold" color={getScoreColor(assignmentScore.score)}>
                                                                {assignmentScore.totalAssignedHours}h
                                                            </Typography>
                                                            {assignmentScore.totalOverAllocationHours > 0 && (
                                                                <Chip
                                                                    label={`+${assignmentScore.totalOverAllocationHours}h`}
                                                                    size="small"
                                                                    sx={{
                                                                        height: 20,
                                                                        fontSize: "0.65rem",
                                                                        bgcolor: "#9C27B0", // Violet
                                                                        color: "white",
                                                                        fontWeight: "bold",
                                                                        "& .MuiChip-label": {
                                                                            px: 0.75,
                                                                            py: 0
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Box>
                                </Paper>

                                {/* Employee Breakdown */}
                                <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
                                    {t("shift.assignmentScore.employeeBreakdown") || "Employee Breakdown"}
                                </Typography>
                                <Box sx={{ maxHeight: "calc(95vh - 350px)", overflowY: "auto", pr: 1 }}>
                                    <Grid container spacing={1}>
                                        {assignmentScore.employeeScores.map((empScore, index) => (
                                            <Grid item xs={12} sm={4} key={empScore.employee.id}>
                                                <Paper
                                                    elevation={1}
                                                    sx={{
                                                        p: 1,
                                                        borderRadius: 1.5,
                                                        border: `1px solid ${getAllocationColor(empScore)}`,
                                                        bgcolor: alpha(getAllocationColor(empScore), 0.1),
                                                        transition: "all 0.2s ease",
                                                        "&:hover": {
                                                            transform: "translateY(-2px)",
                                                            boxShadow: 2
                                                        }
                                                    }}
                                                >
                                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                        <Avatar
                                                            src={getEmployeeImageUrl(empScore.employee.filePath)}
                                                            sx={{ width: 32, height: 32 }}
                                                        >
                                                            {!empScore.employee.filePath && empScore.employee.firstName?.[0]}{!empScore.employee.filePath && empScore.employee.lastName?.[0]}
                                                        </Avatar>
                                                        <Box flex={1} minWidth={0}>
                                                            <Typography variant="body2" fontWeight="bold" noWrap sx={{ fontSize: "0.85rem" }}>
                                                                {`${empScore.employee.firstName} ${empScore.employee.lastName}`}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: "0.7rem" }}>
                                                                {empScore.employee.employeesToDepartments?.[0]?.workPosition?.name || t("shift.noPosition")}
                                                            </Typography>
                                                        </Box>
                                                        <Box textAlign="right" flexShrink={0}>
                                                            <Typography variant="h6" fontWeight="bold" color={getAllocationColor(empScore)} sx={{ fontSize: "1.1rem" }}>
                                                                {empScore.score}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                                                                / 100
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Grid container spacing={0.75} mb={1}>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="text.secondary" display="block" mb={0.25} sx={{ fontSize: "0.7rem" }}>
                                                                {t("shift.assignmentScore.required") || "Required"}
                                                            </Typography>
                                                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                                                                {empScore.requiredHours}h
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="text.secondary" display="block" mb={0.25} sx={{ fontSize: "0.7rem" }}>
                                                                {t("shift.assignmentScore.assigned") || "Assigned"}
                                                            </Typography>
                                                            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                                                <Typography variant="body2" fontWeight="bold" color={getAllocationColor(empScore)} sx={{ fontSize: "0.85rem" }}>
                                                                    {empScore.assignedHours}h
                                                                </Typography>
                                                                {empScore.overAllocationHours > 0 && (
                                                                    <Chip
                                                                        label={`+${empScore.overAllocationHours}h`}
                                                                        size="small"
                                                                        sx={{
                                                                            height: 18,
                                                                            fontSize: "0.65rem",
                                                                            bgcolor: "#9C27B0", // Violet
                                                                            color: "white",
                                                                            fontWeight: "bold",
                                                                            "& .MuiChip-label": {
                                                                                px: 0.5,
                                                                                py: 0
                                                                            }
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                    <Box>
                                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                                                                {t("shift.assignmentScore.progress") || "Progress"}
                                                            </Typography>
                                                            <Typography variant="caption" fontWeight="bold" color={getAllocationColor(empScore)} sx={{ fontSize: "0.7rem" }}>
                                                                {Math.round(empScore.percentage)}%
                                                            </Typography>
                                                        </Box>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={Math.min(100, empScore.percentage)}
                                                            sx={{
                                                                height: 5,
                                                                borderRadius: 2.5,
                                                                bgcolor: alpha(getAllocationColor(empScore), 0.2),
                                                                "& .MuiLinearProgress-bar": {
                                                                    bgcolor: getAllocationColor(empScore),
                                                                    borderRadius: 2.5
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>

                                <Box display="flex" justifyContent="flex-end" mt={2}>
                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            setOpenAssignmentScoreModal(false)
                                            setAssignmentScore(null)
                                        }}
                                    >
                                        {t("common.forms.button.close") || "Close"}
                                    </Button>
                                </Box>
                            </>
                        )
                    })()}
                </Paper>
            </Modal>

            {/* Manager-only shift notes for the selected date */}
            <ShiftNotesModal
                open={openShiftNotesModal}
                onClose={() => {
                    setOpenShiftNotesModal(false)
                    setSelectedShiftNotesDate(undefined)
                }}
                date={selectedShiftNotesDate}
                notes={selectedShiftNotesDate ? getShiftNotesForDay(selectedShiftNotesDate) : []}
                fetchData={fetchShiftNotes}
                departmentId={selectedDepartment?.id}
                seasonId={seasonId}
            />
        </>
    )
}

export default ShiftAssignmentV2 